const CUSTOMIZER_ASSET_BASE=(()=>{
  const src=document.currentScript&&document.currentScript.src;
  return src?new URL('.',src):new URL('/assets/',window.location.origin);
})();

/* ============================================================
   DATA
   ============================================================ */
// Tier "from" prices — 5.1 locked price sheet, approved 2026-08-14
// (Petite $1,990 · Classic $2,350 · Statuesque $2,350; Classic/Statuesque were
// previously $2,140/$2,290, below the locked price).
const BODY_OPTIONS=[
  {key:'petite',label:'Petite',desc:'152cm · delicate frame',billable:true,absolute:true,sw:'sw-a'},
  {key:'classic',label:'Classic',desc:'162cm · balanced proportions',billable:true,absolute:true,sw:'sw-b'},
  {key:'statuesque',label:'Statuesque',desc:'172cm · long, elegant lines',billable:true,absolute:true,sw:'sw-c'},
];
const BREAST_SIZE=[
  {key:'natural',label:'Natural',desc:'Soft, subtle curve',sw:'sw-a'},
  {key:'balanced',label:'Balanced',desc:'Classic, proportional',sw:'sw-b'},
  {key:'full',label:'Full',desc:'Bold, voluptuous',sw:'sw-c'},
];
const BUTT_SHAPE=[
  {key:'subtle',label:'Subtle',desc:'Slim, toned',sw:'sw-a'},
  {key:'rounded',label:'Rounded',desc:'Natural curve',sw:'sw-b'},
  {key:'full',label:'Full',desc:'Full, shapely',sw:'sw-c'},
];
// Approved, customer-facing mold catalog generated from ../mannequins. The
// source tool's repair/regeneration flags never enter this file.
// Shorten customer-facing names without changing catalog IDs or variant keys.
const BODY_CATALOG=(window.SBD_BODY_CATALOG||[]).map(model=>({
  ...model,
  label:model.label.replace(/irontech/gi,'irt'),
}));
const BODY_SKINS=window.SBD_BODY_SKINS||[];
const BODY_SKIN_MAP={natural:'porcelain',glow:'natural',tanned:'honey',darktan:'caramel',black:'espresso'};
const BODY_SKIN_REVERSE=Object.fromEntries(Object.entries(BODY_SKIN_MAP).map(([customizer,body])=>[body,customizer]));

/* Body renders withdrawn on quality (owner, 2026-09-09). Keyed by body uid ->
   the BODY tone ids that must not be offered for it.

   Held HERE rather than by deleting the entries from assets/sbd-body-data.js,
   whose own first line reads "Generated by scripts/export-body-assets.mjs — do
   not edit by hand". That script is no longer in the repo, so a hand-edit there
   could not be reproduced and would be silently undone the day it is restored
   and re-run. This list is a product decision and belongs in product code; the
   generated data stays exactly as generated.

   Remember the tone vocabularies differ. The customizer's skin keys are
   natural/glow/tanned/darktan/black; a BODY tone is porcelain/natural/honey/
   caramel/espresso, via BODY_SKIN_MAP. So "espresso" here is the body shown for
   the customizer's BLACK skin, and "natural" is the one shown for GLOW — the
   word "natural" means different tones on the two sides of that map, which is
   exactly the trap this comment exists to stop. */
const BODY_TONE_EXCLUSIONS={
  'body_irontech_4_164T':                    ['espresso'],                                   // Silicone 164T
  'body_irontech_8_159cm':                   ['espresso'],                                   // Silicone 159cm
  'body_irontech_11_PR160':                  ['espresso'],                                   // Silicone PR160
  'body_irontech_15_Silicone-168cm':         ['natural','honey','caramel','espresso'],       // Silicone 168cm — porcelain only
  'body_irontech_18_Silicone-161cm':         ['espresso'],                                   // Silicone 161cm
  'body_irontech_20_Silicone-PR178':         ['espresso'],                                   // Silicone PR178
  'body_irontech_23_Silicone-163cm':         ['espresso'],                                   // Silicone 163cm
  'body_irontech_24_Silicone-169cm':         ['espresso'],                                   // Silicone 169cm
  'body_irontech_25_Silicone-158cm-Pregnant':['espresso'],                                   // Silicone 158cm Pregnant
  'body_irontech_26_Silicone-162cm':         ['espresso'],                                   // Silicone 162cm
};
function bodyToneId(skinKey){ return BODY_SKIN_MAP[skinKey]||'natural'; }
/* Whether a body may be shown in a given customizer skin tone. Both halves
   matter: the render has to exist AND not be one of the withdrawn ones. */
function bodyHasTone(model,skinKey){
  if(!model) return false;
  const toneId=bodyToneId(skinKey);
  const withdrawn=BODY_TONE_EXCLUSIONS[model.uid];
  if(withdrawn&&withdrawn.indexOf(toneId)>=0) return false;
  return Boolean(model.assets&&model.assets[toneId]);
}
function bodiesForTone(skinKey){ return BODY_CATALOG.filter(model=>bodyHasTone(model,skinKey)); }
/* The closest still-available body when the chosen one has no render for a tone.
   Scored on the three axes the pickers themselves expose, so the swap lands on
   something the customer would recognise as the same shape rather than on
   whatever happens to sort first. */
function nearestBodyForTone(current,skinKey){
  const options=bodiesForTone(skinKey);
  if(!options.length) return null;
  if(!current) return options[0];
  let best=options[0],bestScore=-1;
  for(const model of options){
    let score=0;
    if(model.breast===current.breast) score+=3;
    if(model.butt===current.butt) score+=3;
    if(model.heightBand===current.heightBand) score+=2;
    if(Number.isFinite(model.height)&&Number.isFinite(current.height)) score+=Math.max(0,3-Math.abs(model.height-current.height)/5);
    if(score>bestScore){ bestScore=score; best=model; }
  }
  return best;
}
// Permanent machine identifiers. Short keys such as `kitty`, `darktan`, `J8`
// and `20` remain useful for assets and display logic, but must never be the
// identifiers written to a build sheet or treated as durable product metadata.
const HEAD_UID_BY_KEY=Object.freeze({
  luna:'head_irontech_luna',
  suki:'head_irontech_suki',
  penny:'head_irontech_penny',
  kitty:'head_irontech_kitty',
  eileen:'head_irontech_eileen', // Irontech S40 — restored 2026-08-31 (dropped at the Bronze→Cam's Body switch)
  ivy:'head_irontech_ivy',       // Irontech S27 — restored 2026-08-31
});
const SKIN_UID_BY_KEY=Object.freeze({
  natural:'skin_irontech_natural',
  glow:'skin_irontech_glow',
  tanned:'skin_irontech_tanned',
  darktan:'skin_irontech_darktan',
  black:'skin_irontech_black',
});
const EYE_UIDS=Object.freeze([
  'eyes_irontech_1-teal-green',
  'eyes_irontech_2-navy-blue',
  'eyes_irontech_3-amber-brown',
  'eyes_irontech_4-turquoise',
  'eyes_irontech_5-charcoal-gray',
  'eyes_irontech_6-smoky-gray',
  'eyes_irontech_7-sky-blue',
  'eyes_irontech_8-cyan-blue',
  'eyes_irontech_9-olive-green',
  'eyes_irontech_10-dark-brown',
  'eyes_irontech_11-silver-gray',
  'eyes_irontech_12-hazel-green',
  'eyes_irontech_13-rose-pink',
  'eyes_irontech_14-electric-blue',
  'eyes_irontech_15-ruby-red',
  'eyes_irontech_16-pale-green',
  'eyes_irontech_17-aqua-blue',
  'eyes_irontech_18-copper-brown',
  'eyes_irontech_19-deep-blue',
  'eyes_irontech_20-gold-green',
  'eyes_irontech_21-taupe-brown',
  'eyes_irontech_22-auburn-brown',
  'eyes_irontech_23-amber-orange',
  'eyes_irontech_24-golden-brown',
  'eyes_irontech_25-plum-purple',
  'eyes_irontech_26-dark-purple',
  'eyes_irontech_27-gold-yellow',
  'eyes_irontech_28-bright-blue',
  'eyes_irontech_29-blue-gray',
]);
const HAIR_UID_BY_CODE=Object.freeze({
  J1:'hair_irontech_J1-Dark-Brown-Short',
  J2:'hair_irontech_J2-Dark-Brown-Long',
  J3:'hair_irontech_J3-Dark-Brown-Medium',
  J4:'hair_irontech_J4-Black-Long',
  J5:'hair_irontech_J5-Dark-Brown-Long',
  J6:'hair_irontech_J6-Black-Long',
  J7:'hair_irontech_J7-Black-Short',
  J8:'hair_irontech_J8-Gray-Brown-Long',
  J9:'hair_irontech_J9-Ash-Brown-Medium',
  J10:'hair_irontech_J10-Black-Medium',
  J11:'hair_irontech_J11-Mauve-Brown-Long',
  J12:'hair_irontech_J12-Blond-Long',
  W1:'hair_irontech_W1-Dark-Brown-Long',
  W2:'hair_irontech_W2-Brown-Blond-Long',
  W3:'hair_irontech_W3-Gray-Blond-Long',
  W4:'hair_irontech_W4-Auburn-Long',
  W5:'hair_irontech_W5-Platinum-Bob',
  W6:'hair_irontech_W6-Black-Long',
  W7:'hair_irontech_W7-Blond-Black-Long',
  W8:'hair_irontech_W8-Black-Medium',
  W9:'hair_irontech_W9-Black-Blond-Long',
  W10:'hair_irontech_W10-Burgundy-Long',
  W11:'hair_irontech_W11-Honey-Blond-Long',
  W12:'hair_irontech_W12-Dark-Brown-Medium',
  W13:'hair_irontech_W13-Black-Purple-Long',
  W14:'hair_irontech_W14-Brown-Blond-Long',
  W15:'hair_irontech_W15-Auburn-Long',
  W16:'hair_irontech_W16-Honey-Blond-Long',
  W17:'hair_irontech_W17-Platinum-Long',
  W18:'hair_irontech_W18-Black-Brown-Long',
});
const BODY_BREAST_TO_CHOICE={small:'natural',medium:'balanced',large:'full'};
const BODY_BUTT_TO_CHOICE={small:'subtle',medium:'rounded',large:'full'};
const BODY_SIZE_LABEL={small:'Small',medium:'Medium',large:'Large'};
const BODY_PREFERENCE_LABEL={small:'Subtle',medium:'Balanced',large:'Full'};
const BODY_HEIGHT_LABEL={short:'Shorter',tall:'Taller'};
const BODY_QUERY_MODE=new URLSearchParams(window.location.search).get('body_ux');
const BODY_EXPERIENCE=['gallery','guided'].includes(BODY_QUERY_MODE)
  ?BODY_QUERY_MODE
  :(document.getElementById('atelier-landing')?.dataset.bodyExperience||'gallery');
const bodyGalleryFilters={breast:new Set(),butt:new Set(),heightBand:new Set()};
const bodyGuidePrefs={breast:'medium',butt:'medium',heightBand:'short'};
let bodyGuideShowAll=false;
let bodyView='lace';
let bodyAngleIndex=0;
let bodyAutoRotate=!window.matchMedia('(prefers-reduced-motion: reduce)').matches;
let bodyAutoTimer=null;
const BODY_AUTO_MS=525; // 25% slower than the source finder's 420ms cadence.
const BODY_ANGLES=[
  {deg:0,label:'Front'},
  {deg:45,label:'Front three-quarter · left'},
  {deg:90,label:'Left profile'},
  {deg:135,label:'Back three-quarter · left'},
  {deg:180,label:'Back'},
  {deg:225,label:'Back three-quarter · right'},
  {deg:270,label:'Right profile'},
  {deg:315,label:'Front three-quarter · right'},
];

function bodyTierFor(model){
  if(!model) return BODY_OPTIONS[1];
  const key=model.height<=158?'petite':(model.height>=167?'statuesque':'classic');
  return BODY_OPTIONS.find(o=>o.key===key)||BODY_OPTIONS[1];
}
// `view` defaults to the Body studio's lace/nude toggle; the stage close-ups
// pass 'nude' explicitly (the option they illustrate is on her skin).
function bodyAssetData(model,view=bodyView){
  /* No fall back to the `natural` tone. It used to read
       assets[skinId][view] || assets.natural[view] || null
     which was dead code while every body carried all five tones — but the
     moment one does not, it paints a LIGHT body under whatever head the
     customer chose. A missing render must read as missing, not as the wrong
     skin; the pickers below never offer a body the tone has no render for, so
     reaching this null means a real bug rather than a cosmetic fallback. */
  if(!bodyHasTone(model,state.skin.key)) return null;
  return model?.assets?.[bodyToneId(state.skin.key)]?.[view]||null;
}
// Body sprites and thumbs are THEME assets, and a theme-asset URL reaches
// cdnImg as <shop>/cdn/shop/t/12/assets/..., which its `cdn.shopify.com/s/files/`
// test does not match — so every one of them was served at full size to every
// device. A sprite is an 8-frame strip up to 4744x1600 (~330 KB served) and the
// review stage paints it 424px wide on a phone; a thumb is 583x1600 (~67 KB)
// painted 127px in the body picker.
//
// Shopify's ?width= does work on these (verified against live: 67 KB -> 37 KB
// at width=300), and every frame offset in this file is a percentage of the
// strip, so a narrower strip crops to exactly the same frames.
//
// `w` is a DEVICE-pixel width from fitWidth(); 0 means "ask for the original".
function bodyAssetUrl(model,kind,view=bodyView,w=0){
  const file=bodyAssetData(model,view)?.[kind];
  if(!file) return '';
  const url=new URL(file,CUSTOMIZER_ASSET_BASE).href;
  return w?url+(url.indexOf('?')>-1?'&':'?')+'width='+w:url;
}
// Native pixel width of a body asset, so fitWidth() never asks the CDN to
// upscale. Sprites are 8 frames of `width` side by side.
function bodyAssetNative(model,kind,view=bodyView){
  const a=bodyAssetData(model,view);
  if(!a) return 0;
  return kind==='sprite'?(a.width||593)*8:(a.width||583);
}
// One sprite width per device class. It is deliberately NOT per surface:
// warmBodySprite() prefetches this exact URL, and a warm that misses is a
// second ~300 KB download rather than a saving. The widest any surface paints
// the strip is the studio turntable — ~1240px at 1440 wide, ~875px on a phone —
// and the review stage and all-angles grid paint it smaller.
function bodySpriteW(model,view=bodyView){
  // Phone body close-ups (breast/butt feel, nipple size, piercing, pubic hair,
  // navel, enhancements) paint the NUDE strip zoomed 3-6x past the 900px turntable
  // this rung was sized for: at 2048 the visible crop is ~220px of source spread
  // over ~660 device px, with blocky WebP halos along the silhouette. The original
  // strip is ~330 KB vs 172 KB, already built on the CDN (no cold ~7 s variant
  // build), and still the same URL for warmBodySprite() and the stage.
  if(mobileMQ.matches&&view==='nude') return 0;
  return fitWidth(mobileMQ.matches?900:1280,bodyAssetNative(model,'sprite',view));
}
function applyBodyModel(model){
  if(!model) return;
  state.bodyModel=model;
  state.body=bodyTierFor(model);
  state.breastSize=BREAST_SIZE.find(o=>o.key===BODY_BREAST_TO_CHOICE[model.breast])||BREAST_SIZE[1];
  state.buttShape=BUTT_SHAPE.find(o=>o.key===BODY_BUTT_TO_CHOICE[model.butt])||BUTT_SHAPE[1];
  window.SBD_CURRENT_BODY_UID=model.uid||model.id||'';
  document.dispatchEvent(new CustomEvent('sbd:body-model-change',{detail:{body:model}}));
}
// Breast and butt "feel" are separate arrays so each can carry its own reference
// photo — the gel/standard look differs between breast and butt. (Breast silicone
// keeps a swatch: there is no standard-breast reference photo in the asset set.)
// Breast: gel-infused = DEFAULT, always FREE — cosmetic top-level choice (no SKU).
// Butt: gel-infused is priced by its live Shopify add-on variant.
const BREAST_FEEL=[
  {key:'silicone',label:'Silicone',desc:'Firm, precisely sculpted',price:0,sw:'sw-a'},
  {key:'gel',label:'Gel-infused',desc:'Softer give, warms to touch',price:0,img:'https://cdn.shopify.com/s/files/1/1011/2752/0539/files/gel-breast.jpg',alt:'Gel-infused breast, reference photo'},
];
const BUTT_FEEL=[
  {key:'silicone',label:'Silicone',desc:'Firm, precisely sculpted',price:0,img:'https://cdn.shopify.com/s/files/1/1011/2752/0539/files/standard-butt.jpg',alt:'Standard silicone butt, reference photo'},
  {key:'gel',label:'Gel-infused',desc:'Softer give, warms to touch',billable:true,img:'https://cdn.shopify.com/s/files/1/1011/2752/0539/files/gel-butt.jpg',alt:'Gel-infused butt, reference photo'},
];
// Areola size — nipple/areola diameter shown in the Breast step.
// TODO: confirm which size is the standard/default (defaulting to 4 cm) and pricing (treated as included/no-cost).
// Nipple size (was "Areola") — labels only, no descriptions.
// The cards are theme assets, not Shopify files: these started life as 500px PNGs and a PNG is
// the one thing the Shopify CDN will not re-encode, so `?width=168` still shipped ~48 KB per
// 84px tile. Re-cut to 320px WebP (~10 KB each), sized once here because a theme-asset URL
// passes through cdnImg untouched.
const areolaCard=cm=>new URL(`sbd-areola-size-${cm}.webp`,CUSTOMIZER_ASSET_BASE).href;
const AREOLA_SIZES=[
  {key:'3cm',label:'3 cm',price:0,img:areolaCard('3cm'),alt:'3 cm nipple size'},
  {key:'4cm',label:'4 cm',price:0,img:areolaCard('4cm'),alt:'4 cm nipple size'},
  {key:'5cm',label:'5 cm',price:0,img:areolaCard('5cm'),alt:'5 cm nipple size'},
];
// Breast (nipple) piercing — on/off. Pricing confirmed by the 5.1 locked price sheet
// (approved 2026-08-14): "Nipple + Navel Piercing" is $10 combined (Irontech floor
// $6.50 combined) — split $5/$5 across the two independent steps below since this
// concept renders them in separate wizard steps (breast step vs. finishing touches),
// unlike index.html which merged them into one combined line item. Flag for review:
// confirm whether this should also be merged into a single combined choice.
const BREAST_PIERCING=[
  {key:'off',label:'Off',price:0,noSwatch:true},
  {key:'on',label:'On',billable:true,img:'https://cdn.shopify.com/s/files/1/1011/2752/0539/files/piercing-nipple.jpg',alt:'Nipple piercing'},
];
// Navel piercing — shown under Finishing Touches. Pricing confirmed by the 5.1 locked
// price sheet (approved 2026-08-14) — see note above BREAST_PIERCING.
const NAVEL_PIERCING=[
  {key:'none',label:'None',price:0,noSwatch:true},
  {key:'style1',label:'Style 1',billable:true,img:'https://cdn.shopify.com/s/files/1/1011/2752/0539/files/navel-z1.jpg',alt:'Navel piercing, style 1'},
  {key:'style2',label:'Style 2',billable:true,img:'https://cdn.shopify.com/s/files/1/1011/2752/0539/files/navel-z2.jpg',alt:'Navel piercing, style 2'},
];
// Makeup preview styles from the PDP Buybox branch. Presets are included; only
// a customer-supplied reference uses Shopify's Custom AI Makeup add-on variant.
// `uid` and `ref` are both written to the order so the factory has a durable,
// machine-readable selection and the exact source image used by generation.
const MAKEUP_STYLES=[
  {key:'natural',uid:'makeup_irontech_natural',label:'Natural',desc:'Soft, barely-there finish',price:0,
    img:'https://customizerbackend.siliconeangels.com/storage/v1/object/public/customizer/swatches/makeup/thumbs/natural.jpg',
    ref:'https://s3.us-east-005.backblazeb2.com/siliconedolls/swatches/makeup/natural.jpg'},
  {key:'softglam',uid:'makeup_irontech_softglam',label:'Soft Glam',desc:'Warm, everyday glow',price:0,
    img:'https://customizerbackend.siliconeangels.com/storage/v1/object/public/customizer/swatches/makeup/thumbs/softglam.jpg',
    ref:'https://s3.us-east-005.backblazeb2.com/siliconedolls/swatches/makeup/softglam.jpg'},
  {key:'boldevening',uid:'makeup_irontech_boldevening',label:'Bold Evening',desc:'Graphic liner, deep lip',price:0,
    img:'https://customizerbackend.siliconeangels.com/storage/v1/object/public/customizer/swatches/makeup/thumbs/boldevening.jpg',
    ref:'https://s3.us-east-005.backblazeb2.com/siliconedolls/swatches/makeup/boldevening.jpg'},
  {key:'smokeyeye',uid:'makeup_irontech_smokeyeye',label:'Smokey Eye',desc:'Classic smoky wing',price:0,
    img:'https://customizerbackend.siliconeangels.com/storage/v1/object/public/customizer/swatches/makeup/thumbs/smokeyeye.jpg',
    ref:'https://s3.us-east-005.backblazeb2.com/siliconedolls/swatches/makeup/smokeyeye.jpg'},
  {key:'sweetrose',uid:'makeup_irontech_sweetrose',label:'Sweet Rose',desc:'Fresh rosy glow, soft pink lip',price:0,
    img:'https://customizerbackend.siliconeangels.com/storage/v1/object/public/customizer/swatches/makeup/thumbs/sweetrose.jpg',
    ref:'https://customizerbackend.siliconeangels.com/storage/v1/object/public/customizer/swatches/makeup/sweetrose.jpg'},
  {key:'oriental',uid:'makeup_irontech_oriental',label:'Oriental',desc:'Porcelain glow, gradient lip',price:0,
    img:'https://customizerbackend.siliconeangels.com/storage/v1/object/public/customizer/swatches/makeup/thumbs/oriental.jpg',
    ref:'https://customizerbackend.siliconeangels.com/storage/v1/object/public/customizer/swatches/makeup/oriental.jpg'},
  {key:'classicred',uid:'makeup_irontech_classicred',label:'Classic Red',desc:'Matte red lip, winged liner',price:0,
    img:'https://customizerbackend.siliconeangels.com/storage/v1/object/public/customizer/swatches/makeup/thumbs/classicred.jpg',
    ref:'https://customizerbackend.siliconeangels.com/storage/v1/object/public/customizer/swatches/makeup/classicred.jpg'},
  {key:'darkglam',uid:'makeup_irontech_darkglam',label:'Dark Glam',desc:'Smoky eyes, deep wine lip',price:0,
    img:'https://customizerbackend.siliconeangels.com/storage/v1/object/public/customizer/swatches/makeup/thumbs/darkglam.jpg',
    ref:'https://customizerbackend.siliconeangels.com/storage/v1/object/public/customizer/swatches/makeup/darkglam.jpg'},
  {key:'bombshell',uid:'makeup_irontech_bombshell',label:'Bombshell',desc:'Bronzed glow, glossy nude lip',price:0,
    img:'https://customizerbackend.siliconeangels.com/storage/v1/object/public/customizer/swatches/makeup/thumbs/bombshell.jpg',
    ref:'https://customizerbackend.siliconeangels.com/storage/v1/object/public/customizer/swatches/makeup/bombshell.jpg'},
  {key:'custom',uid:'makeup_irontech_custom',label:'Fully Custom',desc:'Matched from your own reference photo',billable:true,noSwatch:true},
];
const MAKEUP_FN_URL='https://customizerbackend.siliconeangels.com/functions/v1/makeup';
// "Email me my design" (Review step) + ?design=<token> restore. See
// supabase/functions/saved-builds/index.ts for the contract.
const SAVED_BUILD_FN_URL='https://customizerbackend.siliconeangels.com/functions/v1/saved-builds';
// Hard ceiling on one preview generation. The service itself takes 15–40s, so
// this is a stalled-connection backstop, not an impatience timer.
const MAKEUP_TIMEOUT_MS=60000;
// ── Face photo assets ────────────────────────────────────────────────────────
// Composited portraits live per face + skin tone at:
//   Heads/{Face}/Hair/{Dir}/aligned/eyes_定制{N}/{Face}-{Dir}-{eyeTok}-{hair}.png
// N is the eye colour (1–29). Where eyeInName is true the filename encodes the eye
// number (定制{N}); otherwise it uses the literal token "Green" and relies on the
// folder name for the eye colour. Each face+tone ships its OWN hair set (different
// counts and names), so hair options are built per selected face + skin tone.
// All six faces now ship all five tones. Luna's Dark Tanned set landed
// 2026-08-31 (full 30-wig chart-code set); any tone that regresses to an empty
// hair list is hidden by skinTonesFor() rather than shown as "coming soon".
const LUNA_SKINS={
  // 2026-08-20 audit: the uncoded extras were visual duplicates of coded wigs
  // (White 03/05/12, Tanned 07/14/33, Black 27) — dropped, since an uncoded wig
  // cannot be ordered unambiguously anyway. Every tone below lists real chart
  // codes only.
  natural:{dir:'White',      eyeInName:false, hairs:['02','04','06','07','08','09','10','11','13','15','31','16','17','18','19','20','21','22','23','24','25','26','27','28','32','29','30','33','34','35'], codes:['J1','J2','J3','J4','J5','J6','J7','J8','J9','J10','J11','J12','W1','W2','W3','W4','W5','W6','W7','W8','W9','W10','W11','W12','W13','W14','W15','W16','W17','W18']},
  glow:   {dir:'Glow',       eyeInName:false, hairs:['j1','j2','j3','j4','j5','j6','j7','j8','j9','j10','new01','new02','new03','new04','new05','new06','new07','new08','new09','new10','new11','new12','new13','new14','new15','new16','new17','new18','new19','new20'], codes:['J1','J2','J3','J4','J5','J6','J7','J8','J9','J10','J11','J12','W1','W2','W3','W4','W5','W6','W7','W8','W9','W10','W11','W12','W13','W14','W15','W16','W17','W18']}, // 2026-08-18: full eye matrix regenerated with ASCII names; the old 定制 keys were invalid Supabase keys and the tone never worked
  // '34' (Tanned) and '29'-'31' (Black) are the 2026-08-20 fills for the chart
  // gaps the audit found (Tanned W6; Black J8/J9/W1) — generated on the
  // validated gpt-image-2 pipeline and fanned out across all 29 eyes.
  tanned: {dir:'Tanned',     eyeInName:false, hairs:['01','02','03','04','05','06','08','09','10','11','12','13','15','16','17','18','19','34','21','22','23','24','25','26','27','28','29','30','31','32'], codes:['J1','J2','J3','J4','J5','J6','J7','J8','J9','J10','J11','J12','W1','W2','W3','W4','W5','W6','W7','W8','W9','W10','W11','W12','W13','W14','W15','W16','W17','W18']},
  darktan:{dir:'DarkTanned', eyeInName:false, hairs:['J1','J2','J3','J4','J5','J6','J7','J8','J9','J10','J11','J12','W1','W2','W3','W4','W5','W6','W7','W8','W9','W10','W11','W12','W13','W14','W15','W16','W17','W18'], codes:['J1','J2','J3','J4','J5','J6','J7','J8','J9','J10','J11','J12','W1','W2','W3','W4','W5','W6','W7','W8','W9','W10','W11','W12','W13','W14','W15','W16','W17','W18']},
  black:  {dir:'Black',      eyeInName:false, hairs:['01','02','03','04','05','06','07','29','30','08','09','10','31','11','12','13','14','15','16','17','18','19','20','21','22','23','24','25','26','28'], codes:['J1','J2','J3','J4','J5','J6','J7','J8','J9','J10','J11','J12','W1','W2','W3','W4','W5','W6','W7','W8','W9','W10','W11','W12','W13','W14','W15','W16','W17','W18']},
};
// Suki & Kitty: full five-tone render sets (Green eye token in filename, eye
// colour by folder). Hair lists are the intersection present in ALL 29 eye
// folders for that tone, generated from the actual files on disk 2026-08-15.
const SUKI_SKINS={
  natural:{dir:'White', eyeInName:false, hairs:['01','03','04','05','06','07','08','09','10','11','12','13','14','15','16','17','18','19','20','21','22','23','24','25','26','27','28','29','J2','J11'], codes:['J1','J3','J4','J5','J6','J7','J8','J9','J10','J12','W1','W2','W3','W4','W5','W6','W7','W8','W9','W10','W11','W12','W13','W14','W15','W16','W17','W18','J2','J11']},
  glow:{dir:'Glow', eyeInName:false, hairs:['01','02','03','04','05','06','07','08','09','10','11','12','13','14','15','16','17','18','19','20','21','22','23','24','25','26','27','28','29','30'], codes:['J1','J2','J5','J6','J7','J8','J9','J10','J11','J12','W1','W2','W3','W4','W5','W6','W7','W8','W9','W10','W11','W12','W13','W14','W15','W16','W17','W18','J3','J4']},
  tanned:{dir:'Tanned', eyeInName:false, hairs:['01','02','03','04','05','06','07','08','09','10','11','12','13','14','15','16','18','19','20','21','22','23','24','25','26','27','29','30','W6','W18'], codes:['J1','J2','J3','J4','J5','J6','J7','J8','J9','J10','J11','W1','W2','W3','W4','W5','W7','W8','W9','W11','W12','W13','W14','W15','W16','W17','J12','W10','W6','W18']},
  darktan:{dir:'DarkTanned', eyeInName:false, hairs:['01','02','03','04','05','06','07','08','09','10','11','12','13','14','15','16','17','18','19','20','21','22','23','24','25','26','27','28','29','30'], codes:['J1','J2','J3','J4','J5','J6','J7','J8','J9','J10','J11','J12','W1','W2','W3','W4','W5','W6','W7','W8','W9','W10','W11','W12','W13','W14','W15','W16','W17','W18']}, // 2026-08-18: set rebuilt from the corrected DT base on the standard wig chart; old idx files retired (unidentified batch, no factory codes)
  black:{dir:'Black', eyeInName:false, hairs:['01','02','03','04','05','06','07','08','09','10','11','12','13','14','15','16','17','18','19','20','21','22','23','24','25','26','27','28','29','30'], codes:['J2','J3','J4','J5','J6','J7','J9','J10','J11','J12','W3','W4','W5','W6','W7','W8','W10','W11','W12','W13','W15','W16','W17','W18','J1','W2','W9','J8','W14','W1']},
};
/* KITTY — rewired to the supplier's chart codes 2026-09-07.

   Her old bald base was never the Irontech S32 head. The 2026-08-26 audit put
   it at ArcFace 0.434 against the factory photos on a pass-under-0.35 scale:
   the old face is slim, angular and thin-lipped where the real S32 is short,
   round and plump-cheeked with large eyes. Every wig composite derived from
   that base inherited the wrong face.

   All five tone bases were regenerated from the factory photos (0.177 vs
   factory), the full 30-wig matrix was rebuilt on them, aligned and cropped,
   and fanned out across the 29 irises — 4,350 files — and the owner confirmed
   that upload ran clean the same night. The new objects are keyed by CHART CODE
   (Kitty-<Tone>-Green-J9.webp), the convention Penny already uses.

   The theme rewiring that was supposed to accompany it never landed, so the
   customizer went on requesting the OLD legacy per-tone indices
   (Kitty-<Tone>-Green-09.webp), which still exist in the bucket and still hold
   the wrong face. Both key sets resolve 200, which is why nothing 404'd and
   nothing looked broken — it just quietly served the wrong woman for twelve
   days.

   Verified before this change: all 30 chart codes return 200 in all five tones
   (150/150), and a 100-key spot-check across 4 eye colours confirmed the fanout.
   `hairs` === `codes` now, as for Penny — the storage key IS the chart code, so
   the per-doll-per-tone index ambiguity is gone for this head. */
/* Spelled out rather than aliased to PENNY_CHART: that const is declared below
   this block, so referencing it here would hit the temporal dead zone. */
const KITTY_CHART=['J1','J2','J3','J4','J5','J6','J7','J8','J9','J10','J11','J12','W1','W2','W3','W4','W5','W6','W7','W8','W9','W10','W11','W12','W13','W14','W15','W16','W17','W18'];
/* Kitty's tone mapping is the plain one — dir === tone — and the 2026-09-07
   "shift everything one step lighter" remap that briefly lived here has been
   REVERTED. It was wrong, and the way it was wrong is worth recording.

   That remap rested on a homegrown neck-band luminance metric. tonekit.py's
   header warns against exactly this: "Measure the face, not the canvas… it
   reads a head as correct exactly when that head is mis-framed", and "Two
   metrics, and they are not interchangeable" — face_tone for light tones,
   face_tone_big for Black. Measured properly with tonekit, Kitty's stored
   ladder was already right, and sits alongside Penny's almost step for step:

              White    Glow  Tanned  DarkTan   Black
     Kitty    164.1   136.3   109.9    101.3    72.1
     Penny    160.3   135.3   108.3     90.8    71.6
     Luna     154.1   128.7   133.2     93.4    72.2

   Monotonic, evenly spaced, and matching the head nobody has ever complained
   about. The shift took a correct ladder, moved every label one step off its
   render, and cost two tones. (Note Luna, not Kitty, is the head with a real
   defect here — her Tanned reads LIGHTER than her Glow.)

   Per-wig tone spread inside one tone is large — 51 across eight sampled live
   Glow wigs — but that is inherent to the hair swap shading the skin, and it is
   the same for every head. It is not evidence that a tone is mislabelled.

   If a tone ever does need re-judging: use tonekit.measure, compare against
   Penny at the same tone, and do not correct a finished render's skin colour —
   "per-channel correction through a body mask fixes skin but turns blonde wigs
   ashy, because the mask covers hair too". Fix the base and regenerate. */
const KITTY_SKINS={
  natural: {dir:'White',      eyeInName:false, hairs:KITTY_CHART, codes:KITTY_CHART},
  glow:    {dir:'Glow',       eyeInName:false, hairs:KITTY_CHART, codes:KITTY_CHART},
  tanned:  {dir:'Tanned',     eyeInName:false, hairs:KITTY_CHART, codes:KITTY_CHART},
  darktan: {dir:'DarkTanned', eyeInName:false, hairs:KITTY_CHART, codes:KITTY_CHART},
  black:   {dir:'Black',      eyeInName:false, hairs:KITTY_CHART, codes:KITTY_CHART},
};
// Penny: added 2026-08-19 from the August regeneration batch. Her storage key
// IS the supplier's chart code, so `hairs` and `codes` are the same list — the
// other three dolls carry a per-doll-per-tone index that means a different wig
// for each of them, which is the ambiguity that retired Suki's old Dark Tanned
// set. Codes absent below have no render that both exists and aligns; see
// Heads/Penny/_penny_wig_key.json for which, and why.
// 2026-08-20: Penny completed to the FULL 30-wig chart in all five tones
// (_penny_wig_key.json — VLM identification pass + 21-slot regeneration).
// hairs === codes because her storage key IS the supplier's chart code.
const PENNY_CHART=['J1','J2','J3','J4','J5','J6','J7','J8','J9','J10','J11','J12','W1','W2','W3','W4','W5','W6','W7','W8','W9','W10','W11','W12','W13','W14','W15','W16','W17','W18'];
const PENNY_SKINS={
  natural: {dir:'White',      eyeInName:false, hairs:PENNY_CHART, codes:PENNY_CHART},
  glow:    {dir:'Glow',       eyeInName:false, hairs:PENNY_CHART, codes:PENNY_CHART},
  tanned:  {dir:'Tanned',     eyeInName:false, hairs:PENNY_CHART, codes:PENNY_CHART},
  darktan: {dir:'DarkTanned', eyeInName:false, hairs:PENNY_CHART, codes:PENNY_CHART},
  black:   {dir:'Black',      eyeInName:false, hairs:PENNY_CHART, codes:PENNY_CHART},
};
// Eileen (S40) and Ivy (S27): generated 2026-08-27 on the same pipeline as the
// Kitty/Penny regeneration — chart-code storage keys, full 30-wig chart, all
// five tones, 29 eyes, identity-QA'd per image. They shipped in the Bronze-era
// build but were dropped when the UID build launched on Cam's Body; restored
// 2026-08-31 after re-verifying the bucket (all thumbnail surfaces + the J9
// diagonal probe 200 across both heads × 5 tones). Like Penny, their storage
// key IS the supplier's chart code, so hairs === codes.
const EILEEN_SKINS={
  natural: {dir:'White',      eyeInName:false, hairs:PENNY_CHART, codes:PENNY_CHART},
  glow:    {dir:'Glow',       eyeInName:false, hairs:PENNY_CHART, codes:PENNY_CHART},
  tanned:  {dir:'Tanned',     eyeInName:false, hairs:PENNY_CHART, codes:PENNY_CHART},
  darktan: {dir:'DarkTanned', eyeInName:false, hairs:PENNY_CHART, codes:PENNY_CHART},
  black:   {dir:'Black',      eyeInName:false, hairs:PENNY_CHART, codes:PENNY_CHART},
};
const IVY_SKINS={
  natural: {dir:'White',      eyeInName:false, hairs:PENNY_CHART, codes:PENNY_CHART},
  glow:    {dir:'Glow',       eyeInName:false, hairs:PENNY_CHART, codes:PENNY_CHART},
  tanned:  {dir:'Tanned',     eyeInName:false, hairs:PENNY_CHART, codes:PENNY_CHART},
  darktan: {dir:'DarkTanned', eyeInName:false, hairs:PENNY_CHART, codes:PENNY_CHART},
  black:   {dir:'Black',      eyeInName:false, hairs:PENNY_CHART, codes:PENNY_CHART},
};
const SBD_CDN='https://customizerbackend.siliconeangels.com/storage/v1/object/public/customizer';
// ── Image delivery ───────────────────────────────────────────────────────────
// Every option photo is painted as a small tile in a grid, but both CDNs store
// originals at full size — the eye close-ups are ~1350px / up to 199 KB each,
// and one step renders all 29 of them plus 30 hair portraits. Ask each CDN for
// the box we actually paint instead.
// The two CDNs are NOT interchangeable here: Shopify's ?width= is a true
// aspect-preserving resize, while Supabase's renderer keeps the SOURCE height
// unless a height is passed too — width alone comes back squashed.
const SBD_RENDER=SBD_CDN.replace('/object/public/','/render/image/public/');
const IMG_Q=72;         // option tiles — visually lossless at tile sizes
const IMG_Q_PREVIEW=80; // the large live-preview portrait
function cdnImg(url,w,h,opts){
  if(!url) return url;
  const o=opts||{};
  // Guarded so this block is character-identical to concept.html's copy, where
  // SBD_CDN is null because the prototype reads renders off disk.
  if(SBD_CDN&&url.indexOf(SBD_CDN)===0)
    return `${SBD_RENDER}${url.slice(SBD_CDN.length)}?width=${w}&height=${h}&resize=${o.fit||'cover'}&quality=${o.q||IMG_Q}`;
  if(url.indexOf('cdn.shopify.com/s/files/')>-1)
    // `quality` was missing here, so these came back at the CDN's default (~40% heavier than
    // IMG_Q at tile sizes). `format` is ignored on this path — a PNG stays a PNG, which is why
    // the nipple-size cards below are theme-asset WebP rather than Shopify files.
    return url+(url.indexOf('?')>-1?'&':'?')+'width='+w+'&quality='+(o.q||IMG_Q);
  return url; // anything else (theme assets, data URIs) passes through untouched
}
// These URLs are interpolated into HTML strings, so the query separators have to
// be escaped — a bare &... in an attribute is parsed as a character reference.
function attrUrl(u){ return u.replace(/&/g,'&amp;'); }
// src + 1x/2x srcset, so a non-retina display doesn't pay for retina pixels.
// The width/height attributes reserve the box before the bytes land; CSS
// aspect-ratio still governs the painted size.
// A URL with no resizing behind it yields the same string at every size, and
// claiming that one file is both the 1x and the 2x asset is a lie the browser
// acts on — so the srcset is emitted only when the candidates really differ.
function imgAttrs(url,w,h,opts){
  const a=attrUrl(cdnImg(url,w,h,opts)), b=attrUrl(cdnImg(url,w*2,h*2,opts));
  const set=a===b?'':` srcset="${a} 1x, ${b} 2x"`;
  // Collapsed: describe the image but do not fetch it. renderAll() on open
  // rebuilds these same tags with a real src.
  if(custDeferImages) return `data-src="${a}" width="${w}" height="${h}"`;
  return `src="${a}"${set} width="${w}" height="${h}"`;
}
// Widest a square tile ever paints, per grid density. This is the DESKTOP
// column math (at >=900px the question panel is 768px: 1180 wrap - 340 preview
// - 32 gap, less card padding and the 12px grid gaps) and it is now only the
// fallback — tileBox() below measures the real box instead.
//
// It used to be the only answer, and the note that mobile "lands in the same
// range" was wrong: below 900px sbd-polish.css turns these grids into
// horizontal scrollers and caps .opt-photo by viewport HEIGHT (max-height:
// Nsvh), so a .pair card paints 106px at 375x812 while this table asked the
// CDN for 352 — 704 on a retina phone, ~44x the pixels it paints.
const TILE_W={'cols-6':120,'cols-5':144,'cols-4':176,'cols-3':232,'cols-2':352};
function tileWidth(cols){
  const c=cols||'';
  if(c.indexOf('compact-photo')>-1) return 84; // .compact-photo caps .opt-photo
  const k=Object.keys(TILE_W).find(k=>c.indexOf(k)>-1);
  return k?TILE_W[k]:232;
}
// Round a CSS-pixel box up to a rung of device pixels. Rungs (rather than the
// exact number) keep the CDN building a handful of variants instead of one per
// viewport: the first request for a variant it has not built yet costs ~2 s on
// live, which is the whole reason warmBodySprite() exists. DPR is capped at 2 —
// a 3x phone painting a 106px tile does not need a 318px file.
// 84 is a rung because .compact-photo pins those tiles to exactly 84px.
const IMG_RUNGS=[64,84,96,128,160,192,224,256,320,384,448,512,640,768,896,1024,1280,1600,2048,2560,3200];
function ceilRung(px){ return IMG_RUNGS.find(r=>r>=px)||Math.ceil(px); }
// Device-pixel width for an <img> that carries no srcset — the body sprites and
// thumbs, whose src is assigned straight from JS. Tiles must NOT use this: they
// go through imgAttrs, which is handed a CSS-pixel box and adds the 2x
// candidate itself, so doubling here would double again.
function fitWidth(cssPx,native){
  const rung=ceilRung(Math.ceil(cssPx*Math.min(window.devicePixelRatio||1,2)));
  // A rung at least as wide as the source would only upscale: ask for the
  // original (0 = no ?width=) so the CDN never builds a pointless variant.
  return native&&rung>=native?0:rung;
}
// The painted size of an option tile, measured rather than derived.
//
// The rules that decide it live in sbd-polish.css and key off viewport height
// as well as width, across five height breakpoints — restating them here would
// be a second copy of a fact that already has one home, and it would drift.
// So: render the group's OWN markup, srcless, into a hidden probe, read the
// box back, and throw the probe away. Faithful by construction, because the
// probe and the real grid come from the same builder.
//
// Cached per group per viewport, so this is one extra layout the first time a
// step is seen at a given size and nothing thereafter.
const TILE_BOX=new Map();
function tileBox(key,buildHtml){
  const k=key+'|'+window.innerWidth+'x'+window.innerHeight;
  if(TILE_BOX.has(k)) return TILE_BOX.get(k);
  let box=null;
  const host=document.getElementById('questionPanel');
  if(host&&host.clientWidth>0){
    const cs=getComputedStyle(host);
    const inner=host.clientWidth-parseFloat(cs.paddingLeft||0)-parseFloat(cs.paddingRight||0);
    const probe=document.createElement('div');
    probe.setAttribute('aria-hidden','true');
    // Off-screen but still inside #questionPanel, because every rule that
    // sizes these tiles is written as a descendant of #atelier-landing. An
    // explicit width stands in for the flow width absolute positioning drops.
    probe.style.cssText='position:absolute;top:0;left:-99999px;visibility:hidden;'+
      'pointer-events:none;width:'+Math.round(inner)+'px';
    try{
      probe.innerHTML=buildHtml();
      host.appendChild(probe);
      const photo=probe.querySelector('.opt-photo');
      if(photo){
        const r=photo.getBoundingClientRect();
        if(r.width>0&&r.height>0) box=[Math.round(r.width),Math.round(r.height)];
      }
    }catch(e){ box=null; }
    finally{ if(probe.parentNode) probe.parentNode.removeChild(probe); }
  }
  // Only a real measurement is cached. The panel has no width until the
  // customizer is opened, and caching that miss would pin the group to the
  // desktop fallback for the rest of the session.
  if(box) TILE_BOX.set(k,box);
  return box;
}
// Tile height follows the CSS aspect-ratio of the class the group asked for.
// Note 'opt-portrait' (hair) has no CSS rule, so it keeps the default 1/1.
function tileHeight(w,photoAspect){
  const a=photoAspect||'';
  if(a.indexOf('wide')>-1) return Math.round(w/2.7); // .opt-photo.wide
  if(a.indexOf('tall')>-1) return Math.round(w*5/3); // .opt-photo.tall
  return w;                                          // .opt-photo default 1/1
}
const FACE_ASSETS={
  luna:  {root:SBD_CDN+'/heads/luna',   prefix:'Luna',   skins:LUNA_SKINS},
  suki:  {root:SBD_CDN+'/heads/suki',   prefix:'Suki',   skins:SUKI_SKINS},
  kitty: {root:SBD_CDN+'/heads/kitty',  prefix:'Kitty',  skins:KITTY_SKINS},
  penny: {root:SBD_CDN+'/heads/penny',  prefix:'Penny',  skins:PENNY_SKINS},
  eileen:{root:SBD_CDN+'/heads/eileen', prefix:'Eileen', skins:EILEEN_SKINS},
  ivy:   {root:SBD_CDN+'/heads/ivy',    prefix:'Ivy',    skins:IVY_SKINS},
};
function faceImg(faceKey,skinKey,eyeNum,hairKey){
  const f=FACE_ASSETS[faceKey]; if(!f) return '';
  const s=f.skins[skinKey]; if(!s||!hairKey) return '';
  const eyeTok=s.eyeInName?('定制'+eyeNum):'Green';
  const eyeDir=String(eyeNum).padStart(2,'0');
  return encodeURI(`${f.root}/${s.dir}/eyes_${eyeDir}/${f.prefix}-${s.dir}-${eyeTok}-${hairKey}.webp`);
}
// Back-compat wrapper: existing Luna-only call sites keep passing (skin, eye, hair).
function lunaImg(skinKey,eyeNum,hairKey){ return faceImg('luna',skinKey,eyeNum,hairKey); }
const HAIR_THUMB_EYE=19; // hair-button previews use eye colour #19
/* Hair options for a given face + skin tone — that face's own set for the tone.
   Each option carries `code`, its position on the SUPPLIER'S wig chart (J1-J12
   then W1-W18). That code is the only hair identifier the factory can act on:
   the storage index in `hairs` is per-doll-per-tone, so "10" is J9 for
   Luna/Tanned but J7 for Luna/White and J12 for Suki/Glow. Recording the index
   on an order would be ambiguous enough to fit the wrong wig.
   Every selectable render must resolve to a canonical UID. A future render with
   no chart mapping is deliberately omitted instead of creating an unorderable,
   ambiguous choice. */
function hairListFor(faceKey,skinKey){
  const f=FACE_ASSETS[faceKey]; const s=f&&f.skins[skinKey]; const hairs=(s&&s.hairs)||[];
  const codes=(s&&s.codes)||[];
  return hairs.map((h,i)=>{
    const code=codes[i]||null;
    const uid=code&&HAIR_UID_BY_CODE[code];
    return uid?{key:uid,uid,hair:h,code,label:'Style '+(i+1),img:faceImg(faceKey,skinKey,HAIR_THUMB_EYE,h),alt:'Hairstyle '+(i+1)}:null;
  }).filter(Boolean);
}
function retainHairSelection(list,current){
  if(!list.length) return null;
  if(!current) return list[0];
  return list.find(option=>option.uid===current.uid)
    ||list.find(option=>option.code===current.code)
    ||list[0];
}
// First skin tone that actually has renders for a face (used when switching faces).
function firstSkinFor(faceKey){
  const f=FACE_ASSETS[faceKey]; if(!f) return null;
  return Object.keys(f.skins).find(k=>(f.skins[k].hairs||[]).length>0)||null;
}
// Face-selection thumbnail for a face in a given skin tone (eye 1, that tone's first
// hair). Faces without renders for the tone (e.g. Suki/Kitty outside Glow) fall back
// to their first available tone so the face still shows; returns '' if none exist.
/* Card thumbnails (face cards, skin-tone cards) all show the SAME wig, so those
   cards compare only the thing they're asking about. That wig is J9 on the
   supplier's chart; these renders are still stored under the old per-folder
   index, so J9 has a different filename in every doll+tone — hence the table.
   See Heads/Finished Heads/_hair_number_key.txt for the full mapping.
   Luna was never rendered with J9 in Black, so that one falls back to J10, the
   closest long dark style. */
const CARD_HAIR={
  luna:  {natural:'13', glow:'j9', tanned:'10', darktan:'J9' /* chart-code key — DT set is chart-keyed */, black:'30' /* J9 — rendered 2026-08-20 */},
  suki:  {glow:'07'},
  // kitty has no entry any more: her storage key IS the chart code since the
  // 2026-09-07 rewiring, so cardHairFor derives J9 from the codes table. The old
  // {glow:'09', darktan:'08'} overrides pointed at legacy-index objects that
  // still exist in the bucket and still hold the superseded face — as an
  // override they would have WON over the derived value and kept the two face
  // cards wrong on their own.
  penny: {natural:'J9', glow:'J9', tanned:'J9', darktan:'J9', black:'J9'},
  eileen:{natural:'J9', glow:'J9', tanned:'J9', darktan:'J9', black:'J9'},
  ivy:   {natural:'J9', glow:'J9', tanned:'J9', darktan:'J9', black:'J9'},
};
/* The wig every card is supposed to wear. It is a CHART code, so it is the same
   physical wig on every doll — which is the whole point of the card set. */
const CARD_HAIR_CODE='J9';
/* CARD_HAIR above only ever listed a few face+tone pairs. Every pair it did NOT
   list fell through to `hairs[0]`, which is whatever render happens to sort
   first — J1 for Suki and Kitty, a short bob. So on the default Tanned tone the
   face row showed four women in the same long J9 wig and two in a bob, and the
   customer was asked to compare faces while two of the six had different hair.
   Seven pairs were wrong: suki natural/tanned/darktan/black, kitty
   natural/tanned/black.

   The index is now DERIVED from the codes table — `codes[i]` is the chart code
   for `hairs[i]`, so the J9 render can be looked up per face and tone instead of
   hand-listed, and a new tone cannot silently reintroduce this. CARD_HAIR stays
   as an explicit override for a deliberate exception (a face with no J9 render
   in some tone); every entry currently in it already agrees with the derived
   value, so nothing that works today changes. */
function cardHairFor(faceKey,skinKey){
  const f=FACE_ASSETS[faceKey]; const s=f&&f.skins[skinKey];
  if(!s||!s.hairs||!s.hairs.length) return null;
  const override=(CARD_HAIR[faceKey]||{})[skinKey];
  if(override) return override;
  const i=(s.codes||[]).indexOf(CARD_HAIR_CODE);
  if(i>=0&&s.hairs[i]) return s.hairs[i];
  return s.hairs[0]||null;   // no J9 render for this tone: first available
}
function faceThumb(faceKey,skinKey){
  const f=FACE_ASSETS[faceKey]; if(!f) return '';
  let s=f.skins[skinKey];
  if(!s || !s.hairs.length){ const fs=firstSkinFor(faceKey); if(!fs) return ''; skinKey=fs; }
  const h=cardHairFor(faceKey,skinKey);
  return h?faceImg(faceKey,skinKey,1,h):'';
}
// Close-up eyeball photos (定制1.jpg–定制29.jpg) used for the eye-option buttons.
const EYES_BASE=SBD_CDN+'/eyes';
function eyeImg(n){ return `${EYES_BASE}/${String(n).padStart(2,'0')}.webp`; }
// Curated starting looks. Canonical wig codes resolve through each head/tone map.
const HEAD_LOOKS=Object.freeze({
  suki:  {skin:'glow',hair:'J4',eyes:10,makeup:'natural'},
  luna:  {skin:'tanned',hair:'W11',eyes:12,makeup:'softglam'},
  penny: {skin:'darktan',hair:'W1',eyes:3,makeup:'softglam'},
  kitty: {skin:'glow',hair:'W5',eyes:7,makeup:'sweetrose'},
  eileen:{skin:'tanned',hair:'J6',eyes:9,makeup:'smokeyeye'},
  ivy:   {skin:'natural',hair:'W4',eyes:12,makeup:'natural'},
});
function headLookImage(faceKey){
  const look=HEAD_LOOKS[faceKey];
  const hair=hairListFor(faceKey,look.skin).find(option=>option.code===look.hair);
  return hair?faceImg(faceKey,look.skin,look.eyes,hair.hair):faceThumb(faceKey,look.skin);
}
const FACES=[
  {key:'suki',uid:HEAD_UID_BY_KEY.suki,label:'Suki',tag:'Soft, Japanese-inspired',desc:'Quiet warmth and gentle curiosity.',sw:'sw-face1',img:faceThumb('suki','glow'),alt:'Suki — glow skin'},
  {key:'luna',uid:HEAD_UID_BY_KEY.luna,label:'Luna',tag:'Girl-next-door',desc:'Easy warmth, an unhurried presence.',sw:'sw-face2',img:faceThumb('luna','glow'),alt:'Luna — glow skin'},
  {key:'penny',uid:HEAD_UID_BY_KEY.penny,label:'Penny',tag:'Bold',desc:'Confident, magnetic, a little daring.',sw:'sw-face3',img:faceThumb('penny','glow'),alt:'Penny — glow skin'},
  {key:'kitty',uid:HEAD_UID_BY_KEY.kitty,label:'Kitty',tag:'Playful',desc:'Bright, quick-witted, all mischief.',sw:'sw-face4',img:faceThumb('kitty','glow'),alt:'Kitty — glow skin'},
  {key:'eileen',uid:HEAD_UID_BY_KEY.eileen,label:'Eileen',tag:'Glamorous',desc:'Poised and magnetic, with a knowing calm.',sw:'sw-face3',img:faceThumb('eileen','glow'),alt:'Eileen — glow skin'},
  {key:'ivy',uid:HEAD_UID_BY_KEY.ivy,label:'Ivy',tag:'Serene',desc:'Soft-spoken warmth and a steady gaze.',sw:'sw-face1',img:faceThumb('ivy','glow'),alt:'Ivy — glow skin'},
];
// Luna wearing J9 in every tone, so only her SKIN changes down the row — the
// Dark Tanned card joined the set 2026-08-31 when Luna's DT renders landed.
const SKIN_TONES=[
  {key:'natural',uid:SKIN_UID_BY_KEY.natural,label:'Natural',desc:'Fair, soft pink undertone',tint:'#f3c4bc',sil:['#ffdbd2','#f3c4bc','#856b67'],img:faceThumb('luna','natural'),alt:'Natural fair skin tone — Luna'},
  {key:'glow',uid:SKIN_UID_BY_KEY.glow,label:'Glow',desc:'Light, warm golden',tint:'#d3aa88',sil:['#ecbe98','#d3aa88','#745d4a'],img:faceThumb('luna','glow'),alt:'Glow warm light skin tone — Luna'},
  {key:'tanned',uid:SKIN_UID_BY_KEY.tanned,label:'Tanned',desc:'Sun-kissed bronze',tint:'#d69e78',sil:['#efb086','#d69e78','#755642'],img:faceThumb('luna','tanned'),alt:'Tanned bronze skin tone — Luna'},
  {key:'darktan',uid:SKIN_UID_BY_KEY.darktan,label:'Dark Tanned',desc:'Deep, warm bronze',tint:'#ba7354',sil:['#d0805e','#ba7354','#663f2e'],img:faceThumb('luna','darktan'),alt:'Dark tanned deep bronze skin tone — Luna'},
  {key:'black',uid:SKIN_UID_BY_KEY.black,label:'Black',desc:'Rich, deep ebony',tint:'#55352d',sil:['#5f3b32','#55352d','#2e1d18'],img:faceThumb('luna','black'),alt:'Black deep ebony skin tone — Luna'},
];
// Skin tones that actually have renders for a face. A tone with an empty hair
// list (Luna/Dark Tanned, until its set landed 2026-08-31) used to render as a
// selectable swatch whose hair step then said "coming soon" — a dead end four
// steps after the choice. Hide the tone for that face instead until its
// renders exist. All six faces currently pass for all five tones, so this is
// a guard for the next regression, not an active filter.
function skinTonesFor(faceKey){
  const f=FACE_ASSETS[faceKey];
  return SKIN_TONES.filter(tone=>{ const s=f&&f.skins[tone.key]; return Boolean(s&&s.hairs&&s.hairs.length); });
}
// 29 eye colours (定制1–定制29). Button thumbnail = the close-up eyeball photo;
// the live preview swaps the full composited portrait for the selected skin/hair.
const EYE_COLORS=Array.from({length:29},(_,i)=>{
  const n=i+1;
  // Button thumbnail = the close-up eyeball photo; the live preview still swaps the
  // full composited portrait via eyeNum + selected hair.
  return {key:EYE_UIDS[i],uid:EYE_UIDS[i],label:'Eye '+n,eyeNum:n,img:eyeImg(n),alt:'Eye colour '+n};
});
// TODO: confirm pricing — treated as included/no-cost for this concept, same as
// hair/eyes; product may want to charge for some finishes (e.g. Black Bow, Holographic).
const FINGERNAIL_STYLES=[
  {key:'metallic-bronze',label:'Metallic Bronze',desc:'Mirror-chrome bronze, almond tip',price:0,img:'https://cdn.shopify.com/s/files/1/1011/2752/0539/files/nail-1.jpg',alt:'Metallic bronze chrome fingernail, almond shape'},
  {key:'nude-french',label:'Nude French',desc:'Soft nude fade with a white French tip',price:0,img:'https://cdn.shopify.com/s/files/1/1011/2752/0539/files/nail-2.jpg',alt:'Nude fingernail with white French tip'},
  {key:'metallic-gold',label:'Metallic Gold',desc:'Mirror-chrome gold, almond tip',price:0,img:'https://cdn.shopify.com/s/files/1/1011/2752/0539/files/nail-3.jpg',alt:'Metallic gold chrome fingernail, almond shape'},
  {key:'rose-ombre',label:'Rose Ombré',desc:'Magenta-to-pink gradient, glossy round tip',price:0,img:'https://cdn.shopify.com/s/files/1/1011/2752/0539/files/nail-4.jpg',alt:'Rose pink ombre fingernail with glossy finish'},
  {key:'iridescent-peach',label:'Iridescent Peach',desc:'Pearlescent peach with a holographic stripe',price:0,img:'https://cdn.shopify.com/s/files/1/1011/2752/0539/files/nail-5.jpg',alt:'Iridescent peach fingernail with pearlescent shimmer stripe'},
  {key:'holographic',label:'Holographic',desc:'Iridescent rainbow shimmer, catches the light',price:0,img:'https://cdn.shopify.com/s/files/1/1011/2752/0539/files/nail-6.jpg',alt:'Holographic iridescent fingernail, almond shape'},
  {key:'natural',label:'Natural',desc:'Soft, sheer nude — barely-there manicure',price:0,img:'https://cdn.shopify.com/s/files/1/1011/2752/0539/files/nail-7.jpg',alt:'Sheer nude fingernail with soft glossy finish'},
  {key:'dusty-rose-matte',label:'Dusty Rose Matte',desc:'Muted matte pink, square tip',price:0,img:'https://cdn.shopify.com/s/files/1/1011/2752/0539/files/nail-8.jpg',alt:'Dusty rose matte fingernail, square shape'},
  {key:'black-bow-glitter',label:'Black Bow Glitter',desc:'Black glitter tip with a 3D bow accent',price:0,img:'https://cdn.shopify.com/s/files/1/1011/2752/0539/files/nail-9.jpg',alt:'Black glitter fingernail with a 3D black bow charm'},
  {key:'pink-ombre-french',label:'Pink Ombré French',desc:'Pink gradient with a silver glitter tip',price:0,img:'https://cdn.shopify.com/s/files/1/1011/2752/0539/files/nail-10.jpg',alt:'Pink ombre fingernail with silver glitter French tip'},
  {key:'lilac-glitter',label:'Lilac Glitter',desc:'Lavender with fine silver glitter, round tip',price:0,img:'https://cdn.shopify.com/s/files/1/1011/2752/0539/files/nail-11.jpg',alt:'Lilac fingernail with fine silver glitter, round shape'},
  {key:'black-bold-stripe',label:'Black Bold Stripe',desc:'Glossy black, statement striped accent',price:0,img:'https://cdn.shopify.com/s/files/1/1011/2752/0539/files/nail-12.jpg',alt:'Glossy black square fingernail with vertical stripe accents'},
];
// TODO: confirm pricing — treated as included/no-cost for this concept, same as
// fingernails. Thumbnails are cropped to the mound/hair-texture detail only (no
// anatomical content) — see CRITIQUE_LOG for the crop rationale.
const PUBIC_HAIR_STYLES=[
  // "None" is clean-shaven — no hair — so it deliberately has NO photo (the
  // grid renders a placeholder tile): a hair-texture image on this card
  // contradicted what the option means.
  // "None" is a real look, not an absent one — it showed a grey placeholder tile
  // beside eight photographed styles, which read as a missing image rather than
  // as clean shaven. pubic-none.jpg is the shaven crop from the same shoot as
  // the other eight, so the row now compares like with like.
  {key:'natural',label:'None',desc:'Clean shaven — as she comes by default',price:0,sw:'sw-none',img:'https://cdn.shopify.com/s/files/1/1011/2752/0539/files/pubic-none.jpg',alt:'Clean shaven, no pubic hair'},
  {key:'bikini-wax',label:'Bikini Wax',desc:'Clean, smooth along the sides',billable:true,img:'https://cdn.shopify.com/s/files/1/1011/2752/0539/files/pubic-bikini-wax.jpg',alt:'Bikini wax pubic hair style, mostly smooth'},
  {key:'freestyle',label:'Freestyle',desc:'Light, natural trim',billable:true,img:'https://cdn.shopify.com/s/files/1/1011/2752/0539/files/pubic-freestyle.jpg',alt:'Freestyle pubic hair style, light trim'},
  {key:'heart',label:'Heart',desc:'Playful, shaped trim',billable:true,img:'https://cdn.shopify.com/s/files/1/1011/2752/0539/files/pubic-heart.jpg',alt:'Heart-shaped pubic hair style'},
  {key:'striping',label:'Striping',desc:'Narrow, vertical trim',billable:true,img:'https://cdn.shopify.com/s/files/1/1011/2752/0539/files/pubic-striping.jpg',alt:'Narrow vertical strip pubic hair style'},
  {key:'martini-glass',label:'Martini Glass',desc:'Slim, tapered shape',billable:true,img:'https://cdn.shopify.com/s/files/1/1011/2752/0539/files/pubic-martini-glass.jpg',alt:'Martini glass shaped pubic hair style'},
  {key:'original',label:'Original',desc:'Full, untrimmed',billable:true,img:'https://cdn.shopify.com/s/files/1/1011/2752/0539/files/pubic-original.jpg',alt:'Original full pubic hair style, untrimmed'},
  {key:'wild',label:'Wild',desc:'Full, natural volume',billable:true,img:'https://cdn.shopify.com/s/files/1/1011/2752/0539/files/pubic-wild.jpg',alt:'Wild pubic hair style, full natural volume'},
];
// "Tan Line" pricing confirmed by the 5.1 locked price sheet (approved 2026-08-14):
// $60 (was treated as included; Irontech floor $55.71). Tan lines are a Yes/No
// choice, not a set of selectable images — the two real reference photos are shown
// as non-selectable illustrations of what "Yes" looks like, beside the choice (see
// renderFinish), rather than as radio options themselves.
const TAN_LINE_CHOICES=[
  {key:'no',label:'No',desc:'Even tone, no tan lines',price:0,noSwatch:true},
  {key:'yes',label:'Yes',desc:'Add tan-line contrast',billable:true,noSwatch:true,extra:'<div class="tan-in"><figure><img src="https://cdn.shopify.com/s/files/1/1011/2752/0539/files/tan-soft.jpg?width=64" srcset="https://cdn.shopify.com/s/files/1/1011/2752/0539/files/tan-soft.jpg?width=64 1x, https://cdn.shopify.com/s/files/1/1011/2752/0539/files/tan-soft.jpg?width=128 2x" width="64" height="64" alt="Soft, faint bikini tan line" loading="lazy" decoding="async"><figcaption>Soft</figcaption></figure><figure><img src="https://cdn.shopify.com/s/files/1/1011/2752/0539/files/tan-bold.jpg?width=64" srcset="https://cdn.shopify.com/s/files/1/1011/2752/0539/files/tan-bold.jpg?width=64 1x, https://cdn.shopify.com/s/files/1/1011/2752/0539/files/tan-bold.jpg?width=128 2x" width="64" height="64" alt="Bold, well-defined bikini tan line" loading="lazy" decoding="async"><figcaption>Bold</figcaption></figure></div>'},
];
const WARMTH_ADDONS=[
  {key:'body',label:'Body heating',desc:'Built-in heating warms her body.',billable:true,img:'https://cdn.shopify.com/s/files/1/1011/2752/0539/files/heat3-body.png',contain:true},
  {key:'oral',label:'Mouth heating',desc:'Adds warmth inside her mouth.',billable:true,img:'https://cdn.shopify.com/s/files/1/1011/2752/0539/files/heat3-oral.png',contain:true,video:'https://cdn.shopify.com/videos/c/o/v/3dbb5cf6ad6c4c35834a0bae26616536.mp4'},
];
const SENSATION_ADDONS=[
  // "tongue movement" was wrong — there is no powered tongue in this option, and
  // the claim shipped to a live product page. Restored to the wording every
  // earlier build used (design_quiz_fullwidth.html, 2D Customizer/index.html),
  // which describes the option without inventing a mechanism it does not have.
  // Oral: the mouth-interior suction clip (feat-oral-suction.mp4), not the older
  // tongue-motion cut. Served as a THEME asset, so it ships with `theme push`
  // rather than needing a Shopify Files upload — same pattern as craft-pose.mp4.
  {key:'oralmotion',label:'Oral suction & stroking',desc:'Rhythmic suction and throat contractions create a stroking sensation.',billable:true,video:new URL('feat-oral-suction.mp4',CUSTOMIZER_ASSET_BASE).href},
  // Installation is a fulfilment decision. Keep the canonical Shopify key while
  // presenting one suction option to the customer.
  {key:'clampinternal',label:'Vaginal suction & stroking',desc:'Rhythmic suction, contractions and stroking, with vibration and heating included.',billable:true,video:new URL('suckingvagina-loop.mp4',CUSTOMIZER_ASSET_BASE).href},
];
/* ============================================================
   INTIMATE SYSTEM — a single-select, deliberately NOT an add-on Set
   ============================================================ */
/* Irontech, 2026-08-18: "Once Bionic VaginaX is installed, the doll's anal
   function will no longer be available." That makes this a build decision the
   factory takes at moulding, not a bolt-on: VaginaX occupies the space the anal
   canal would have used, so the two cannot coexist on one body and VaginaX
   cannot be retrofitted later.

   It is also the bionic replacement for the negative-pressure suction system,
   which is why picking it REMOVES both Clamp & Suck options rather than merely
   unchecking them — a greyed-out tile still reads as "available on request",
   and the combination is not buildable at any price.

   MAP floor $191 (RMB 1,300). Sold at $260, the same ~1.36x the rest of the
   add-on ladder runs at. See truth/prices.yaml addons_chargeable. */
// Withdrawn from the current offering. Retain the option definition and order
// mappings, but exclude it from new and restored builds while hidden.
const VAGINAX_ENABLED=false;
const INTIMATE_SYSTEM=[
  {key:'standard',label:'Standard',desc:'Sculpted vaginal and anal canals',price:0,img:'https://cdn.shopify.com/s/files/1/1011/2752/0539/files/intimate-standard.png',contain:true},
  {key:'vaginax',label:'Bionic VaginaX',desc:'Responsive tightening, vibration and warmth, with phone controls. Easy to clean. Supplied without an anal canal.',billable:true,img:'https://cdn.shopify.com/s/files/1/1011/2752/0539/files/intimate-vaginax.png',contain:true},
].filter(option=>VAGINAX_ENABLED||option.key!=='vaginax');
/* Everything VaginaX supersedes, by the group it lives in. Each excluded option
   is removed from its grid AND cleared from its Set the moment VaginaX is
   picked, so the running total can never quote a body the factory would refuse
   to build. Greying a tile out instead would read as "available on request".

   sensation — the two Clamp & Suck systems: VaginaX is the bionic replacement
     for the negative-pressure suction hardware, so they cannot coexist.
   warmth — intimate warmth: the heating element occupies the same cavity the
     VaginaX mechanism is moulded into.
   (Owner decision 2026-09-06; the sensation pair was already established by
   Irontech's 2026-08-18 note on the anal canal.) */
const VAGINAX_EXCLUDES={
  sensation:['clampinternal','clampexternal'],
  warmth:['intimate'],
};
// Drops the excluded keys from a group's option list while VaginaX is selected.
function withoutVaginaxExclusions(group,options){
  const excluded=VAGINAX_EXCLUDES[group]||[];
  return state.intimateSystem.key==='vaginax'
    ? options.filter(o=>!excluded.includes(o.key))
    : options;
}
function sensationOptions(){ return withoutVaginaxExclusions('sensation',SENSATION_ADDONS); }
function warmthOptions(){ return withoutVaginaxExclusions('warmth',WARMTH_ADDONS); }
function audioOptions(){ return withoutVaginaxExclusions('audio',AUDIO_ADDONS); }
// Clears every excluded option out of state. Called when VaginaX is picked so a
// choice made before it can never survive into the price or the cart payload.
function applyVaginaxExclusions(){
  Object.keys(VAGINAX_EXCLUDES).forEach(group=>{
    const set=state[group];
    if(set&&set.delete) VAGINAX_EXCLUDES[group].forEach(key=>set.delete(key));
  });
}
// These groups have been withdrawn from the live offering. Keep the group
// contracts for saved builds and pricing, without reintroducing their options.
const AUDIO_ADDONS=[];
const SOFTCORE_ADDONS=[];
const MOVEMENT_ADDONS=[
  {key:'headmove',label:'Head movement',desc:'Powered back-and-forth head movement.',billable:true,video:new URL('feat-head-movement.mp4',CUSTOMIZER_ASSET_BASE).href},
  {key:'hip',label:'Hip movement',desc:'Powered movement of her hips and waist.',billable:true,video:new URL('hip-movement.mp4',CUSTOMIZER_ASSET_BASE).href},
];
const ULTRA_LIGHT_CHOICES=[
  {key:'standard',label:'Standard',desc:'Her normal build weight',price:0,noSwatch:true},
  {key:'ultralight',label:'Ultra-Lightweight',desc:'About 20% lighter, with the same look and feel · adds 2–3 weeks to production',billable:true,noSwatch:true},
];

/* ============================================================
   STATE — everything in memory, nothing persisted
   ============================================================ */
// A quiet loading ring over the live preview, for any image that takes longer than
// PREVIEW_RING_DELAY_MS. Two independent things can hold it up — the portrait still
// downloading, and a makeup preview still pending — so each holds its own reason and
// the ring stays while either does. Declared above `state` so no early render can
// reach these bindings before they exist.
const PREVIEW_RING_DELAY_MS=1000;
const previewRingReasons=new Set();
let previewPhotoRingTimer=null, previewPhotoRingWired=false;
function previewRingEl(){
  let ring=document.getElementById('previewLoadingRing');
  if(ring) return ring;
  const anchor=document.getElementById('previewGenerating');
  if(!anchor) return null;
  ring=document.createElement('div');
  ring.id='previewLoadingRing';
  ring.className='preview-loading-ring';
  ring.innerHTML='<span class="plr-spin" aria-hidden="true"></span><span class="plr-text" role="status" aria-live="polite"></span>';
  // A sibling AFTER #previewGenerating, so CSS hides the ring while that overlay is up.
  anchor.insertAdjacentElement('afterend',ring);
  return ring;
}
// Only the background makeup follow says what it is doing. Over freshly changed eyes
// a bare ring reads as "the eyes are loading", when it is her makeup being added.
function paintPreviewRing(){
  const ring=previewRingReasons.size?previewRingEl():document.getElementById('previewLoadingRing');
  if(!ring) return;
  ring.classList.toggle('is-visible',previewRingReasons.size>0);
  const label=ring.querySelector('.plr-text');
  const text=previewRingReasons.has('follow')?'Adding makeup…':'';
  if(label&&label.textContent!==text) label.textContent=text;
}
function showPreviewRing(reason){
  previewRingReasons.add(reason);
  paintPreviewRing();
}
function hidePreviewRing(reason){
  previewRingReasons.delete(reason);
  paintPreviewRing();
}
function watchPreviewPhotoLoad(photo){
  if(!previewPhotoRingWired){
    // #previewPhoto outlives every render, so these are attached exactly once.
    const settled=()=>{ clearTimeout(previewPhotoRingTimer); hidePreviewRing('photo'); };
    photo.addEventListener('load',settled);
    photo.addEventListener('error',settled);
    previewPhotoRingWired=true;
  }
  clearTimeout(previewPhotoRingTimer);
  if(photo.complete){ hidePreviewRing('photo'); return; }
  previewPhotoRingTimer=setTimeout(()=>{
    // Only over a portrait that is actually painted — the body steps hide it.
    if(!photo.complete&&photo.getClientRects().length) showPreviewRing('photo');
  },PREVIEW_RING_DELAY_MS);
}
// Makeup-follow bookkeeping (see scheduleMakeupFollow). Up here for the same reason
// as the ring: updatePreview reaches it, and it must exist before any render can.
const MAKEUP_FOLLOW_DELAY_MS=1500;
const makeupInflight=Object.create(null);
let makeupFollowTimer=null, makeupFollowQueued='', makeupFollowQueuedAt=0, makeupFollowRunning='';
const state={
  name:'Luna',
  body:BODY_OPTIONS[1],
  bodyModel:null,
  breastSize:BREAST_SIZE[1],
  breastFeel:BREAST_FEEL[0],
  areola:AREOLA_SIZES[1],
  breastPiercing:BREAST_PIERCING[0],
  buttShape:BUTT_SHAPE[1],
  buttFeel:BUTT_FEEL[0],
  face:FACES[1],
  skin:SKIN_TONES.find(o=>o.key===HEAD_LOOKS.luna.skin),
  hairStyle:hairListFor('luna',HEAD_LOOKS.luna.skin).find(o=>o.code===HEAD_LOOKS.luna.hair),
  eyes:EYE_COLORS.find(o=>o.eyeNum===HEAD_LOOKS.luna.eyes),
  makeup:MAKEUP_STYLES.find(o=>o.key===HEAD_LOOKS.luna.makeup),
  lookOverrides:{},
  makeupPreviewUrl:null,
  makeupPreviews:Object.create(null),
  makeupRevs:Object.create(null),
  makeupCustomDataUri:null,
  makeupCustomFilename:'',
  makeupConsent:false,
  fingernails:FINGERNAIL_STYLES.find(o=>o.key==='natural'),
  pubicHair:PUBIC_HAIR_STYLES[0],
  navel:NAVEL_PIERCING[0],
  tanLine:TAN_LINE_CHOICES[0],
  intimateSystem:INTIMATE_SYSTEM[0],
  warmth:new Set(),
  sensation:new Set(),
  audio:new Set(),
  softcore:new Set(),
  movement:new Set(),
  ultraLight:ULTRA_LIGHT_CHOICES[0],
};

function metafieldText(value){
  return value===null||value===undefined?'':String(value).trim();
}
function uidEquals(left,right){
  return metafieldText(left).toLowerCase()===metafieldText(right).toLowerCase();
}
function readProductDefaults(){
  const root=document.getElementById('atelier-landing');
  const node=root&&root.querySelector('[data-sbd-product-defaults]');
  if(!node) return null;
  try{ return JSON.parse(node.textContent||'{}'); }
  catch(error){ console.error('Could not parse product customizer metafields.',error); return null; }
}
function variantIsUltralight(variant){
  const values=Array.isArray(variant&&variant.options)
    ?variant.options
    :[variant&&variant.option1,variant&&variant.option2,variant&&variant.option3];
  return values.some(value=>/^ultra[\s-]*light(?:weight)?$/i.test(metafieldText(value)));
}
function selectedProductVariantId(defaults){
  const inputs=[...document.querySelectorAll('form[action*="/cart/add"] [name="id"]')];
  const live=inputs.find(input=>metafieldText(input.value))||null;
  const queryId=new URLSearchParams(window.location.search).get('variant');
  return Number((live&&live.value)||queryId||(defaults&&defaults.selectedVariantId))||null;
}
function eyeNumberFromMetadata(raw){
  const value=metafieldText(raw);
  const match=value.match(/^(?:eyes_irontech_|eye-)?(\d{1,2})(?:-|$)/i);
  const number=match?Number(match[1]):NaN;
  return number>=1&&number<=EYE_COLORS.length?number:null;
}
function hairCodeFromMetadata(raw){
  const value=metafieldText(raw);
  if(HAIR_UID_BY_CODE[value.toUpperCase()]) return value.toUpperCase();
  const match=value.match(/^hair_irontech_([jw]\d{1,2})(?:-|$)/i);
  return match&&HAIR_UID_BY_CODE[match[1].toUpperCase()]?match[1].toUpperCase():null;
}
function applyProductDefaults(defaults){
  const values=defaults||{};
  const report={
    productHandle:metafieldText(values.productHandle),
    raw:{body:values.body,head:values.head,skin:values.skin,hair:values.hair,eyes:values.eyes},
    applied:{},
    warnings:[],
  };
  const warn=(field,value,detail)=>{
    if(metafieldText(value)) report.warnings.push(`${field}: ${JSON.stringify(value)}${detail?' — '+detail:''}`);
  };

  const bodyRaw=metafieldText(values.body);
  if(bodyRaw){
    const body=BODY_CATALOG.find(model=>[model.uid,model.id,model.legacyId].some(id=>uidEquals(id,bodyRaw)));
    if(body){ applyBodyModel(body); report.applied.body=body.uid||body.id; }
    else warn('UID_body',values.body,'no body catalog match');
  }

  const headRaw=metafieldText(values.head);
  if(headRaw){
    const face=FACES.find(option=>[option.uid,option.key,option.label].some(id=>uidEquals(id,headRaw)));
    if(face){ state.face=face; state.name=face.label; report.applied.head=face.uid; }
    else warn('UID_head',values.head,'no head catalog match');
  }

  const skinRaw=metafieldText(values.skin);
  if(skinRaw){
    const skin=SKIN_TONES.find(option=>[option.uid,option.key,option.label].some(id=>uidEquals(id,skinRaw)));
    if(skin){ state.skin=skin; report.applied.skin=skin.uid; }
    else warn('UID_skin',values.skin,'no skin catalog match');
  }
  if(report.applied.body&&report.applied.skin){
    const picturedBody=BODY_CATALOG.find(model=>uidEquals(model.uid,report.applied.body));
    if(!bodyHasTone(picturedBody,state.skin.key)) warn('UID_body_skin',`${bodyRaw} / ${skinRaw}`,'selected body and skin have no approved render');
  }

  const availableHair=hairListFor(state.face.key,state.skin.key);
  const hairRaw=metafieldText(values.hair);
  if(hairRaw){
    const hairCode=hairCodeFromMetadata(hairRaw);
    const hair=availableHair.find(option=>uidEquals(option.uid,hairRaw))
      ||availableHair.find(option=>option.code===hairCode);
    if(hair){ state.hairStyle=hair; report.applied.hair=hair.uid; }
    else {
      state.hairStyle=retainHairSelection(availableHair,state.hairStyle);
      warn('UID_hair',values.hair,hairCode?'not rendered for the selected head and skin':'unknown hair UID or chart code');
    }
  } else {
    state.hairStyle=retainHairSelection(availableHair,state.hairStyle);
  }

  const eyesRaw=metafieldText(values.eyes);
  if(eyesRaw){
    const eyeNumber=eyeNumberFromMetadata(eyesRaw);
    const eyes=EYE_COLORS.find(option=>uidEquals(option.uid,eyesRaw))
      ||EYE_COLORS.find(option=>option.eyeNum===eyeNumber);
    if(eyes){ state.eyes=eyes; report.applied.eyes=eyes.uid; }
    else warn('UID_eyes',values.eyes,'unknown eye UID or chart number');
  }

  window.SBD_CUSTOMIZER_PREFILL=report;
  const productStudio=document.querySelector('#atelier-landing.sbd-customizer--collapsed');
  if(productStudio&&(report.warnings.length||['body','head','skin','hair','eyes'].some(field=>!report.applied[field]))){
    document.querySelectorAll('[data-sbd-customize-her], [data-scroll-customize], #sbd-desc-open-ue').forEach(button=>{ button.remove(); });
  }
  if(report.warnings.length) console.warn('Some product customizer metafields could not be applied.',report);
  return report;
}

const PRODUCT_DEFAULTS=readProductDefaults();
const PRODUCT_VARIANTS=Array.isArray(PRODUCT_DEFAULTS&&PRODUCT_DEFAULTS.variants)?PRODUCT_DEFAULTS.variants:[];
const ULTRALIGHT_ELIGIBLE=PRODUCT_VARIANTS.some(variantIsUltralight);
function syncUltralightFromProduct(){
  if(!ULTRALIGHT_ELIGIBLE){ state.ultraLight=ULTRA_LIGHT_CHOICES[0]; return; }
  const selectedId=selectedProductVariantId(PRODUCT_DEFAULTS);
  const selected=PRODUCT_VARIANTS.find(variant=>Number(variant.id)===selectedId);
  state.ultraLight=ULTRA_LIGHT_CHOICES[variantIsUltralight(selected)?1:0];
}

applyBodyModel(BODY_CATALOG.find(model=>model.uid==='body_irontech_11_PR160'||model.legacyId==='M-S-PR160'||model.id==='M-S-PR160')||BODY_CATALOG[0]);
const headProductDefaults=applyProductDefaults(PRODUCT_DEFAULTS);
if(headProductDefaults&&headProductDefaults.applied){
  if(headProductDefaults.applied.body) state.lookOverrides.skin=true;
  for(const [field,source] of [['skin','skin'],['hairStyle','hair'],['eyes','eyes']]){
    if(headProductDefaults.applied[source]) state.lookOverrides[field]=true;
  }
}
applyHeadLook(state.face.key,false);
syncUltralightFromProduct();
window.SBD_SYNC_ULTRALIGHT=()=>{
  const previous=state.ultraLight&&state.ultraLight.key;
  syncUltralightFromProduct();
  if(previous!==state.ultraLight.key) renderAll();
};

/* ============================================================
   PERSISTENCE — a refresh or an accidental back-swipe used to
   destroy every step of a build. Selections are saved (as keys,
   never as derived data) and re-validated against the catalogs
   on the way back in, so a stale save can only ever restore
   choices that are still real.
   ============================================================ */
const BUILD_PRODUCT_HANDLE=metafieldText(PRODUCT_DEFAULTS&&PRODUCT_DEFAULTS.productHandle);
const BUILD_STORAGE_KEY='sbd-customizer-build-v1'+(BUILD_PRODUCT_HANDLE&&BUILD_PRODUCT_HANDLE!=='your-perfect-companion'?':'+BUILD_PRODUCT_HANDLE:'');
// ENTRY ATTRIBUTION: first observed origin belongs to the design; latest entry
// describes this opening. Never persist query strings (including design tokens).
let sbdBuildAttribution=null;
let sbdEntryOpened=false;
let sbdEntryResume='new';
function sbdEntryPath(value){
  return typeof value==='string'&&value.length<=240&&/^\/(?:[A-Za-z0-9_-]+\/)*[A-Za-z0-9_-]*$/.test(value)
    &&(value==='/'||/\/(products|pages|collections|blogs)\//.test(value))?value:'';
}
function sbdCleanEntry(raw){
  const unknown={source:'unknown',path:'',productHandle:'',referrerPath:'',openedAt:''};
  if(!raw||typeof raw!=='object') return unknown;
  const source=['premade_product','direct_customizer','other','unknown'].includes(raw.source)?raw.source:'unknown';
  const path=sbdEntryPath(raw.path);
  const productHandle=source==='premade_product'&&typeof raw.productHandle==='string'&&/^[a-z0-9-]{1,160}$/.test(raw.productHandle)?raw.productHandle:'';
  const openedAt=typeof raw.openedAt==='string'&&/^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d\.\d{3}Z$/.test(raw.openedAt)&&Number.isFinite(Date.parse(raw.openedAt))?raw.openedAt:'';
  return {source,path,productHandle,referrerPath:sbdEntryPath(raw.referrerPath),openedAt};
}
function sbdCurrentEntry(){
  const path=sbdEntryPath(window.location.pathname);
  const handle=BUILD_PRODUCT_HANDLE;
  const onProduct=/\/products\/[^/]+\/?$/.test(path);
  const premade=onProduct&&handle&&handle!=='your-perfect-companion';
  let referrerPath='';
  try{
    const ref=new URL(document.referrer);
    if(['siliconeangels.com','www.siliconeangels.com','small-and-big-dolls.myshopify.com'].includes(ref.hostname)) referrerPath=sbdEntryPath(ref.pathname);
  }catch(error){}
  return sbdCleanEntry({source:premade?'premade_product':(path==='/'||/\/pages\/design-your-companion\/?$/.test(path)||/\/products\/your-perfect-companion\/?$/.test(path))?'direct_customizer':'other',path,productHandle:premade?handle:'',referrerPath,openedAt:new Date().toISOString()});
}
function sbdRestoreAttribution(saved,resume){
  const raw=saved&&saved.attribution;
  sbdEntryResume=resume;
  sbdBuildAttribution={v:1,origin:sbdCleanEntry(raw&&raw.v===1?raw.origin:null),latest:sbdEntryOpened?sbdCurrentEntry():null,resume};
}
function sbdCaptureEntry(){
  sbdEntryOpened=true;
  if(!sbdBuildAttribution){
    const entry=sbdCurrentEntry();
    sbdBuildAttribution={v:1,origin:entry,latest:{...entry},resume:sbdEntryResume};
  }else if(!sbdBuildAttribution.latest){
    sbdBuildAttribution.latest=sbdCurrentEntry();
  }
  return sbdBuildAttribution;
}
window.SBD_ENTRY_PROPERTIES=()=>{
  const a=sbdCaptureEntry();
  const props={'_SBD Entry Version':'1','_SBD Entry Resume':a.resume};
  for(const [prefix,entry] of [['Entry',a.origin],['Latest Entry',a.latest]]){
    for(const [label,key] of [['Source','source'],['Page','path'],['Product','productHandle'],['Referrer','referrerPath'],['At','openedAt']]){
      if(entry[key]) props['_SBD '+prefix+' '+label]=entry[key];
    }
  }
  return props;
};
// END ENTRY ATTRIBUTION

const savedDesignToken=new URLSearchParams(window.location.search).get('design');
let savedDesignPending=savedDesignToken!==null;
let savedDesignChanges=[];
const SAVED_BUILD_FIELDS=['body','breastSize','breastFeel','areola','breastPiercing','buttShape','buttFeel','face','skin','hair','eyes','makeup','fingernails','pubicHair','navel','tanLine','intimateSystem'];
const SAVED_BUILD_SETS=['warmth','sensation','audio','softcore','movement'];
function validSavedBuild(saved){
  return !!saved&&saved.v===1&&!Array.isArray(saved)
    &&typeof saved.body==='string'&&typeof saved.face==='string'
    &&SAVED_BUILD_FIELDS.every(field=>saved[field]==null||typeof saved[field]==='string')
    &&SAVED_BUILD_SETS.every(field=>saved[field]==null||(Array.isArray(saved[field])&&saved[field].every(key=>typeof key==='string')));
}
function changedSavedChoices(saved,current){
  const changes=SAVED_BUILD_FIELDS.filter(field=>{
    if(saved[field]==null) return false;
    if(field==='body'){
      const body=BODY_CATALOG.find(model=>[model.uid,model.id,model.legacyId].some(id=>uidEquals(id,saved.body)));
      return !body||!uidEquals(body.uid,current.body);
    }
    return saved[field]!==current[field];
  });
  SAVED_BUILD_SETS.forEach(field=>{
    if(Array.isArray(saved[field])&&JSON.stringify([...new Set(saved[field])].sort())!==JSON.stringify([...new Set(current[field])].sort())) changes.push(field);
  });
  return changes;
}

function serializeBuild(){
  return {
    v:1,
    attribution:sbdBuildAttribution?JSON.parse(JSON.stringify(sbdBuildAttribution)):null,
    lookOverrides:{...state.lookOverrides},
    name:state.name,
    body:selectedBodyUid()||null,
    breastSize:state.breastSize.key, breastFeel:state.breastFeel.key,
    areola:state.areola.key, breastPiercing:state.breastPiercing.key,
    buttShape:state.buttShape.key, buttFeel:state.buttFeel.key,
    face:state.face.key, skin:state.skin.key,
    hair:(state.hairStyle&&state.hairStyle.code)||null,
    eyes:state.eyes.key,
    // A custom-photo makeup can't be restored (the reference photo is never
    // persisted), so it saves as the included default instead of restoring
    // into a half-configured custom state.
    makeup:state.makeup.key==='custom'?'natural':state.makeup.key,
    fingernails:state.fingernails.key, pubicHair:state.pubicHair.key,
    navel:state.navel.key, tanLine:state.tanLine.key,
    intimateSystem:state.intimateSystem.key,
    warmth:[...state.warmth], sensation:[...state.sensation],
    audio:[...state.audio], softcore:[...state.softcore], movement:[...state.movement],
    step:(typeof STEPS!=='undefined'&&STEPS[stepIndex]&&STEPS[stepIndex].id)||null,
  };
}
function saveBuild(){
  if(savedDesignPending||savedDesignChanges.length) return;
  try{ localStorage.setItem(BUILD_STORAGE_KEY,JSON.stringify(serializeBuild())); }catch(error){ /* private mode / storage off — the build just stays session-only */ }
}
let saveBuildTimer=null;
function scheduleSaveBuild(){
  clearTimeout(saveBuildTimer);
  saveBuildTimer=setTimeout(saveBuild,150);
}
// Shared by the localStorage restore below and the emailed ?design=<token> links —
// both replay a saved v1 build through the same catalog re-validation.
function applySavedBuild(saved,resume='local'){
  if(!validSavedBuild(saved)) return null;
  sbdRestoreAttribution(saved,resume);
  // Legacy saves used Tanned/J9/Eye 1/Natural as universal defaults. Keep any
  // departures from those defaults when a returning customer changes heads.
  // Restore the saved look itself unchanged below, including default values.
  const legacyOverrides={skin:saved.skin!=='tanned',hairStyle:saved.hair!=='J9',
    eyes:saved.eyes!==EYE_COLORS[0].key,makeup:saved.makeup!=='natural'};
  state.lookOverrides=Object.fromEntries(['skin','hairStyle','eyes','makeup'].map(key=>
    [key,saved.lookOverrides?Boolean(saved.lookOverrides[key]):legacyOverrides[key]]));
  const pick=(list,key)=>list.find(option=>option.key===key)||null;
  if(typeof saved.name==='string'&&saved.name.trim()) state.name=saved.name.slice(0,24);
  const body=BODY_CATALOG.find(model=>[model.uid,model.id,model.legacyId].some(id=>uidEquals(id,saved.body)));
  if(body) applyBodyModel(body);
  state.intimateSystem=INTIMATE_SYSTEM[0];
  [['breastSize',BREAST_SIZE],['breastFeel',BREAST_FEEL],['areola',AREOLA_SIZES],
   ['breastPiercing',BREAST_PIERCING],['buttShape',BUTT_SHAPE],['buttFeel',BUTT_FEEL],
   ['fingernails',FINGERNAIL_STYLES],['pubicHair',PUBIC_HAIR_STYLES],['navel',NAVEL_PIERCING],
   ['tanLine',TAN_LINE_CHOICES],['intimateSystem',INTIMATE_SYSTEM],['makeup',MAKEUP_STYLES],
  ].forEach(([field,list])=>{
    const option=pick(list,saved[field]);
    if(option) state[field]=option;
  });
  const face=pick(FACES,saved.face);
  if(face){ state.face=face; }
  // The skin must be one this face actually has renders for — the same rule the
  // Skin step now enforces — so a save from before a tone was hidden can't
  // resurrect a dead combination.
  const skin=pick(skinTonesFor(state.face.key),saved.skin);
  if(skin) state.skin=skin;
  else if(!skinTonesFor(state.face.key).some(tone=>tone.key===state.skin.key)){
    const fallback=SKIN_TONES.find(tone=>tone.key===firstSkinFor(state.face.key));
    if(fallback) state.skin=fallback;
  }
  // The skin has just been validated above; a saved body may have no render in
  // it (BODY_TONE_EXCLUSIONS), so re-check the body against the settled tone.
  if(!bodyHasTone(state.bodyModel,state.skin.key)){
    const swap=nearestBodyForTone(state.bodyModel,state.skin.key);
    if(swap) applyBodyModel(swap);
  }
  const hairList=hairListFor(state.face.key,state.skin.key);
  state.hairStyle=hairList.find(option=>option.code===saved.hair)||retainHairSelection(hairList,state.hairStyle);
  const eyes=pick(EYE_COLORS,saved.eyes);
  if(eyes) state.eyes=eyes;
  ADDON_GROUPS.forEach(([field,list])=>{
    // Older designs stored head movement under Sensation. Preserve the paid
    // selection while moving it into Automatic movement.
    const source=field==='movement'
      ? [...(Array.isArray(saved.movement)?saved.movement:[]),...(Array.isArray(saved.sensation)?saved.sensation.filter(key=>key==='headmove'):[])]
      : saved[field];
    if(!Array.isArray(source)) return;
    const valid=new Set(list().map(option=>option.key));
    const savedKeys=field==='sensation'
      ? source.map(key=>key==='clampexternal'?'clampinternal':key)
      : source;
    state[field]=new Set(savedKeys.filter(key=>valid.has(key)));
  });
  if(state.intimateSystem.key==='vaginax') applyVaginaxExclusions();
  savedDesignChanges=changedSavedChoices(saved,serializeBuild());
  return saved;
}
function restoreBuild(){
  let saved=null;
  try{ saved=JSON.parse(localStorage.getItem(BUILD_STORAGE_KEY)||'null'); }catch(error){ saved=null; }
  return applySavedBuild(saved);
}
// The actual restore call happens further down, after ADDON_GROUPS exists —
// see RESTORED_BUILD below the pricing tables.

const STEPS_GROUPED=[
  {id:'face',label:'Face',render:renderFace},
  {id:'skin',label:'Skin',render:renderSkin},
  {id:'body',label:'Body',render:renderBody},
  {id:'feel',label:'Feel',render:renderFeel},
  {id:'look',label:'Her Look',render:renderLook},
  {id:'makeup',label:'Makeup',render:renderMakeup},
  {id:'finish',label:'Finishing Touches',render:renderFinish},
  // Warmth, sensation, movement and eligible build weight stay grouped here.
  {id:'enhance',label:'Enhancements',render:renderEnhancements},
  {id:'name',label:'Her name',render:renderName},
  {id:'review',label:'Review',render:renderReview},
];
function subStepRender(base,keep,title,help,post){
  return function(panel){
    base(panel);
    panel.querySelectorAll('fieldset.group').forEach(fieldset=>{
      if(!fieldset.querySelector('[name="'+keep+'"]')) fieldset.remove();
    });
    const heading=panel.querySelector('.q-title'); if(heading) heading.textContent=title;
    const helper=panel.querySelector('.q-help');
    if(helper){ if(help) helper.textContent=help; else helper.remove(); }
    panel.querySelectorAll('.group-label').forEach(label=>label.classList.add('visually-hidden'));
    if(post) post(panel);
    enhAugmentPanel(panel,keep);
  };
}
const GRANULAR_BODY_CONFIRM_STEP={id:'bodyConfirm',label:'Body details',render:renderBodyConfirmation};
const GRANULAR_BUILD_WEIGHT_STEP={id:'ultraLight',label:'Build weight',render:renderBodyConfirmation};
const STEPS_GRANULAR=[
  {id:'face',label:'Face',render:renderFace},
  {id:'skin',label:'Skin',render:renderSkin},
  {id:'body',label:'Body',render:renderBody},
  ...(ULTRALIGHT_ELIGIBLE?[GRANULAR_BUILD_WEIGHT_STEP]:[GRANULAR_BODY_CONFIRM_STEP]),
  {id:'breastFeel',label:'Breast feel',render:subStepRender(renderFeel,'breastFeel','How her breasts feel','Silicone is included; gel-infused gives a softer touch.')},
  {id:'buttFeel',label:'Butt feel',render:subStepRender(renderFeel,'buttFeel','How her curves feel','Silicone is included; gel-infused adds softer, more natural movement.')},
  {id:'areola',label:'Nipple size',render:subStepRender(renderFeel,'areola','Choose her nipple size','')},
  {id:'breastPiercing',label:'Nipple piercing',render:subStepRender(renderFeel,'breastPiercing','Nipple piercing','')},
  {id:'hair',label:'Hair',render:subStepRender(renderLook,'hairStyle','Choose her hair','Swipe to compare every available style.')},
  {id:'eyes',label:'Eyes',render:subStepRender(renderLook,'eyes','Choose her eyes','',()=>{
    teardownLookZoom();
    document.querySelector('.preview-figure')?.classList.add('zoomed');
  })},
  {id:'makeup',label:'Makeup',render:renderMakeup},
  {id:'fingernails',label:'Fingernails',render:subStepRender(renderFinish,'fingernails','Choose her fingernails','Hand-finished manicure, included.')},
  {id:'pubicHair',label:'Pubic hair',render:subStepRender(renderFinish,'pubicHair','Pubic hair','Choose clean shaven or a hand-styled finish.')},
  {id:'tanLine',label:'Tan lines',render:subStepRender(renderFinish,'tanLine','Tan lines','')},
  {id:'navel',label:'Navel piercing',render:subStepRender(renderFinish,'navel','Navel piercing','')},
  {id:'intimateSystem',label:'Intimate system',render:subStepRender(renderEnhancements,'intimateSystem','Her intimate system',''),skip:()=>INTIMATE_SYSTEM.length<2},
  {id:'warmth',label:'Heating',render:subStepRender(renderEnhancements,'warmth','Heating','Add any upgrades you like.')},
  {id:'movement',label:'Automatic movement',render:subStepRender(renderEnhancements,'movement','Automatic movement','Add any upgrades you like.')},
  {id:'sensation',label:'Suction & stroking',render:subStepRender(renderEnhancements,'sensation','Suction & stroking','Add any upgrades you like.')},
  {id:'name',label:'Her name',render:renderName},
  {id:'review',label:'Review',render:renderReview},
];
const COMPOSITE_OF={bodyConfirm:'body',breastFeel:'feel',buttFeel:'feel',areola:'feel',breastPiercing:'feel',hair:'look',eyes:'look',fingernails:'finish',pubicHair:'finish',tanLine:'finish',navel:'finish',intimateSystem:'enhance',warmth:'enhance',sensation:'enhance',audio:'enhance',softcore:'enhance',movement:'enhance',ultraLight:'body'};
const FIRST_CHILD={feel:'breastFeel',look:'hair',finish:'fingernails',enhance:INTIMATE_SYSTEM.length>1?'intimateSystem':'warmth'};
const mobileFlowQuery=new URLSearchParams(window.location.search).get('sbd_mobile_flow');
const mobileFlowSetting=document.getElementById('atelier-landing')?.dataset.mobileFlow;
const MOBILE_FLOW=['grouped','granular'].includes(mobileFlowQuery)?mobileFlowQuery:(mobileFlowSetting==='granular'?'granular':'grouped');
const mobileMQ=window.matchMedia('(max-width:899px)');
let isGranularMobile=mobileMQ.matches&&MOBILE_FLOW==='granular';
let STEPS=isGranularMobile?STEPS_GRANULAR:STEPS_GROUPED;
let stepIndex=0;
mobileMQ.addEventListener('change',()=>{
  const granular=mobileMQ.matches&&MOBILE_FLOW==='granular';
  if(granular===isGranularMobile) return;
  const currentId=STEPS[stepIndex]&&STEPS[stepIndex].id;
  isGranularMobile=granular;
  STEPS=granular?STEPS_GRANULAR:STEPS_GROUPED;
  let target=STEPS.findIndex(step=>step.id===currentId);
  if(target<0&&COMPOSITE_OF[currentId]) target=STEPS.findIndex(step=>step.id===COMPOSITE_OF[currentId]);
  if(target<0&&FIRST_CHILD[currentId]) target=STEPS.findIndex(step=>step.id===FIRST_CHILD[currentId]);
  stepIndex=Math.max(0,target);
  renderAll();
});

/* ============================================================
   PRICE
   ============================================================ */
const CONFIG_PRODUCT_HANDLE='your-perfect-companion';
const CONFIG_ADDON_HANDLE='companion-customisation-addon';
const ADDON_VARIANT_TITLES={
  body:'Body warmth',
  oral:'Oral warmth',
  intimate:'Intimate warmth',
  oralmotion:'Oral motion',
  headmove:'Head movement',
  clampinternal:'Clamp & Suck (Internal)',
  clampexternal:'Clamp & Suck (External)',
  hip:'Hip movement',
};
function singleAddonVariantTitle(field,option){
  const key=option&&option.key;
  if(field==='buttFeel'&&key==='gel') return 'Gel-infused butt';
  if(field==='pubicHair'&&key&&key!=='natural') return 'Pubic hair styling';
  if(field==='tanLine'&&key==='yes') return 'Tan line';
  if(field==='breastPiercing'&&key==='on') return 'Nipple piercing';
  if(field==='navel'&&key&&key!=='none') return 'Navel piercing';
  if(field==='makeup'&&key==='custom') return 'Custom AI Makeup';
  if(field==='intimateSystem'&&key==='vaginax') return 'Bionic VaginaX';
  if(field==='ultraLight'&&key==='ultralight') return 'Ultra-Lightweight build';
  return null;
}
function readEmbeddedPricingProducts(){
  const node=document.querySelector('[data-sbd-pricing-products]');
  if(!node) return {};
  try{ return JSON.parse(node.textContent||'{}')||{}; }
  catch(error){ console.error('Could not parse embedded Shopify pricing products.',error); return {}; }
}
const EMBEDDED_PRICING=readEmbeddedPricingProducts();
const shopifyPricing={
  baseProduct:Array.isArray(EMBEDDED_PRICING.baseProduct?.variants)?EMBEDDED_PRICING.baseProduct:null,
  addonProduct:Array.isArray(EMBEDDED_PRICING.addonProduct?.variants)?EMBEDDED_PRICING.addonProduct:null,
  baseError:null,
  addonError:null,
  promise:null,
  addonTitles:ADDON_VARIANT_TITLES,
  singleAddonTitle:singleAddonVariantTitle,
};
window.SBD_SHOPIFY_PRICING=shopifyPricing;
async function fetchShopifyPricingProduct(handle){
  const response=await fetch(`/products/${handle}.js`,{credentials:'same-origin'});
  if(!response.ok) throw new Error(`Could not load Shopify pricing product: ${handle}`);
  const product=await response.json();
  if(!product||!Array.isArray(product.variants)) throw new Error(`Invalid Shopify pricing product: ${handle}`);
  return product;
}
function loadShopifyPricing(){
  if(shopifyPricing.promise) return shopifyPricing.promise;
  const baseRequest=shopifyPricing.baseProduct
    ?Promise.resolve(shopifyPricing.baseProduct)
    :fetchShopifyPricingProduct(CONFIG_PRODUCT_HANDLE);
  const addonRequest=shopifyPricing.addonProduct
    ?Promise.resolve(shopifyPricing.addonProduct)
    :fetchShopifyPricingProduct(CONFIG_ADDON_HANDLE);
  shopifyPricing.promise=Promise.allSettled([baseRequest,addonRequest]).then(results=>{
    const baseResult=results[0], addonResult=results[1];
    if(baseResult.status==='fulfilled'){
      shopifyPricing.baseProduct=baseResult.value;
      shopifyPricing.baseError=null;
    }else shopifyPricing.baseError=baseResult.reason||new Error('Base pricing unavailable');
    if(addonResult.status==='fulfilled'){
      shopifyPricing.addonProduct=addonResult.value;
      shopifyPricing.addonError=null;
    }else shopifyPricing.addonError=addonResult.reason||new Error('Add-on pricing unavailable');
    if(!shopifyPricing.baseProduct) throw shopifyPricing.baseError;
    if(shopifyPricing.addonError) console.error('Shopify add-on pricing could not be loaded.',shopifyPricing.addonError);
    return shopifyPricing;
  }).catch(error=>{
    shopifyPricing.promise=null;
    throw error;
  });
  return shopifyPricing.promise;
}
function selectedBodyUid(customizerState=state){
  const model=customizerState&&customizerState.bodyModel;
  return String((model&&(model.uid||model.id))||'').trim();
}
function baseVariantForBodyUid(bodyUid,product=shopifyPricing.baseProduct){
  const wanted=String(bodyUid||'').trim();
  if(!wanted||!Array.isArray(product?.variants)) return null;
  return product.variants.find(variant=>{
    const actual=Array.isArray(variant.options)?variant.options[0]:variant.option1;
    return String(actual||'').trim()===wanted;
  })||null;
}
// Reserve uses this same resolver. Keeping the displayed price and cart variant
// on one exact UID lookup prevents the old size-tier logic from drifting back in.
shopifyPricing.baseVariantForBodyUid=baseVariantForBodyUid;
function configuredBaseVariant(){
  return baseVariantForBodyUid(selectedBodyUid());
}
function configuredBodyPrice(){
  const variant=configuredBaseVariant();
  return variant&&Number.isFinite(Number(variant.price))?Number(variant.price)/100:null;
}
function shopifyAddonPrice(title){
  if(!title) return 0;
  const variant=shopifyPricing.addonProduct?.variants.find(option=>option.title===title);
  return variant&&Number.isFinite(Number(variant.price))?Number(variant.price)/100:null;
}
function optionPrice(field,option){
  if(!option) return 0;
  const title=singleAddonVariantTitle(field,option)||ADDON_VARIANT_TITLES[option.key];
  return shopifyAddonPrice(title);
}
/* Every billable single-select choice, in the order it should appear on the
   receipt. Previously total() hard-coded only breastFeel and buttFeel, so the
   four other priced groups — tan lines (+$60), nipple piercing, navel piercing
   and pubic hair — were selectable, showed a "+$" on their card, and charged
   the customer nothing. Declaring them once here is what stops total() and
   priceRows() from disagreeing again. */
const PRICED_CHOICES=[
  {field:'breastFeel',     get:()=>state.breastFeel,     label:()=>'Gel-infused breast'},
  {field:'buttFeel',       get:()=>state.buttFeel,       label:()=>'Gel-infused butt'},
  {field:'breastPiercing', get:()=>state.breastPiercing, label:()=>'Nipple piercing'},
  {field:'pubicHair',      get:()=>state.pubicHair,      label:o=>`Pubic hair · ${o.label}`},
  {field:'tanLine',        get:()=>state.tanLine,        label:()=>'Tan lines'},
  {field:'navel',          get:()=>state.navel,          label:o=>`Navel piercing · ${o.label}`},
  {field:'makeup',         get:()=>state.makeup,         label:o=>`Makeup · ${o.label}`},
  {field:'intimateSystem', get:()=>state.intimateSystem, label:()=>'Bionic VaginaX'},
  {field:'ultraLight',     get:()=>state.ultraLight,     label:()=>'Ultra-Lightweight build'},
];
/* The multi-select add-on groups, paired with the Set that tracks them. */
const ADDON_GROUPS=[
  ['warmth',   ()=>WARMTH_ADDONS,    ()=>state.warmth],
  ['sensation',()=>SENSATION_ADDONS, ()=>state.sensation],
  ['audio',    ()=>AUDIO_ADDONS,     ()=>state.audio],
  ['softcore', ()=>SOFTCORE_ADDONS,  ()=>state.softcore],
  ['movement', ()=>MOVEMENT_ADDONS,  ()=>state.movement],
];
// A product page always opens with that doll's UID look. Saved builds remain
// available on the standalone studio, and explicit design links still override.
const RESTORED_BUILD=BUILD_PRODUCT_HANDLE&&window.location.pathname.indexOf('/products/')===0
  ?null
  :restoreBuild();
function total(){
  let t=configuredBodyPrice();
  if(t===null) return null;
  for(const choice of PRICED_CHOICES){
    const price=optionPrice(choice.field,choice.get());
    if(price===null) return null;
    if(price) t+=price;
  }
  ADDON_GROUPS.forEach(([field,list,set])=>{
    list().forEach(o=>{
      if(!set().has(o.key)) return;
      const price=optionPrice(field,o);
      if(price===null) t=null;
      else if(t!==null) t+=price;
    });
  });
  return t;
}
function unavailablePrice(){ return Boolean(shopifyPricing.baseError||shopifyPricing.addonError); }
function pendingPriceText(){ return unavailablePrice()?'Price unavailable':'Loading price…'; }
function money(n){ return Number.isFinite(n)?('$'+n.toLocaleString()):pendingPriceText(); }
function monthlyEstimateText(n){
  if(!Number.isFinite(n)) return unavailablePrice()?'Monthly estimate unavailable':'Calculating monthly estimate…';
  // Round up so the displayed amount never understates a 24-way split.
  const monthlyCents=Math.ceil(Math.round(n*100)/24);
  const amount=(monthlyCents/100).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2});
  return `as low as $${amount}/month`;
}
const reduceMotion=window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function animateNumber(el,from,to){
  // An animation frame is not guaranteed to ever fire: hidden/background tabs
  // suspend rAF entirely, and a re-render can interrupt a running count mid-way
  // and leave a stale intermediate value on screen. So: cancel any count still
  // owning this element, and only animate when frames are actually being served
  // — in every other case write the final amount synchronously.
  if(el._sbdCountFrame){ cancelAnimationFrame(el._sbdCountFrame); el._sbdCountFrame=null; }
  if(from===to||reduceMotion||document.hidden){ el.textContent=money(to); return; }
  const dur=380, start=performance.now();
  function tick(now){
    const p=Math.min(1,(now-start)/dur);
    const eased=1-Math.pow(1-p,3);
    const val=Math.round(from+(to-from)*eased);
    el.textContent=money(val);
    el._sbdCountFrame=p<1?requestAnimationFrame(tick):null;
  }
  el._sbdCountFrame=requestAnimationFrame(tick);
}

// Single source of truth for "what am I paying for" — used by the desktop
// price panel, the mobile sticky-bar drawer, AND the review step's itemized
// breakdown, so the three can never drift out of sync with each other.
function priceRows(){
  const baseLabel=state.bodyModel?`${state.bodyModel.label} base`:`${state.body.label} base`;
  const rows=[[baseLabel,money(configuredBodyPrice())]];
  PRICED_CHOICES.forEach(c=>{
    const o=c.get(), price=optionPrice(c.field,o);
    if(price>0) rows.push([c.label(o),'+'+money(price)]);
  });
  ADDON_GROUPS.forEach(([field,list,set])=>{
    list().forEach(o=>{ const price=optionPrice(field,o); if(set().has(o.key)&&price>0) rows.push([o.label,'+'+money(price)]); });
  });
  return rows;
}
function priceRowsHtml(){
  return priceRows().map(([k,v])=>`<li><span>${k}</span><span>${v}</span></li>`).join('');
}

let lastTotal=0;
function updatePrice(){
  const t=total();
  const ready=Number.isFinite(t);
  const totalEl=document.getElementById('priceTotal');
  if(ready){
    animateNumber(totalEl,lastTotal,t);
    if(!reduceMotion){ totalEl.classList.remove('bump'); void totalEl.offsetWidth; totalEl.classList.add('bump'); }
  }else totalEl.textContent=unavailablePrice()?'Price unavailable':'Loading…';
  document.getElementById('sbTotal').textContent=money(t);
  document.getElementById('priceMo').textContent=monthlyEstimateText(t);
  document.getElementById('sbMo').textContent=monthlyEstimateText(t);
  if(ready) lastTotal=t;

  const html=priceRowsHtml();
  document.getElementById('priceBreakdown').innerHTML=html;
  document.getElementById('sbBreakdownList').innerHTML=html;
  scheduleSaveBuild();
}

function addonCount(){
  return state.warmth.size+state.sensation.size+state.audio.size+state.softcore.size+state.movement.size
    +(singleAddonVariantTitle('intimateSystem',state.intimateSystem)?1:0)
    +(singleAddonVariantTitle('ultraLight',state.ultraLight)?1:0);
}
// The live preview only ever reflected body/face/skin — a customer who spent five
// steps adding warmth, sensation and movement upgrades saw no acknowledgment of
// that until the review screen. Folding the running addon count into the caption
// (every step) and into the reveal's glow intensity (review only) makes the whole
// configurator feel like it's building toward her, not just the last screen.
function updatePreview(){
  const sil=document.getElementById('previewSil');
  const [c1,c2,c3]=state.skin.sil;
  sil.style.setProperty('--sil-1',c1);
  sil.style.setProperty('--sil-2',c2);
  sil.style.setProperty('--sil-3',c3);
  const n=addonCount();
  const cap=document.getElementById('previewCaption');
  const who=(state.name||'').trim()||'Her';
  // Body labels already carry the height ("Silicone 160cm"), and the default
  // name IS the face name — both read as stutters ("Luna · Luna · … 160cm · 160cm").
  const bodyLabel=state.bodyModel
    ?(new RegExp(`\\b${state.bodyModel.height}\\s*cm\\b`,'i').test(state.bodyModel.label)?state.bodyModel.label:`${state.bodyModel.label} · ${state.bodyModel.height}cm`)
    :state.body.label;
  const parts=[who];
  if(state.face.label.toLowerCase()!==who.toLowerCase()) parts.push(state.face.label);
  parts.push(bodyLabel,`${state.skin.label} skin`);
  let text=parts.join(' · ');
  if(n>0) text+=` · +${n} enhancement${n>1?'s':''}`;
  cap.textContent=text;
  const figure=document.querySelector('.preview-figure');
  if(figure){ figure.style.setProperty('--glow-a',(.18+Math.min(n,6)*0.033).toFixed(3)); }
  // Swap in the real composited portrait for the current face + hair + eye colour.
  // Faces with a render set for the current skin tone (Luna: all tones; Suki/Kitty:
  // Glow) show a photo; anything else falls back to the silhouette.
  const photo=document.getElementById('previewPhoto');
  const photoOpen=document.getElementById('previewPhotoOpen');
  const makeupOverride=syncMakeupPreview();
  if(photo&&figure){
    const fa=FACE_ASSETS[state.face.key];
    const sk=fa&&fa.skins[state.skin.key];
    const hasPhoto=sk&&sk.hairs.length>0&&state.hairStyle&&state.hairStyle.hair&&state.eyes&&state.eyes.eyeNum;
    if(hasPhoto){
      const raw=makeupOverride||faceImg(state.face.key,state.skin.key,state.eyes.eyeNum,state.hairStyle.hair);
      // Portraits are all 1024x1280. The panel paints this 300px wide at >=900px
      // and as a 48px thumb below that, so offer the whole ladder and let
      // `sizes` choose. Asking for a 4:5 box is a no-op crop on a 4:5 source,
      // and cover (not contain) keeps it that way if a render ever ships
      // off-aspect — letterbox bars would bake into the image.
      const pv=w=>cdnImg(raw,w,Math.round(w*5/4),{q:IMG_Q_PREVIEW});
      const src=pv(300);
      if(!custDeferImages&&photo.getAttribute('src')!==src){
        photo.setAttribute('src',src);
        watchPreviewPhotoLoad(photo);
        // Same caveat as imgAttrs: without a resizer the candidates collapse to
        // one URL, and w-descriptors on identical URLs mislead the browser.
        // 450 sits where a phone lands: the stage paints ~200-260px there
        // (sbd-polish.js initPreviewSizing measures it), so at DPR 2 the next
        // rung up from 300 was 600 — 21 KB where 14 KB covers it.
        // sbd-polish.js STAGE_LADDER mirrors this list; keep them identical.
        const cands=[96,150,300,450,600,900].map(w=>[pv(w),w]);
        if(cands.some(c=>c[0]!==src)){
          photo.setAttribute('srcset',cands.map(([u,w])=>`${u} ${w}w`).join(', '));
          photo.setAttribute('sizes','(min-width:900px) 300px, 100vw');
        }else{
          photo.removeAttribute('srcset');
          photo.removeAttribute('sizes');
        }
      }
      photo.alt=`${who}, ${state.skin.label} skin — ${state.hairStyle.label}, eye colour ${state.eyes.eyeNum}`+(makeupOverride?`, ${state.makeup.label} makeup`:'');
      photo.dataset.fullSrc=raw;
      if(photoOpen){ photoOpen.hidden=false; photoOpen.disabled=false; }
      // Only claim a photo once one is actually set. While the image gate holds
      // the portrait back, has-photo with no src painted the stage as an empty
      // black frame (seen live on iOS, 2026-09-14); the silhouette is the honest
      // placeholder until the release re-renders.
      figure.classList.toggle('has-photo',Boolean(photo.getAttribute('src')));
    } else {
      figure.classList.remove('has-photo');
      photo.removeAttribute('data-full-src');
      if(photoOpen){ photoOpen.hidden=true; photoOpen.disabled=true; }
    }
  }

  // Once the Body step is complete, keep the exact selected body visible below
  // the head portrait in the desktop side panel. This uses the same asset lookup
  // as the Body studio, so skin tone and nude/laced view never drift apart.
  const bodyCard=document.getElementById('previewBodyCard');
  const bodyPhoto=document.getElementById('previewBodyPhoto');
  const bodyLabelEl=document.getElementById('previewBodyLabel');
  const onReview=STEPS[stepIndex]?.id==='review';
  // Review only: on every other step the card competed with the head for the
  // side panel's height, and the body already has its own step.
  const showBody=onReview&&Boolean(state.bodyModel);
  const bodySrc=showBody?bodyAssetUrl(state.bodyModel,'thumb',bodyView,fitWidth(320,bodyAssetNative(state.bodyModel,'thumb'))):'';
  // Mobile review: the exact body turns beside her head inside the stage
  // (markup in the section; layout in sbd-polish.css). Same sprite and angle
  // index as the Body studio, so it opens on whatever angle she was last seen at.
  // Mobile body-option steps: the stage shows the part of her body the step is
  // about instead of the head — one sprite frame, zoomed on the region.
  const focusEl=document.getElementById('previewBodyFocus');
  if(focusEl&&figure){
    const spec=mobileMQ.matches&&state.bodyModel?enhFocusFor(STEPS[stepIndex]?.id):null;
    const sprite=spec?bodyAssetUrl(state.bodyModel,'sprite','nude',bodySpriteW(state.bodyModel,'nude')):'';
    focusEl.hidden=!sprite;
    figure.classList.toggle('has-body-focus',Boolean(sprite));
    if(sprite){
      const focusImg=document.getElementById('previewBodyFocusImg');
      const frame=focusEl.querySelector('.pbf-frame');
      const asset=bodyAssetData(state.bodyModel,'nude');
      if(!custDeferImages&&focusImg.getAttribute('src')!==sprite) focusImg.setAttribute('src',sprite);
      focusImg.alt=`${state.bodyModel.label}, ${BODY_ANGLES[spec.angle].label.toLowerCase()} view, ${spec.region}`;
      focusImg.style.setProperty('--body-frame-offset',`${-spec.angle*12.5}%`);
      if(frame){
        frame.style.setProperty('--body-frame-ratio',`${asset?.width||593}/${asset?.height||1600}`);
        frame.style.setProperty('--fy',String(spec.fy));
        frame.style.setProperty('--fz',String(spec.fz));
      }
      paintBodyOverlay(focusEl,STEPS[stepIndex]?.id,asset);
    }
  }
  // Desktop groups breast + butt under one "Feel" step, so show BOTH close-ups
  // together — breast on top, butt below (mobile splits them into own steps).
  const duo=ensureBodyDuo();
  if(duo&&figure){
    const showDuo=!mobileMQ.matches&&STEPS[stepIndex]?.id==='feel'&&Boolean(state.bodyModel);
    duo.hidden=!showDuo;
    duo.setAttribute('aria-hidden',showDuo?'false':'true');
    figure.classList.toggle('has-body-duo',showDuo);
    if(showDuo){
      const asset=bodyAssetData(state.bodyModel,'nude');
      const sprite=bodyAssetUrl(state.bodyModel,'sprite','nude',bodySpriteW(state.bodyModel,'nude'));
      paintDuoCell(duo.querySelector('[data-region="chest"]'),sprite,asset,DESKTOP_FEEL_FOCUS.chest,'breastPiercing');
      paintDuoCell(duo.querySelector('[data-region="hips"]'),sprite,asset,DESKTOP_FEEL_FOCUS.hips,'buttFeel');
    }
  }
  const bodyStage=document.getElementById('previewBodyStage');
  if(bodyStage&&figure){
    const sprite=onReview&&mobileMQ.matches&&state.bodyModel?bodyAssetUrl(state.bodyModel,'sprite',bodyView,bodySpriteW(state.bodyModel)):'';
    bodyStage.hidden=!sprite;
    figure.classList.toggle('has-body-stage',Boolean(sprite));
    if(sprite){
      const stageImg=document.getElementById('previewBodyStageImg');
      if(stageImg&&!custDeferImages&&stageImg.getAttribute('src')!==sprite) stageImg.setAttribute('src',sprite);
      paintPreviewBodyStage();
    }
  }
  if(bodyCard){
    bodyCard.hidden=!(showBody&&bodySrc);
    document.querySelector('.preview-panel')?.classList.toggle('has-body-preview',showBody&&Boolean(bodySrc));
  }
  if(showBody&&bodySrc&&bodyPhoto){
    if(!custDeferImages&&bodyPhoto.getAttribute('src')!==bodySrc) bodyPhoto.setAttribute('src',bodySrc);
    bodyPhoto.alt=`${state.bodyModel.label}, full body front preview in ${state.skin.label} skin`;
  }
  if(bodyLabelEl) bodyLabelEl.textContent=showBody?bodyLabel:'';
  // Every selection path ends in updatePreview and/or updatePrice, so hooking
  // the (debounced) save here covers the whole configurator without touching
  // each change handler individually.
  // The enhancement feature stage keys off the same signal set: every step
  // change and every selection change funnels through updatePreview. It runs
  // BEFORE the save/warm tail so a failure there can never leave a stale scene.
  renderEnhStage();
  scheduleSaveBuild();
  warmBodySprite();
}
/* The chest close-ups (breast feel, butt feel, nipple size, nipple piercing) paint the NUDE
   turntable sprite — a different 250-350 KB file from the lace one the body picker shows. The
   step used to start that download only when it opened, and the FIRST request for a given
   body+tone waits ~2 s while the CDN builds its WebP variant (measured on live), so the frame
   sat empty. Warm it as soon as the body and tone are known.

   Debounced, because the skin step re-runs this on every tone the customer tries and each one
   is a different sprite — 700 ms means only a tone they actually settle on is fetched. Low
   priority so it never competes with the portrait that is on screen. */
/* The customizer renders while it is still collapsed (display:none) so that
   price, saved build and cart wiring are live the moment it opens. Painting it
   that early, though, downloaded 1.6 MB of portraits and a 1 MB body sprite for
   a panel nobody could see yet. Image work is held back until it is genuinely
   open, then replayed by a single renderAll().

   The homepage studio is never collapsed, but it starts below the hero (1285px
   down on a 375px phone), and it fetched the portrait, a body sprite and the
   option posters at load all the same. So an open customizer that starts
   further than CUST_NEAR_PX below the first screen is held too, until it
   scrolls within CUST_NEAR_PX of the viewport.

   deferImages is armed ONLY when we can also detect the opening (or the
   approach), so a browser without the observer paints immediately rather
   than never.

   The approach is detected THREE ways, and any one releases: the observer, a
   plain scroll/resize/pageshow position check, and any touch or focus inside
   the customizer. On iOS (DuckDuckGo, 2026-09-14) the observer alone left a
   customer scrolled onto the studio with a blank stage that never filled.
   custDeferGate holds the observer and listeners at module level so nothing
   can be collected while the gate is armed. */
let custDeferImages=false;
let custDeferGate=null;
const CUST_NEAR_PX=300;
function customizerCollapsed(){
  const root=document.getElementById('atelier-landing');
  return !!(root&&root.classList.contains('sbd-customizer--collapsed'));
}
(function armCustomizerImageGate(){
  try{
    const root=document.getElementById('atelier-landing');
    if(!root) return;
    const release=function(){
      custDeferImages=false;
      try{ renderAll(); }catch(e){}
    };
    if(customizerCollapsed()){
      if(!window.MutationObserver) return;
      custDeferImages=true;
      const mo=new MutationObserver(function(){
        if(customizerCollapsed()) return;
        mo.disconnect();
        release();
      });
      mo.observe(root,{attributes:true,attributeFilter:['class']});
      return;
    }
    const near=function(){ return root.getBoundingClientRect().top<window.innerHeight+CUST_NEAR_PX; };
    // Already on, or close to, the first screen (or scrolled past): paint now.
    if(near()) return;
    custDeferImages=true;
    const gate={io:null,done:false};
    const onMove=function(){ if(near()) finish(); };
    const onTouch=function(){ finish(); };
    function finish(){
      if(gate.done) return;
      gate.done=true;
      if(gate.io) gate.io.disconnect();
      window.removeEventListener('scroll',onMove);
      window.removeEventListener('resize',onMove);
      window.removeEventListener('pageshow',onMove);
      root.removeEventListener('pointerdown',onTouch,true);
      root.removeEventListener('focusin',onTouch,true);
      custDeferGate=null;
      release();
    }
    if(window.IntersectionObserver){
      gate.io=new IntersectionObserver(function(entries){
        if(entries.some(function(entry){ return entry.isIntersecting; })) finish();
      },{rootMargin:CUST_NEAR_PX+'px 0px'});
      gate.io.observe(root);
    }
    window.addEventListener('scroll',onMove,{passive:true});
    window.addEventListener('resize',onMove,{passive:true});
    window.addEventListener('pageshow',onMove);
    root.addEventListener('pointerdown',onTouch,true);
    root.addEventListener('focusin',onTouch,true);
    custDeferGate=gate;
  }catch(e){
    // Never leave a customer on a held stage: an error here means paint now.
    if(custDeferImages){ custDeferImages=false; try{ renderAll(); }catch(err){} }
  }
})();

let warmedBodySprite='', warmSpriteTimer=null;
function warmBodySprite(){
  if(!state.bodyModel) return;
  if(customizerCollapsed()||custDeferImages) return; // nothing to warm for a panel that is shut or off screen
  clearTimeout(warmSpriteTimer);
  warmSpriteTimer=setTimeout(()=>{
    const url=bodyAssetUrl(state.bodyModel,'sprite','nude',bodySpriteW(state.bodyModel,'nude'));
    if(!url||url===warmedBodySprite) return;
    warmedBodySprite=url;
    const img=new Image();
    img.decoding='async';
    if('fetchPriority' in img) img.fetchPriority='low';
    img.src=url;
  },700);
}

/* ============================================================
   RENDER: stepper + shell
   ============================================================ */
// A step may opt out of being asked when its answer is already determined by
// earlier choices. Visibility is recomputed on every render for any future
// conditional steps.
function isVisible(i){ const s=STEPS[i]; return !s.skip || !s.skip(); }
function stepIndexForId(id){
  let index=STEPS.findIndex(step=>step.id===id);
  if(index<0&&COMPOSITE_OF[id]) index=STEPS.findIndex(step=>step.id===COMPOSITE_OF[id]);
  if(index<0&&FIRST_CHILD[id]) index=STEPS.findIndex(step=>step.id===FIRST_CHILD[id]);
  return index;
}
function nearestVisible(i){
  if(isVisible(i)) return i;
  for(let f=i+1; f<STEPS.length; f++){ if(isVisible(f)) return f; }
  for(let b=i-1; b>=0; b--){ if(isVisible(b)) return b; }
  return i;
}
// Next/Back must skip a hidden step entirely rather than land on it — this is
// what footer/sticky-bar navigation uses; stepper pills only ever render
// visible steps in the first place, so they can call goToStep directly.
function stepBy(delta){
  let i=stepIndex;
  do{ i+=delta; } while(i>=0 && i<STEPS.length && !isVisible(i));
  goToStep(Math.max(0,Math.min(STEPS.length-1,i)));
}
function renderStepper(){
  const el=document.getElementById('stepper');
  const visible=STEPS.map((s,i)=>({s,i})).filter(({i})=>isVisible(i));
  const currentPosition=Math.max(0,visible.findIndex(({i})=>i===stepIndex));
  const currentStep=visible[currentPosition]&&visible[currentPosition].s;
  const stageLabel=document.getElementById('previewStepLabel');
  const stageCount=document.getElementById('previewStepCount');
  const stageProgress=document.getElementById('previewProgressFill');
  if(stageLabel) stageLabel.textContent=currentStep?currentStep.label:'';
  if(stageCount) stageCount.textContent=visible.length?`${currentPosition+1} of ${visible.length}`:'';
  if(stageProgress) stageProgress.style.width=visible.length?`${((currentPosition+1)/visible.length)*100}%`:'0%';
  el.innerHTML=visible.map(({s,i},pos)=>{
    const current=i===stepIndex;
    const done=i<stepIndex;
    // The checkmark is a purely visual "done" signal (aria-hidden number badge) —
    // add a screen-reader-only suffix so that state isn't sighted-only.
    const doneSuffix=done?'<span class="visually-hidden"> (completed)</span>':'';
    return `<button type="button" class="step-btn${done?' is-done':''}" data-step="${i}" ${current?'aria-current="step"':''}>
      <span class="num" aria-hidden="true">${pos+1}</span><span class="step-label">${s.label}</span>${doneSuffix}
    </button>`;
  }).join('');
  el.querySelectorAll('.step-btn').forEach(btn=>{
    btn.addEventListener('click',()=>goToStep(parseInt(btn.dataset.step,10)));
  });
}
function closeSbBreakdown(){
  const drawer=document.getElementById('sbBreakdown');
  const toggle=document.getElementById('sbToggle');
  drawer.setAttribute('hidden','');
  toggle.setAttribute('aria-expanded','false');
}
// Browser-back steps backwards through the configurator instead of leaving the
// page and losing the build. Each real step change pushes a #step-<id> entry;
// popstate re-enters goToStep with pushing suppressed so history isn't re-written
// while it's being replayed.
let replayingHistoryStep=false;
function pushHistoryStep(){
  if(replayingHistoryStep||!(window.history&&history.pushState)) return;
  const id=STEPS[stepIndex]&&STEPS[stepIndex].id;
  if(!id||(history.state&&history.state.sbdStep===id)) return;
  try{ history.pushState({sbdStep:id},'','#step-'+id); }catch(error){}
}
function goToStep(i){
  stepIndex=nearestVisible(Math.max(0,Math.min(STEPS.length-1,i)));
  pushHistoryStep();
  closeSbBreakdown();
  renderAll();
  const panel=document.getElementById('questionPanel');
  /* The desktop shell scrolls INSIDE this panel (sbd-polish.css, min-width:900px)
     rather than scrolling the document, and an element keeps its scrollTop across
     an innerHTML swap. Without this, leaving a long step half-read and picking the
     next one opened it already scrolled past its own heading — the question would
     be off the top of the box.

     Reset here in goToStep and NOT in renderAll: renderAll also runs on ordinary
     option changes, where yanking the panel back to the top mid-list is exactly
     what a customer does not want. */
  panel.scrollTop=0;
  panel.focus({preventScroll:true});
  const activePill=document.querySelector('.step-btn[aria-current="step"]');
  if(activePill){ activePill.scrollIntoView({behavior:reduceMotion?'auto':'smooth',block:'nearest',inline:'center'}); }
  if(window.innerWidth<900){
    const anchor=document.getElementById('cfgGrid')||document.getElementById('cfgHeading');
    anchor.scrollIntoView({behavior:reduceMotion?'auto':'smooth',block:'start'});
  }
}
/* Which sprite frame and which part of it each body-option step shows in the
   mobile stage. angle indexes BODY_ANGLES (0 front, 4 back); fy is the focus
   point as a fraction of the frame's height; fz the zoom. Tuned on the
   593x1600 Irontech frames: chest ~0.24, navel ~0.40, hips ~0.47. */
const BODY_FOCUS_BY_STEP={
  breastFeel:    {angle:0,fy:0.30,fz:1.15,region:'chest'},
  areola:        {angle:0,fy:0.29,fz:1.35,region:'chest'},
  breastPiercing:{angle:0,fy:0.29,fz:1.35,region:'chest'},
  buttFeel:      {angle:4,fy:0.5,fz:1.55,region:'hips'},
  tanLine:       {angle:4,fy:0.40,fz:1.7,region:'back and hips'},
  pubicHair:     {angle:0,fy:0.49,fz:2.2,region:'hips'},
  navel:         {angle:0,fy:0.41,fz:2.2,region:'navel'},
};
/* ============================================================
   ENHANCEMENT FEATURE STAGE (granular mobile)
   ============================================================
   On the six enhancement sub-steps the stage stops re-showing the same head
   render and demonstrates the feature instead: her own body zone (the body
   focus sprite) as the "what's included" backdrop, the real feature footage /
   product visuals in a framed media panel, a one-line benefit caption and
   verified fact rows. The option cards on these steps carry explicit Yes/No
   pills that proxy the existing checkbox/radio inputs, so pricing, cart
   serialization and the VaginaX exclusions run through the untouched
   wireChange path. Canal construction is independent of heating and suction;
   do not infer removability from those selections. Media provenance is
   labelled honestly: Real footage / Product photo / Product visual for
   supplier material, Illustration for generated art. */
/* Wide, recognizable framings only — tight zooms upscale the sprite into a
   blurry close-up (owner-rejected 2026-09-10). fz stays at or near 1 so the
   figure reads as her body, not an anatomical crop. */
const ENH_FOCUS_BY_STEP={
  intimateSystem:{angle:0,fy:0.42,fz:1.1,region:'waist and hips'},
  warmth:{angle:0,fy:0.32,fz:1.05,region:'torso'},
  sensation:{angle:0,fy:0.24,fz:1.05,region:'upper body'},
  audio:{angle:0,fy:0.18,fz:1.1,region:'head and shoulders'},
  softcore:{angle:0,fy:0.4,fz:1.05,region:'waist and thighs'},
  movement:{angle:4,fy:0.45,fz:1.0,region:'full body'},
};
function enhSpotlightKey(grp){
  const grpScene=ENH_SCENES[grp]; if(!grpScene) return null;
  const prev=enhPreviewKey[grp];
  if(prev&&grpScene.options[prev]) return prev;
  const picked=enhSelectedKeys(grp);
  let key=enhLastPick[grp];
  if(!key||!picked.includes(key)) key=picked.length?picked[picked.length-1]:null;
  return key;
}
function enhFocusFor(stepId){
  const base=BODY_FOCUS_BY_STEP[stepId];
  if(base) return base;
  const grpScene=ENH_SCENES[stepId];
  if(!grpScene) return ENH_FOCUS_BY_STEP[stepId]||null;
  const key=enhSpotlightKey(stepId);
  const sc=key?grpScene.options[key]:null;
  // Photo-backdrop options keep the group's wide body focus underneath purely
  // for layout (the stage sizes full-bleed off it); the portrait paints on top.
  return (sc&&sc.focus)||ENH_FOCUS_BY_STEP[stepId]||null;
}
const enhAsset=f=>new URL(f,CUSTOMIZER_ASSET_BASE).href;
// Per-option stage scenes. `asset` is a theme asset filename; options without
// one fall back to their card's own img/video (the Shopify Files originals).
const ENH_SCENES={
  intimateSystem:{
    kicker:'Intimate system',
    learn:'/pages/robot-functions#intimate',
    included:{title:'Her standard build',line:'Sculpted silicone vaginal and anal canals.'},
    options:{
      standard:{line:'Sculpted silicone vaginal and anal canals.',bodyFrame:{fy:0.47,fz:1.1},tag:'Product visual',focus:{angle:0,fy:0.42,fz:1.1,region:'waist and hips'}},
      vaginax:{asset:'sbd-enh-vaginax.jpg',tag:'Product visual',focus:{angle:0,fy:0.44,fz:1.25,region:'hips'},
        line:'Senses how you move and answers it — contraction and vibration, tuned from your phone.',
        extra:'No anal canal — cannot be added later.',
        power:'Cordless — runs on a rechargeable battery',
        facts:['Responsive tightening and vibration','Internal heating, adjustable 33–44°C','Easy to clean']},
    }},
  warmth:{kicker:'Heating',power:'Cordless — runs on a rechargeable battery',learn:'/pages/robot-functions#warmth',
    included:{line:'No heating in the base build — her silicone stays at room temperature.',bodyFrame:{fy:0.3,fz:1},tag:'Product visual'},
    options:{
      body:{asset:'sbd-enh-warm-body.jpg',tag:'Illustration',focus:{angle:0,fy:0.32,fz:1.05,region:'torso'},line:'She’s warm when you reach for her — internal heating, through the whole body.',
        facts:['The option owners keep using most','Fitted at production — can’t be added later']},
      oral:{asset:'sbd-enh-warm-oral.jpg',tag:'Illustration',line:'Reaches 37°C inside the mouth and on the tongue — body temperature, exactly.',
        facts:['Best taken alongside body warmth','Fitted at production — can’t be added later']},
    }},
  sensation:{kicker:'Suction & stroking',power:'Rechargeable powered stimulation',learn:'/pages/robot-functions#intimate',
    included:{line:'Add oral or vaginal suction and stroking, independently.',bodyFrame:{fy:0.3,fz:1},tag:'Product visual'},
    options:{
      oralmotion:{tag:'Real footage',learn:'/pages/robot-functions#intimate',line:'Rhythmic suction and throat contractions create a stroking sensation.',
        facts:['Powered oral stimulation','Mouth heating is a separate option']},
      clampinternal:{tag:'Real footage',learn:'/pages/robot-functions#intimate',focus:{angle:0,fy:0.44,fz:1.3,region:'hips'},line:'Rhythmic suction, contractions and stroking, with vibration and heating included.',
        facts:['Adjustable stimulation patterns','Vaginal heating included — no separate heating charge']},
    }},
  movement:{kicker:'Automatic movement',power:'Controlled from the included remote',learn:'/pages/robot-functions#motion',
    included:{title:'What’s included',video:'craft-pose.mp4',videoIsAsset:true,tag:'Real footage',
      line:'No motors — her stainless-steel skeleton, articulated at every joint, holds any pose you set.'},
    options:{
      headmove:{tag:'Real footage',learn:'/pages/robot-functions#motion',line:'Powered back-and-forth head movement.',
        facts:['Controlled from the included remote','Fitted during production']},
      hip:{tag:'Real footage',focus:{angle:4,fy:0.45,fz:1.15,region:'hips'},line:'Powered movement of her hips and waist.',
        facts:['Discreet handheld remote — no app or account','Five speed settings, adjustable rhythm','Fitted at production — can’t be added later']},
    }},
};
const ENH_OPTION_SOURCES={
  intimateSystem:()=>INTIMATE_SYSTEM,
  warmth:()=>warmthOptions(),
  sensation:()=>sensationOptions(),
  audio:()=>audioOptions(),
  softcore:()=>SOFTCORE_ADDONS,
  movement:()=>MOVEMENT_ADDONS,
};
function enhSelectedKeys(grp){
  if(grp==='intimateSystem') return state.intimateSystem?[state.intimateSystem.key]:[];
  const set=state[grp];
  return set&&set.size?[...set]:[];
}
let enhLastPick={};
// Transient "just looking" state: tapping a card BODY previews that option on
// the stage without selecting it (only the Yes/No pills change the build).
let enhPreviewKey={};
let enhSceneStepId='';
// Desktop "?" spotlight: {grp,key} while a card's info button is open, else null.
let enhDesktopKey=null;
function ensureEnhScene(){
  let scene=document.getElementById('enhScene');
  if(scene) return scene;
  const fig=document.querySelector('.preview-figure');
  if(!fig) return null;
  scene=document.createElement('div');
  scene.className='sbd-enh-scene';
  scene.id='enhScene';
  scene.hidden=true;
  scene.innerHTML='<img class="ses-bg" alt="" aria-hidden="true" hidden>'
    +'<div class="ses-cap">'
    +'<div class="ses-kicker"></div>'
    +'<div class="ses-title"></div>'
    +'<div class="ses-line"></div>'
    +'<div class="ses-extra" hidden></div>'
    +'<a class="ses-learn" target="_blank" rel="noopener" hidden>Learn more</a>'
    +'</div>'
    +'<div class="ses-frame" hidden>'
    +'<div class="ses-body" hidden><img alt="" decoding="async"></div>'
    +'<video muted loop playsinline preload="none" hidden></video>'
    +'<img alt="" decoding="async" hidden>'
    +'<span class="ses-tag" hidden></span>'
    +'</div>'
    +'<ul class="ses-facts" hidden></ul>'
    +'<button type="button" class="ses-close" aria-label="Close">\u00d7</button>';
  fig.appendChild(scene);
  return scene;
}
/* Paint HER body (current model + tone) inside the media frame — a mini
   version of the pbf transform: the 8-frame nude sprite sheet, one frame,
   zoomed to a region. Pixel math against the frame's live box. */
function paintEnhBodyFrame(scene,spec){
  const holder=scene.querySelector('.ses-body'); const img=holder&&holder.querySelector('img');
  if(!holder||!img) return false;
  if(!spec||!state.bodyModel){ holder.hidden=true; return false; }
  const asset=bodyAssetData(state.bodyModel,'nude');
  const sprite=asset?bodyAssetUrl(state.bodyModel,'sprite','nude',bodySpriteW(state.bodyModel,'nude')):'';
  if(!sprite){ holder.hidden=true; return false; }
  if(img.getAttribute('src')!==sprite) img.setAttribute('src',sprite);
  holder.hidden=false;
  const box=holder.getBoundingClientRect();
  const H=box.height||200, W=box.width||200;
  // Width-fill: one sprite frame spans the square (times fz), fy picks the
  // vertical band. Height-fill left a thin strip in a black box.
  const fz=spec.fz||1, fy=spec.fy||0.4, angle=spec.angle||0;
  const fw=asset.width||556, fh=asset.height||1600;
  const frameW=W*fz, imgH=frameW*fh/fw;
  img.style.width=(frameW*8)+'px';
  img.style.height=imgH+'px';
  img.style.maxWidth='none';
  img.style.transform='translate('+(-(angle*frameW)+(W-frameW)/2)+'px,'+(H/2-fy*imgH)+'px)';
  return true;
}
function stopEnhVideo(){
  const v=document.querySelector('#enhScene video');
  if(v&&!v.paused) v.pause();
}
function renderEnhStage(){
  try{ renderEnhStageInner(); }catch(e){
    // Never let the scene break the preview chain — worst case it hides.
    try{
      const s=document.getElementById('enhScene'); if(s) s.hidden=true;
      document.querySelector('.preview-figure')?.classList.remove('has-enh-scene','enh-has-media');
    }catch(e2){}
  }
}
function renderEnhStageInner(){
  const fig=document.querySelector('.preview-figure');
  if(!fig) return;
  const stepId=STEPS[stepIndex]?.id;
  if(stepId!==enhSceneStepId){ enhSceneStepId=stepId; enhPreviewKey={}; enhDesktopKey=null; }
  // Desktop: the scene opens only while a card's "?" info button is active.
  const desktopOn=Boolean(!mobileMQ.matches&&enhDesktopKey&&ENH_SCENES[enhDesktopKey.grp]);
  const grpScene=ENH_SCENES[desktopOn?enhDesktopKey.grp:stepId];
  const on=Boolean(grpScene&&((isGranularMobile&&mobileMQ.matches)||desktopOn));
  fig.classList.toggle('has-enh-scene',on);
  const scene=ensureEnhScene();
  if(!scene) return;
  scene.classList.toggle('ses-desktop',desktopOn);
  document.querySelectorAll('.sbd-enh-info').forEach(b=>{
    b.classList.toggle('is-on',desktopOn&&b.dataset.grp===enhDesktopKey.grp&&b.dataset.key===enhDesktopKey.key);
  });
  if(!on){ scene.hidden=true; fig.classList.remove('enh-has-media','enh-bg-photo'); stopEnhVideo(); return; }
  const grp=desktopOn?enhDesktopKey.grp:stepId;
  const options=ENH_OPTION_SOURCES[grp]?ENH_OPTION_SOURCES[grp]():[];
  const picked=enhSelectedKeys(grp);
  // Who holds the stage: the most recent pick while still selected, else the
  // last selected, else nobody — the "what's included" summary.
  const key=desktopOn?enhDesktopKey.key:enhSpotlightKey(grp);
  const sc=key?(grpScene.options[key]||null):null;
  // Face-zone options paint her actual portrait across the stage (the body
  // sprite keeps sizing the stage underneath; the portrait covers it).
  const wantPhoto=Boolean(sc&&sc.bg==='photo');
  fig.classList.toggle('enh-bg-photo',wantPhoto);
  const bgImg=scene.querySelector('.ses-bg');
  if(bgImg){
    const photoEl=document.getElementById('previewPhoto');
    const bgSrc=wantPhoto?(photoEl&&(photoEl.currentSrc||photoEl.getAttribute('src')))||'':'';
    if(bgSrc){ if(bgImg.getAttribute('src')!==bgSrc) bgImg.setAttribute('src',bgSrc); bgImg.hidden=false; }
    else bgImg.hidden=true;
  }
  const opt=key?options.find(o=>o.key===key):null;
  const frame=scene.querySelector('.ses-frame');
  // :scope> — the .ses-body layer holds its own <img>; a bare querySelector
  // grabbed that one and the whole frame painted into the wrong element.
  const video=frame.querySelector(':scope > video');
  const img=frame.querySelector(':scope > img');
  const tagEl=frame.querySelector('.ses-tag');
  const extraEl=scene.querySelector('.ses-extra');
  const factsEl=scene.querySelector('.ses-facts');
  let title,line,extra=null,facts=null,mediaImg=null,mediaVideo=null,mediaBody=null,tag='';
  if(opt&&sc){
    title=opt.label;
    line=sc.line||opt.desc||'';
    extra=sc.extra||null;
    facts=(sc.facts||[]).slice();
    const power=sc.power!==undefined?sc.power:grpScene.power;
    if(power) facts.push(power);
    if(grp!=='intimateSystem'){
      const others=picked.filter(k=>k!==key)
        .map(k=>{const o=options.find(x=>x.key===k);return o&&o.label;})
        .filter(Boolean);
      if(others.length) facts.push('Also selected: '+others.join(', '));
    }
    if(!sc.noMedia){
      if(sc.bodyFrame){ mediaBody=sc.bodyFrame; tag=sc.tag||'Product visual'; }
      else if(sc.asset){ mediaImg=enhAsset(sc.asset); tag=sc.tag||'Illustration'; }
      else if(opt.video){ mediaVideo=opt.video; tag=sc.tag||'Real footage'; }
      else if(opt.img){ mediaImg=opt.img; tag=sc.tag||'Product photo'; }
    }
  } else {
    const inc=grpScene.included||{};
    title=inc.title||'What’s included';
    line=inc.line||'';
    if(grp!=='intimateSystem'){
      facts=options.map(o=>{
        const p=optionPrice(grp,o);
        return 'Add '+o.label+(p>0?' — +'+money(p):'');
      });
    }
    if(inc.bodyFrame){ mediaBody=inc.bodyFrame; tag=inc.tag||'Product visual'; }
    else if(inc.asset){ mediaImg=enhAsset(inc.asset); tag=inc.tag||'Product visual'; }
    else if(inc.video){ mediaVideo=inc.videoIsAsset?enhAsset(inc.video):inc.video; tag=inc.tag||'Real footage'; }
  }
  const learnEl=scene.querySelector('.ses-learn');
  if(learnEl){
    const learnUrl=(opt&&sc&&sc.learn)||grpScene.learn||'';
    learnEl.hidden=!learnUrl;
    if(learnUrl&&learnEl.getAttribute('href')!==learnUrl) learnEl.setAttribute('href',learnUrl);
  }
  scene.querySelector('.ses-kicker').textContent=grpScene.kicker||'';
  scene.querySelector('.ses-title').textContent=title||'';
  scene.querySelector('.ses-line').textContent=line||'';
  extraEl.hidden=!extra;
  if(extra) extraEl.textContent=extra;
  if(facts&&facts.length){
    factsEl.hidden=false;
    factsEl.innerHTML=facts.map(f=>'<li></li>').join('');
    [...factsEl.children].forEach((li,i)=>{ li.textContent=facts[i]; });
  } else { factsEl.hidden=true; factsEl.innerHTML=''; }
  const hasMedia=Boolean(mediaImg||mediaVideo||mediaBody);
  frame.hidden=!hasMedia;
  fig.classList.toggle('enh-has-media',hasMedia);
  if(mediaBody){ stopEnhVideo(); video.hidden=true; img.hidden=true; paintEnhBodyFrame(scene,mediaBody); }
  else { const bh=scene.querySelector('.ses-body'); if(bh) bh.hidden=true; }
  if(mediaBody){ /* body frame painted above */ }
  else if(mediaVideo){
    img.hidden=true;
    video.hidden=false;
    if(video.getAttribute('src')!==mediaVideo){ video.setAttribute('src',mediaVideo); video.load(); }
    const p=video.play(); if(p&&p.catch) p.catch(()=>{});
  } else {
    stopEnhVideo();
    video.hidden=true;
    if(mediaImg){ img.hidden=false; if(img.getAttribute('src')!==mediaImg) img.setAttribute('src',mediaImg); }
    else img.hidden=true;
  }
  tagEl.hidden=!hasMedia||!tag;
  tagEl.textContent=tag||'';
  scene.hidden=false;
}
/* Yes/No pills on the enhancement cards. Buttons live OUTSIDE the <label>
   (same rule as .opt-audio: a button inside a label activates the label). */
function enhYnLabel(input,opt,grp){
  if(input.checked) return '✓ Yes';
  const p=opt?optionPrice(grp,opt):null;
  return 'Yes'+(p>0?' +'+money(p):'');
}
function enhRefreshPills(scope,grp){
  // Re-sync every Yes/No pill in the group with its input — needed after ANY
  // change (card-label taps included), since the None-sync can flip siblings.
  const options=ENH_OPTION_SOURCES[grp]?ENH_OPTION_SOURCES[grp]():[];
  scope.querySelectorAll('input[name="'+grp+'"]').forEach(i=>{
    const c=i.closest('.opt'); if(!c) return;
    const add=c.querySelector('[data-upgrade-add]');
    if(add){
      const option=options.find(o=>o.key===i.value);
      add.textContent=i.checked?'✓ Added':'Add';
      add.setAttribute('aria-pressed',String(i.checked));
      add.setAttribute('aria-label',(i.checked?'Remove ':'Add ')+(option?.label||'upgrade'));
      return;
    }
    const yesBtn=c.querySelector('[data-yn="yes"]'), noBtn=c.querySelector('[data-yn="no"]');
    if(!yesBtn) return;
    const o=options.find(x=>x.key===i.value);
    yesBtn.classList.toggle('is-on',i.checked);
    yesBtn.textContent=enhYnLabel(i,o,grp);
    yesBtn.setAttribute('aria-pressed',String(i.checked));
    if(noBtn){ noBtn.classList.toggle('is-on',!i.checked); noBtn.setAttribute('aria-pressed',String(!i.checked)); }
  });
}
function enhAugmentDesktop(panel){
  if(mobileMQ.matches) return;
  panel.classList.remove('sbd-enh-mobile'); // stale after a mobile->desktop resize
  panel.querySelectorAll('.opt').forEach(card=>{
    const input=card.querySelector('input');
    if(!input||!ENH_SCENES[input.name]) return;
    if(card.classList.contains('sbd-upgrade-card')) return;
    if(input.value==='__none__') return;
    if(card.querySelector('.sbd-enh-info')) return;
    card.classList.add('sbd-has-info');
    const b=document.createElement('button');
    b.type='button';
    b.className='sbd-enh-info';
    b.setAttribute('aria-label','About this option');
    b.textContent='?';
    b.dataset.grp=input.name;
    b.dataset.key=input.value;
    card.appendChild(b); // sibling of the label — inside it, it would activate the card
  });
}
function enhAugmentPanel(panel,grp){
  const on=Boolean(ENH_SCENES[grp]&&isGranularMobile&&mobileMQ.matches);
  // #questionPanel outlives every step, so this class has to come OFF too: left
  // on, every step visited after an enhancement step kept the pills' 46px card
  // reserve with no pills in it (hair: 99px of inner scroll instead of 35).
  panel.classList.toggle('sbd-enh-mobile',on);
  if(!on) return;
  // The "Before you choose this" notes collapse to their title here (sbd-polish.css);
  // the delegated listener below opens them.
  panel.querySelectorAll('.opt-note-title').forEach(title=>{
    title.setAttribute('role','button');
    title.setAttribute('tabindex','0');
    title.setAttribute('aria-expanded','false');
  });
  const options=ENH_OPTION_SOURCES[grp]?ENH_OPTION_SOURCES[grp]():[];
  panel.querySelectorAll('.opt').forEach(card=>{
    const input=card.querySelector('input[name="'+grp+'"]');
    if(!input) return;
    if(card.classList.contains('sbd-upgrade-card')) return;
    if(input.value==='__none__'){ card.classList.add('sbd-opt-none'); return; }
    if(grp==='intimateSystem'&&input.value!=='vaginax') return; // Standard has no price label
    if(card.querySelector('.sbd-yn')) return;
    const opt=options.find(o=>o.key===input.value);
    const row=document.createElement('div');
    row.className='sbd-yn';
    row.innerHTML='<button type="button" class="sbd-yn-no'+(input.checked?'':' is-on')+'" data-yn="no">No</button>'
      +'<button type="button" class="sbd-yn-yes'+(input.checked?' is-on':'')+'" data-yn="yes"></button>';
    row.querySelector('.sbd-yn-yes').textContent=enhYnLabel(input,opt,grp);
    card.appendChild(row);
  });
}
/* Desktop "Feel" shows both close-ups stacked in one column, so each cell is
   roughly half the height of the single mobile stage the table above is tuned
   for. At the mobile zoom levels the hips filled the cell edge to edge and were
   cropped at both sides — the customer was being asked about the shape of
   something they could not see the shape of. These are the same crops pulled
   back far enough to keep the whole region inside the frame with margin around
   it. Deliberately a separate table: retuning BODY_FOCUS_BY_STEP would re-frame
   the mobile stage, which is a different size and is framed correctly already.

   There is a floor on how far these can pull back: each sprite frame is 517px
   wide but the body only occupies the middle ~79% of it (angle 4) / ~83%
   (angle 0), the rest being black padding. The visible width is 1/fz of the
   frame, so below fz≈1.27 (hips) and fz≈1.21 (chest) that padding starts
   showing at the edges. These sit just above their floors — the region fits
   with margin and no more black is exposed than there already was. */
const DESKTOP_FEEL_FOCUS={
  chest:{angle:0,fy:0.30,fz:1.25},
  hips: {angle:4,fy:0.47,fz:1.30},
};
/* Nipple + navel positions per nude sprite (assets/sbd-body-landmarks.json, exported by
   tools/body-landmarks/export.py from the owner-confirmed review). Front-frame pixels
   plus px-per-cm for that render, so the areola discs and piercings below are drawn
   at true size on the body close-ups. Fetched once, on the first close-up. */
let BODY_LANDMARKS=null, bodyLandmarksRequest=null;
// Bump after every `python3 tools/body-landmarks/export.py` — the asset keeps its filename and is
// fetched force-cache, so without a new query a returning browser would keep the old positions/sizes.
const BODY_LANDMARKS_VERSION='20260906b';
function loadBodyLandmarks(){
  if(bodyLandmarksRequest) return bodyLandmarksRequest;
  bodyLandmarksRequest=fetch(new URL(`sbd-body-landmarks.json?v=${BODY_LANDMARKS_VERSION}`,CUSTOMIZER_ASSET_BASE).href,{cache:'force-cache'})
    .then(r=>r.ok?r.json():{}).catch(()=>({}))
    .then(data=>{ BODY_LANDMARKS=data||{}; updatePreview(); return BODY_LANDMARKS; });
  return bodyLandmarksRequest;
}
// One areola per skin tone: Irontech's frontal areola carrying a colour from their
// "Areola color" chart (Natural→Graceful Daisy, Glow→Tender Rosa, Tanned→Misty Rose,
// Dark Tanned→Passion Peony, Black→Mysterious Lilac), baked with its rim matched to that
// tone's skin so it sits seamlessly. Areola = 58% of the square (tools/body-landmarks).
const AREOLA_TEX_FRAC=0.58;
// Bump when the texture files change: the theme CDN caches by filename, and these URLs
// are built here rather than by Liquid's asset_url (which would add its own ?v=).
const AREOLA_TEX_VERSION='20260903i';
// side 'l'/'r' — each breast gets that side's own nipple texture (the two darkest tones are cut
// from real dark-skin photos with a genuine left/right pair; the lighter tones reuse one cut).
function areolaTextureUrl(side){
  const skin=(state.skin&&state.skin.key)||'tanned';
  return new URL(`sbd-areola-skin-${skin}-${side}.png?v=${AREOLA_TEX_VERSION}`,CUSTOMIZER_ASSET_BASE).href;
}
// Steel bead with a highlight, all sizes in frame pixels.
function steelBead(x,y,rad){
  return `<circle cx="${x}" cy="${y}" r="${rad}" fill="#5a5e66" stroke="#2b2e34" stroke-width="${(rad*0.18).toFixed(2)}"/>`+
         `<circle cx="${(x-rad*0.3).toFixed(1)}" cy="${(y-rad*0.35).toFixed(1)}" r="${(rad*0.38).toFixed(2)}" fill="rgba(255,255,255,0.75)"/>`;
}
// A round brilliant seen from the top: steel bezel, octagonal girdle, star facets to a
// small table, and a hard white sparkle.
function gemBead(x,y,rad){
  const P=(r,a)=>`${(x+r*Math.cos(a*Math.PI/180)).toFixed(1)},${(y+r*Math.sin(a*Math.PI/180)).toFixed(1)}`;
  const oct=[...Array(8)].map((_,i)=>P(rad,i*45+22.5)).join(' ');
  const table=[...Array(8)].map((_,i)=>P(rad*0.42,i*45+22.5)).join(' ');
  let s=`<circle cx="${x}" cy="${y}" r="${(rad*1.18).toFixed(2)}" fill="#7e828a" stroke="#3a3d44" stroke-width="${(rad*0.14).toFixed(2)}"/>`;
  s+=`<polygon points="${oct}" fill="#dfe9f7" stroke="#9fb1c9" stroke-width="${(rad*0.08).toFixed(2)}"/>`;
  // facets: alternate light/dark wedges from the girdle to the table
  for(let i=0;i<8;i++){
    const a0=i*45+22.5, a1=a0+45;
    s+=`<polygon points="${P(rad,a0)} ${P(rad,a1)} ${P(rad*0.42,a1)} ${P(rad*0.42,a0)}" fill="${i%2?'rgba(255,255,255,0.55)':'rgba(120,150,190,0.35)'}"/>`;
  }
  s+=`<polygon points="${table}" fill="#f4f8ff" stroke="rgba(150,170,200,0.6)" stroke-width="${(rad*0.06).toFixed(2)}"/>`;
  s+=`<circle cx="${(x-rad*0.28).toFixed(1)}" cy="${(y-rad*0.3).toFixed(1)}" r="${(rad*0.22).toFixed(2)}" fill="#fff"/>`;
  s+=`<path d="M ${x} ${(y-rad*0.95).toFixed(1)} L ${(x+rad*0.12).toFixed(1)} ${(y-rad*0.12).toFixed(1)} L ${(x+rad*0.95).toFixed(1)} ${y} L ${(x+rad*0.12).toFixed(1)} ${(y+rad*0.12).toFixed(1)} L ${x} ${(y+rad*0.95).toFixed(1)} L ${(x-rad*0.12).toFixed(1)} ${(y+rad*0.12).toFixed(1)} L ${(x-rad*0.95).toFixed(1)} ${y} L ${(x-rad*0.12).toFixed(1)} ${(y-rad*0.12).toFixed(1)} Z" fill="rgba(255,255,255,0.85)"/>`;
  return s;
}
// SVG in frame pixels; the .pbf-frame transform zooms it with the sprite.
function bodyOverlayMarkup(stepId,lm){
  const [nL,nR]=lm.n, nav=lm.v, ppc=lm.ppc||9.4;
  const areolaCm=parseFloat(state.areola&&state.areola.key)||4;
  let s='';
  // The chosen areola belongs on every chest close-up, not just the size step — the
  // piercing (and the feel step) must sit on the nipple the customer picked, never on
  // bare skin. Draw it first so the piercing bar layers on top.
  if(stepId==='areola'||stepId==='breastPiercing'||stepId==='breastFeel'){
    // The texture's areola (58% of the square) becomes the chosen size, at this render's own
    // px/cm — so a 4 cm choice measures 4 cm on her body and 5 cm is visibly 67% wider than 3.
    //
    // TRUE SCALE, nothing else (2026-09-06, owner's call). Two fudges used to sit here and both
    // broke that: a `bustScale` of 0.86/1.16 by breast size, and `lm.sc`, the per-frame
    // correction from the review app. `sc` matched the overlay to the nipple painted into each
    // AI render, and those are ~0.6x of Irontech's real 3-5 cm options — so the whole 3-5 cm
    // range drew smaller than a true 3 cm and the label stopped meaning anything. The `sc`
    // values are still exported in sbd-body-landmarks.json (they record how big each render
    // paints its own nipple); nothing reads them. Re-apply both here to go back.
    const size=areolaCm*ppc/AREOLA_TEX_FRAC;
    [['l',nL],['r',nR]].forEach(([side,[x,y]])=>{
      s+=`<image href="${areolaTextureUrl(side)}" x="${(x-size/2).toFixed(1)}" y="${(y-size/2).toFixed(1)}" width="${size.toFixed(1)}" height="${size.toFixed(1)}"/>`;
    });
  }
  if(stepId==='breastPiercing'&&state.breastPiercing&&state.breastPiercing.key==='on'){
    // Irontech's nipple bar: a short straight barbell through the nipple tip, ~1.5 cm bead to
    // bead. `pd` is the per-side offset from that nipple and `pcm` the bar's length, both placed
    // frame by frame in the review app against the FINISHED breast (sprite + the chosen areola),
    // because the tip is not always the centre of the areola in an AI render. Absent = dead
    // centre at 1.44 cm, which is what every frame drew before the placement pass.
    const pd=lm.pd||[[0,0],[0,0]];
    const half=ppc*(lm.pcm||1.44)/2, bar=ppc*0.13, bead=ppc*0.19;
    [nL,nR].forEach(([nx,ny],i)=>{
      const x=nx+((pd[i]&&pd[i][0])||0), y=ny+((pd[i]&&pd[i][1])||0);
      s+=`<line x1="${x-half}" y1="${y+0.6}" x2="${x+half}" y2="${y+0.6}" stroke="rgba(0,0,0,0.4)" stroke-width="${bar+0.8}" stroke-linecap="round"/>`;
      s+=`<line x1="${x-half}" y1="${y}" x2="${x+half}" y2="${y}" stroke="#9a9ea6" stroke-width="${bar}" stroke-linecap="round"/>`;
      s+=steelBead(x-half,y,bead)+steelBead(x+half,y,bead);
    });
  }
  if(stepId==='navel'&&state.navel&&state.navel.key!=='none'){
    // Both styles are curved barbells: a small bead ~1.2 cm above the navel and the
    // main bead resting on the navel's upper rim. Style 1 = steel bead, Style 2 = gem.
    const [x,y]=nav, top=y-ppc*1.25, bot=y-ppc*0.2, bar=ppc*0.12;
    s+=`<path d="M ${x} ${top} Q ${(x-ppc*0.35).toFixed(1)} ${((top+bot)/2).toFixed(1)} ${x} ${bot}" fill="none" stroke="rgba(0,0,0,0.35)" stroke-width="${bar+0.8}" stroke-linecap="round"/>`;
    s+=`<path d="M ${x} ${top} Q ${(x-ppc*0.35).toFixed(1)} ${((top+bot)/2).toFixed(1)} ${x} ${bot}" fill="none" stroke="#9a9ea6" stroke-width="${bar}" stroke-linecap="round"/>`;
    s+=steelBead(x,top,ppc*0.18);
    s+=state.navel.key==='style2'?gemBead(x,bot,ppc*0.3):steelBead(x,bot,ppc*0.32);
  }
  return s;
}
function paintBodyOverlay(focusEl,stepId,asset){
  const svg=focusEl.querySelector('.pbf-overlay');
  if(!svg) return;
  const file=asset&&asset.sprite;
  const lm=BODY_LANDMARKS&&file?BODY_LANDMARKS[file]:null;
  if(!BODY_LANDMARKS) loadBodyLandmarks();
  // SVG elements have no `hidden` IDL property — toggle the attribute itself.
  if(!lm||!lm.n||!lm.v){ svg.innerHTML=''; svg.setAttribute('hidden',''); return; }
  svg.setAttribute('viewBox',`0 0 ${asset.width||593} ${asset.height||1600}`);
  const markup=bodyOverlayMarkup(stepId,lm);
  svg.innerHTML=markup;
  svg.toggleAttribute('hidden',!markup);
}
/* DESKTOP FEEL groups breast + butt in one step, so both close-ups show at once
   (mobile splits them into separate steps). One stacked cell per region, each
   reusing the 8-frame sprite + .pbf-frame zoom + overlay. Built once, lazily. */
function ensureBodyDuo(){
  let duo=document.getElementById('previewBodyDuo');
  if(duo) return duo;
  const figure=document.querySelector('.preview-figure');
  if(!figure) return null;
  duo=document.createElement('div');
  duo.id='previewBodyDuo';
  duo.className='preview-body-duo';
  duo.hidden=true;
  duo.setAttribute('aria-hidden','true');
  duo.innerHTML=
    '<div class="duo-cell" data-region="chest"><div class="pbf-frame"><img class="pbf-img" alt="" decoding="async"><svg class="pbf-overlay" aria-hidden="true" hidden></svg></div><span class="duo-tag">Breasts</span></div>'+
    '<div class="duo-cell" data-region="hips"><div class="pbf-frame"><img class="pbf-img" alt="" decoding="async"><svg class="pbf-overlay" aria-hidden="true" hidden></svg></div><span class="duo-tag">Butt</span></div>';
  figure.appendChild(duo);
  return duo;
}
function paintDuoCell(cell,sprite,asset,spec,overlayStepId){
  if(!cell) return;
  const img=cell.querySelector('.pbf-img');
  const frame=cell.querySelector('.pbf-frame');
  if(img&&img.getAttribute('src')!==sprite) img.setAttribute('src',sprite);
  if(img) img.style.setProperty('--body-frame-offset',`${-spec.angle*12.5}%`);
  if(frame){
    frame.style.setProperty('--body-frame-ratio',`${asset&&asset.width||593}/${asset&&asset.height||1600}`);
    frame.style.setProperty('--fy',String(spec.fy));
    frame.style.setProperty('--fz',String(spec.fz));
  }
  paintBodyOverlay(cell,overlayStepId,asset);
}
/* Review-step body turntable inside the mobile stage. Reuses bodyAngleIndex and
   the 8-frame sprite (12.5% per frame) so the Body studio and this view agree. */
function paintPreviewBodyStage(){
  const stageImg=document.getElementById('previewBodyStageImg');
  const angleEl=document.getElementById('previewBodyStageAngle');
  if(!stageImg||!state.bodyModel) return;
  const angle=BODY_ANGLES[bodyAngleIndex];
  const asset=bodyAssetData(state.bodyModel);
  const viewport=stageImg.closest('.pbs-viewport');
  if(viewport) viewport.style.setProperty('--body-frame-ratio',`${asset?.width||520}/${asset?.height||1600}`);
  stageImg.style.setProperty('--body-frame-offset',`${-bodyAngleIndex*12.5}%`);
  stageImg.alt=`${state.bodyModel.label}, ${angle.label.toLowerCase()} view`;
  if(angleEl) angleEl.textContent=angle.label;
}
(function wirePreviewBodyStage(){
  const stage=document.getElementById('previewBodyStage');
  if(!stage) return;
  stage.addEventListener('click',event=>{
    const rotate=event.target.closest('[data-pbs-rotate]');
    if(!rotate) return;
    bodyAngleIndex=(bodyAngleIndex+Number(rotate.dataset.pbsRotate)+BODY_ANGLES.length)%BODY_ANGLES.length;
    paintPreviewBodyStage();
  });
  const viewport=stage.querySelector('.pbs-viewport');
  if(!viewport) return;
  let dragFrom=null;
  viewport.addEventListener('pointerdown',event=>{
    if(event.button) return;
    dragFrom=event.clientX;
    viewport.setPointerCapture(event.pointerId);
    viewport.classList.add('is-dragging');
  });
  viewport.addEventListener('pointermove',event=>{
    if(dragFrom===null) return;
    const steps=Math.trunc((event.clientX-dragFrom)/28);
    if(!steps) return;
    bodyAngleIndex=(bodyAngleIndex-steps+BODY_ANGLES.length*4)%BODY_ANGLES.length;
    dragFrom+=steps*28;
    paintPreviewBodyStage();
  });
  const release=()=>{ dragFrom=null; viewport.classList.remove('is-dragging'); };
  viewport.addEventListener('pointerup',release);
  viewport.addEventListener('pointercancel',release);
})();
/* A hair thumbnail whose render is missing for the preview eye colour
   (HAIR_THUMB_EYE) falls back to eye set 01 once, instead of a blank tile. */
document.addEventListener('error',event=>{
  const img=event.target;
  if(!(img instanceof HTMLImageElement)||!img.classList.contains('opt-photo')||img.dataset.sbdRetried) return;
  const src=img.currentSrc||img.src||'';
  if(!/\/eyes_\d+\//.test(src)||/eyes_01\//.test(src)||/%E5%AE%9A%E5%88%B6|定制/.test(src)) return;
  img.dataset.sbdRetried='1';
  img.removeAttribute('srcset');
  img.src=src.replace(/\/eyes_\d+\//,'/eyes_01/');
},true);
/* ============================================================
   SBD ANALYTICS — customizer funnel events
   Emitted through window.sbdTrack, defined in snippets/sbd-analytics.liquid.
   That helper runs in the TOP FRAME, so these events carry the same GA4
   client_id as the rest of the session. They are deliberately NOT routed
   through a custom pixel: pixels run in an opaque-origin sandbox that cannot
   read the _ga cookie, so every event would land as a new anonymous user.

   Every call is wrapped — tracking must never be able to break the build flow.
   ============================================================ */
let sbdStarted=false, sbdLastCompleteSig=null;
let sbdActiveStep=null, sbdSuspended=false, sbdReviewReached=false;
let sbdVisibilityTimer=null, sbdExplicitlyClosed=false;
function sbdEmitEvent(name,params){
  try{ if(typeof window.sbdTrack==='function') window.sbdTrack(name,Object.assign({tracking_version:'2'},params||{})); }catch(e){}
}
function sbdStepId(){ try{ return (STEPS[stepIndex]&&STEPS[stepIndex].id)||null; }catch(e){ return null; } }
function sbdTotal(){ try{ const t=total(); return Number.isFinite(t)?t:null; }catch(e){ return null; } }
function sbdDevice(){ try{ return matchMedia('(max-width:899px)').matches?'mobile':'desktop'; }catch(e){ return 'unknown'; } }
function sbdEntrySurface(){
  /* Deliberately read from the URL, not a body class: this theme has no
     generic template-* body class and must not grow one. */
  try{ return location.pathname.indexOf('/products/')===0?'pdp':'page'; }catch(e){ return 'unknown'; }
}
function sbdConfigSignature(){
  try{ return JSON.stringify(serializeBuild()); }catch(e){ return String(Date.now()); }
}
function sbdHash(str){
  let h=5381;
  for(let i=0;i<str.length;i++) h=((h*33)^str.charCodeAt(i))>>>0;
  return h.toString(36);
}
function sbdStartOnce(){
  if(sbdStarted||!sbdCustomizerVisible()) return;
  sbdStarted=true;
  sbdCaptureEntry();
  saveBuild();
  sbdEmitEvent('customizer_start',{
    entry_surface:sbdEntrySurface(),
    entry_source:sbdBuildAttribution.latest.source,
    entry_product:sbdBuildAttribution.latest.productHandle,
    origin_source:sbdBuildAttribution.origin.source,
    flow:isGranularMobile?'granular':'grouped',
    device:sbdDevice(),
    step:sbdStepId(),
    running_total:sbdTotal()
  });
}
// A render is not a visit: PDPs render while collapsed and the homepage
// studio starts below the fold. Only count an open studio in a visible tab
// when it intersects the viewport. No customer identifiers are added.
function sbdCustomizerVisible(){
  try{
    if(sbdExplicitlyClosed||document.visibilityState==='hidden'||customizerCollapsed()) return false;
    const root=document.getElementById('atelier-landing');
    if(!root||!root.getClientRects().length) return false;
    const rect=root.getBoundingClientRect();
    return rect.bottom>0&&rect.top<window.innerHeight&&rect.right>0&&rect.left<window.innerWidth;
  }catch(e){ return false; }
}
function sbdStepParams(){
  return {
    step:sbdStepId(),
    step_number:stepIndex+1,
    step_count:STEPS.length,
    flow:isGranularMobile?'granular':'grouped',
    device:sbdDevice()
  };
}
function sbdTrackStepView(){
  try{
    if(!sbdCustomizerVisible()) return;
    sbdStartOnce();
    const next=sbdStepParams();
    const previous=sbdActiveStep;
    const same=previous&&previous.step===next.step&&previous.flow===next.flow;
    const resumed=sbdSuspended;
    if(resumed){
      sbdEmitEvent('customizer_resume',Object.assign({},next,{review_reached:sbdReviewReached?'yes':'no'}));
      sbdSuspended=false;
    }
    if(!same){
      const direction=!previous?'enter':previous.flow!==next.flow?'flow_change':
        next.step_number>previous.step_number?'forward':'back';
      if(previous){
        sbdEmitEvent('customizer_step_transition',Object.assign({},previous,{
          next_step:next.step,next_flow:next.flow,direction:direction
        }));
      }
      sbdActiveStep=next;
      sbdEmitEvent('customizer_step_view',Object.assign({},next,{direction:direction}));
    }else if(resumed){
      sbdEmitEvent('customizer_step_view',Object.assign({},next,{direction:'resume'}));
    }
    // Review can also render while hidden. Completion belongs to a visible
    // Review visit, and must follow the start/step events in the same flow.
    if(next.step==='review') sbdTrackComplete(sbdTotal());
  }catch(e){ /* analytics must never prevent a step rendering */ }
}
function sbdTrackExit(reason){
  try{
    if(!sbdActiveStep||sbdSuspended) return;
    sbdEmitEvent('customizer_exit',Object.assign({},sbdActiveStep,{
      exit_reason:reason,review_reached:sbdReviewReached?'yes':'no'
    }));
    sbdSuspended=true;
    if(reason==='close') sbdActiveStep=null;
  }catch(e){}
}
function sbdHandleInteraction(event){
  try{
    const target=event.target;
    if(!target||!target.closest) return;
    if(target.closest('.sbd-studio-close')){
      sbdTrackExit('close');
      sbdExplicitlyClosed=true;
      return;
    }
    // Inline/homepage Close can leave the studio on screen. Wait for an
    // explicit reopening or interaction, rather than treating the next
    // scroll/observer callback as a return.
    if(target.closest('#atelier-landing, [data-sbd-customize-her], [data-sbd-step], a[href="#configurator"]')){
      sbdExplicitlyClosed=false;
      sbdScheduleVisibilityCheck();
    }
  }catch(e){}
}
function sbdCheckVisibility(){
  if(customizerCollapsed()) sbdTrackExit('close');
  else sbdTrackStepView();
}
function sbdScheduleVisibilityCheck(){
  if(sbdVisibilityTimer!==null) return;
  sbdVisibilityTimer=setTimeout(()=>{sbdVisibilityTimer=null;sbdCheckVisibility();},0);
}
(function sbdWatchVisibility(){
  try{
    const root=document.getElementById('atelier-landing');
    if(!root) return;
    if(window.MutationObserver){
      new MutationObserver(sbdScheduleVisibilityCheck).observe(root,{attributes:true,attributeFilter:['class']});
    }
    if(window.IntersectionObserver){
      new IntersectionObserver(sbdScheduleVisibilityCheck).observe(root);
    }
    window.addEventListener('scroll',sbdScheduleVisibilityCheck,{passive:true});
    window.addEventListener('resize',sbdScheduleVisibilityCheck,{passive:true});
    window.addEventListener('pageshow',sbdScheduleVisibilityCheck);
    document.addEventListener('click',sbdHandleInteraction,true);
    document.addEventListener('sbd:customizer-open',()=>{sbdExplicitlyClosed=false;sbdScheduleVisibilityCheck();});
    // hidden can mean tab/app switching, not final abandonment. Keep the
    // reason explicit and emit resume on return; reports must not equate
    // every exit event with a distinct permanently lost customer.
    document.addEventListener('visibilitychange',()=>{
      if(document.visibilityState==='hidden') sbdTrackExit('page_hidden');
      else sbdScheduleVisibilityCheck();
    });
    window.addEventListener('pagehide',()=>sbdTrackExit('pagehide'));
  }catch(e){}
})();
function sbdTrackComplete(t){
  if(!sbdCustomizerVisible()||sbdStepId()!=='review') return;
  sbdReviewReached=true;
  const sig=sbdConfigSignature();
  if(sig===sbdLastCompleteSig) return;  // Review re-renders on every edit return
  sbdLastCompleteSig=sig;
  let paid=null;
  try{ paid=Math.max(0,priceRows().length-1); }catch(e){}
  sbdEmitEvent('customizer_complete',Object.assign(sbdStepParams(),{
    configuration_id:sbdHash(sig),
    running_total:Number.isFinite(t)?t:sbdTotal(),
    n_paid_options:paid,
    device:sbdDevice()
  }));
}
function sbdTrackSaveDesign(outcome,extra){
  /* PII RULE: never send the email address or the companion name. Only the
     fact of the save, the marketing opt-in flag, and the build identity. */
  const params=Object.assign({
    step:sbdStepId(),
    running_total:sbdTotal(),
    configuration_id:sbdHash(sbdConfigSignature()),
    device:sbdDevice()
  },extra||{});
  sbdEmitEvent(outcome,params);
}
/* Selections reach state through seven separate wireChange handlers plus
   data-attribute button clicks (body model, body skin), so there is no single
   handler to hook. One delegated capture-phase listener on the question panel
   covers all of them. The emit is deferred a tick so the step's own handler
   has already applied state and repriced — running_total is then the number
   the customer is actually looking at. */
function sbdEmitSelect(group,optionId,label){
  const before=sbdTotal();
  setTimeout(()=>{
    const after=sbdTotal();
    sbdStartOnce();
    sbdEmitEvent('customizer_option_select',{
      step:sbdStepId(),
      step_number:stepIndex+1,
      option_group:group,
      option_id:optionId,
      option_label:label||null,
      price_delta:(before!==null&&after!==null)?Math.round((after-before)*100)/100:null,
      running_total:after,
      device:sbdDevice()
    });
  },0);
}
document.addEventListener('change',event=>{
  try{
    const input=event.target;
    if(!input||!input.closest||!input.closest('#questionPanel')) return;
    if(input.type!=='radio'&&input.type!=='checkbox') return;
    if(input.type==='checkbox'&&!input.name) return;
    const card=input.closest('.opt');
    const label=card?(card.querySelector('.opt-name')||{}).textContent:null;
    sbdEmitSelect(input.name||'unknown',input.value||'',(label||'').trim()||null);
  }catch(e){}
},true);
document.addEventListener('click',event=>{
  try{
    const target=event.target;
    if(!target||!target.closest||!target.closest('#questionPanel')) return;
    const bodyCard=target.closest('[data-body-id]');
    if(bodyCard){
      const id=decodeURIComponent(bodyCard.dataset.bodyId||'');
      const name=(bodyCard.querySelector('strong')||{}).textContent;
      sbdEmitSelect('bodyModel',id,(name||'').trim()||null);
      return;
    }
    const skinPill=target.closest('[data-body-skin]');
    if(skinPill){
      const name=(skinPill.querySelector('.body-skin-name')||{}).textContent;
      sbdEmitSelect('skin',skinPill.dataset.bodySkin||'',(name||'').trim()||null);
    }
  }catch(e){}
},true);
/* "Hear it" — registered ONCE, on document, for the life of the page.
   It must not be registered inside a render function: renderAll() paints into
   the same #questionPanel node every time, so a per-render listener accumulates
   and each duplicate re-toggles the same click (play, then stop, then play…),
   which is what left the button doing nothing. Delegation also survives the
   innerHTML swap that replaces the button element on every render.
   preventDefault matters: the button is a sibling of the option's <label>, and
   a click that lands slightly off it should not toggle the option while someone
   is reaching for play. */
document.addEventListener('click',function(event){
  try{
    const target=event.target;
    if(!target||!target.closest) return;
    const btn=target.closest('.opt-audio');
    if(!btn||!btn.closest('#questionPanel')) return;
    event.preventDefault();
    toggleSample(btn);
  }catch(e){}
});
/* Option-card clips are preload="none" and never autoplay, so a collapsed
   customizer downloads no video at all. This observer starts one only while
   its card is actually on screen, and stops it as soon as it leaves. */
/* A poster for every option clip. These cards have no separate image — the
   clip IS the card's picture — so without one, a card that has not started
   playing yet is a black tile. Keyed by the video's own id/filename. */
const OPT_POSTERS={
  'feat-head-movement':'sbd-optposter-98b8c686ebfa45c8b7b37bc2ed40d76e.jpg',
  'hip-movement':'sbd-optposter-0149a7dac6e544f9a47fbc63a332b00d.jpg',
  'suckingvagina-loop':'sbd-optposter-278b6700f06647f8973ee11811f13295.jpg',
  '98b8c686ebfa45c8b7b37bc2ed40d76e':'sbd-optposter-98b8c686ebfa45c8b7b37bc2ed40d76e.jpg',
  '278b6700f06647f8973ee11811f13295':'sbd-optposter-278b6700f06647f8973ee11811f13295.jpg',
  '0149a7dac6e544f9a47fbc63a332b00d':'sbd-optposter-0149a7dac6e544f9a47fbc63a332b00d.jpg',
  'feat-oral-suction':'sbd-optposter-feat-oral-suction.jpg'
};
function optPosterAttr(url){
  try{
    // A poster attribute downloads even with preload="none", so it waits with the rest.
    if(!url||custDeferImages) return '';
    for(const key in OPT_POSTERS){
      if(url.indexOf(key)!==-1) return ' poster="'+new URL(OPT_POSTERS[key],CUSTOMIZER_ASSET_BASE).href+'"';
    }
  }catch(e){}
  return '';
}
let optVideoIO=null;
function watchOptionVideos(){
  try{
    if(!window.IntersectionObserver) return;
    if(!optVideoIO){
      optVideoIO=new IntersectionObserver(function(entries){
        entries.forEach(function(entry){
          const v=entry.target;
          if(entry.isIntersecting){ const p=v.play(); if(p&&p.catch) p.catch(function(){}); }
          else v.pause();
        });
      },{rootMargin:'100px'});
    }
    document.querySelectorAll('#atelier-landing video.opt-vmedia:not([data-optio]),#atelier-landing video.opt-video:not([data-optio])')
      .forEach(function(v){
        v.setAttribute('data-optio','1');
        optVideoIO.observe(v);
        // These cards have no poster: the clip IS the card's picture, so a card
        // that never starts is a black tile. Don't wait on the observer for the
        // initial state — if the card is on screen right now, start it here.
        if(v.offsetParent!==null){
          const r=v.getBoundingClientRect();
          if(r.bottom>0&&r.top<innerHeight&&r.width>0){ const p=v.play(); if(p&&p.catch) p.catch(function(){}); }
        }
      });
  }catch(e){}
}

/* Card-body taps on pill-carrying enhancement cards PREVIEW the option on
   the stage instead of toggling it — only the Yes/No pills change the build.
   preventDefault stops the <label> from activating its input. */
document.addEventListener('click',function(event){
  try{
    const t=event.target;
    if(!t||!t.closest) return;
    if(t.closest('.sbd-yn')||t.closest('.opt-audio')) return;
    const card=t.closest('.opt');
    if(!card||!card.closest('.sbd-enh-mobile')||!card.closest('#questionPanel')) return;
    if(card.classList.contains('sbd-opt-none')) return;
    if(!card.querySelector('.sbd-yn')) return;
    const input=card.querySelector('input');
    if(!input) return;
    event.preventDefault();
    enhPreviewKey[input.name]=input.value;
    renderEnhStage();
  }catch(e){}
});
/* "Before you choose this" notes collapse to their title on the mobile
   enhancement steps, so the two cards and their Yes/No pills fit a real phone
   (owner report 2026-09-14). The title sits inside the card's <label>: capture
   phase + preventDefault + stopPropagation keep a tap on it from selecting the
   card or previewing it — it only opens or closes the note. One document-level
   listener pair, like the pills; never registered from a render. */
function toggleEnhNote(title){
  const note=title.closest('.opt-note');
  if(!note) return;
  const open=note.classList.toggle('is-open');
  title.setAttribute('aria-expanded',open?'true':'false');
}
function enhNoteTitleFrom(event){
  const t=event.target;
  if(!t||!t.closest) return null;
  const title=t.closest('.sbd-enh-mobile .opt-note-title');
  return title&&title.closest('#questionPanel')?title:null;
}
document.addEventListener('click',function(event){
  try{
    const title=enhNoteTitleFrom(event);
    if(!title) return;
    event.preventDefault();
    event.stopPropagation();
    toggleEnhNote(title);
  }catch(e){}
},true);
document.addEventListener('keydown',function(event){
  try{
    if(event.key!=='Enter'&&event.key!==' ') return;
    const title=enhNoteTitleFrom(event);
    if(!title) return;
    event.preventDefault();
    event.stopPropagation();
    toggleEnhNote(title);
  }catch(e){}
},true);
/* Desktop "?" info buttons + the scene's close button — the scene is a
   spotlight the customer opens and closes; it never changes the build. */
document.addEventListener('click',function(event){
  try{
    const t=event.target;
    if(!t||!t.closest) return;
    const info=t.closest('.sbd-enh-info');
    if(info&&info.closest('#questionPanel')){
      event.preventDefault();
      const same=enhDesktopKey&&enhDesktopKey.grp===info.dataset.grp&&enhDesktopKey.key===info.dataset.key;
      enhDesktopKey=same?null:{grp:info.dataset.grp,key:info.dataset.key};
      renderEnhStage();
      return;
    }
    if(t.closest('.ses-close')){ enhDesktopKey=null; renderEnhStage(); }
  }catch(e){}
});
/* Yes/No pill delegation — same survival rules as .opt-audio above: one
   document-level listener, never re-registered by renderAll's innerHTML swaps.
   The pills only proxy the card's own input and dispatch a bubbling `change`,
   so wireChange's handler does all state, price and cart work. */
document.addEventListener('click',function(event){
  try{
    const target=event.target;
    if(!target||!target.closest) return;
    const add=target.closest('[data-upgrade-add]');
    if(add&&add.closest('#questionPanel')){
      event.preventDefault();
      const input=add.closest('.opt')?.querySelector('input');
      if(!input||input.disabled) return;
      delete enhPreviewKey[input.name];
      input.checked=!input.checked;
      if(input.checked) enhLastPick[input.name]=input.value;
      input.dispatchEvent(new Event('change',{bubbles:true}));
      return;
    }
    const btn=target.closest('.sbd-yn button');
    if(!btn||!btn.closest('#questionPanel')) return;
    event.preventDefault();
    const card=btn.closest('.opt'); if(!card) return;
    const input=card.querySelector('input'); if(!input) return;
    const grp=input.name;
    delete enhPreviewKey[grp];
    const yes=btn.dataset.yn==='yes';
    if(input.type==='radio'){
      // Single-select: No on the upgrade selects the included base build.
      const targetVal=yes?input.value:'standard';
      const targetInput=document.querySelector('#questionPanel input[name="'+grp+'"][value="'+targetVal+'"]')||input;
      enhLastPick[grp]=targetVal;
      if(!targetInput.checked){
        targetInput.checked=true;
        targetInput.dispatchEvent(new Event('change',{bubbles:true}));
      } else renderEnhStage();
      return; // the radio path re-renders the whole step, pills included
    }
    if(yes&&!input.checked){
      enhLastPick[grp]=input.value;
      input.checked=true;
      input.dispatchEvent(new Event('change',{bubbles:true}));
    } else if(!yes&&input.checked){
      input.checked=false;
      input.dispatchEvent(new Event('change',{bubbles:true}));
    } else if(yes){
      enhLastPick[grp]=input.value;
      renderEnhStage();
    }
    enhRefreshPills(btn.closest('#questionPanel'),grp);
  }catch(e){}
});

function renderAll(){
  stopBodyAutoRotation();
  document.querySelectorAll('#questionPanel .sbd-upgrade-demo video').forEach(video=>video.pause());
  renderStepper();
  const panel=document.getElementById('questionPanel');
  panel.setAttribute('tabindex','-1');
  panel.setAttribute('role','region');
  panel.setAttribute('aria-labelledby','cfgHeading');
  // The review step uses the SAME two-column layout as every other step —
  // character/preview on the left, info on the right — so the special "reveal"
  // treatment is intentionally not applied.
  const grid=document.getElementById('cfgGrid');
  grid.classList.toggle('is-review',STEPS[stepIndex].id==='review');
  const root=document.getElementById('atelier-landing');
  if(root){
    root.dataset.activeMobileFlow=isGranularMobile?'granular':'grouped';
    root.dataset.activeStep=STEPS[stepIndex].id;
  }
  const bodyStep=STEPS[stepIndex].id==='body';
  grid.classList.toggle('is-body-studio',bodyStep);
  const angleComparison=document.getElementById('bodyAngleComparison');
  if(angleComparison) angleComparison.hidden=!bodyStep;
  document.querySelector('.preview-panel').classList.remove('is-reveal');
  teardownLookZoom();
  if(STEPS[stepIndex].id!=='makeup'){
    makeupRequestSerial++;
    document.getElementById('previewGenerating')?.classList.remove('is-visible');
    hidePreviewRing('makeup');
  }
  stopSample();   // the button is about to be replaced; don't leave it playing
  // Every step starts without the enhancement-card layout; enhAugmentPanel puts
  // it back on the steps that have Yes/No pills. Steps that never call it
  // (makeup, face, skin) otherwise inherited its 46px card reserve.
  panel.classList.remove('sbd-enh-mobile');
  STEPS[stepIndex].render(panel);
  sbdTrackStepView();
  // The tiles on screen the moment the step paints are the first few AND the
  // ones around the current selection (mobile shelves open centred on it);
  // lazy loading only cost them a round trip and they popped in seconds late.
  const lazyTiles=[...panel.querySelectorAll('img.opt-photo[loading="lazy"]')];
  const selectedTile=lazyTiles.findIndex(img=>img.closest('.opt')?.querySelector('input:checked'));
  lazyTiles.forEach((img,i)=>{
    const nearSelected=selectedTile>=0&&Math.abs(i-selectedTile)<=4;
    if(i>=8&&!nearSelected) return;
    img.loading='eager';
    img.setAttribute('fetchpriority',(i<4||(nearSelected&&Math.abs(i-selectedTile)<=1))?'high':'auto');
  });
  if(mobileMQ.matches){
    panel.querySelectorAll('.opt-grid.cols-6, .opt-grid.hscroll').forEach(gridElement=>{
      const selected=gridElement.querySelector('input:checked');
      const card=selected&&selected.closest('.opt');
      if(!card) return;
      const gridRect=gridElement.getBoundingClientRect();
      const cardRect=card.getBoundingClientRect();
      gridElement.scrollLeft=Math.max(0,(cardRect.left-gridRect.left)+gridElement.scrollLeft-(gridRect.width-cardRect.width)/2);
    });
  }
  panel.querySelectorAll('.hwrap').forEach(wrapper=>{
    const gridElement=wrapper.querySelector('.opt-grid');
    if(!gridElement) return;
    const paint=()=>{
      wrapper.classList.toggle('can-scroll',gridElement.scrollWidth>gridElement.clientWidth+8);
      wrapper.classList.toggle('at-end',gridElement.scrollLeft>=gridElement.scrollWidth-gridElement.clientWidth-16);
    };
    gridElement.addEventListener('scroll',paint,{passive:true});
    paint();
  });
  if(!reduceMotion){ panel.classList.remove('q-anim'); void panel.offsetWidth; panel.classList.add('q-anim'); }
  updatePrice();
  updatePreview();
  renderEnhStage(); // belt-and-braces: never carry a feature scene across steps
  watchOptionVideos();
  const backBtn=document.getElementById('sbBack');
  backBtn.disabled=stepIndex===0;
  const nextBtn=document.getElementById('sbNext');
  const isReview=stepIndex===STEPS.length-1;
  nextBtn.textContent=isReview?'Reserve her':(bodyStep&&!mobileMQ.matches?'Continue with this body':'Continue');
  nextBtn.setAttribute('aria-label',isReview?'Reserve her':(bodyStep?'Continue with selected body':'Continue to next step'));
}

/* helper: build a radio group of option cards */
// A real <fieldset>/<legend> gives assistive tech proper group semantics — e.g.
// VoiceOver/NVDA announce "Body size, group" on entry — which a plain <div> label
// next to the options never did (confirmed via Playwright's ariaSnapshot: the old
// markup exposed the label as disconnected loose text, not tied to the controls).
// When there's no visible label (the step's own heading already says it, e.g.
// "Choose her face"), the legend still exists for AT but is visually hidden rather
// than duplicating text sighted users already see.
function groupWrap(legendText,innerHtml,visibleLabel){
  const legendClass=visibleLabel?'group-label':'group-label visually-hidden';
  return `<fieldset class="group"><legend class="${legendClass}">${legendText}</legend>${innerHtml}</fieldset>`;
}
function hscrollWrap(cols,html){
  return /hscroll|cols-6/.test(cols||'')?`<div class="hwrap">${html}</div>`:html;
}
function radioGroup(name,options,selectedKey,cols,photoAspect,imageOnly){
  // If ANY option here uses a photo, the non-photo options get a same-size
  // placeholder tile so every card on the row matches in size.
  const hasPhoto=options.some(o=>o.img);
  // One tile box for the whole grid — density comes from `cols`, shape from
  // `photoAspect`, so this stays correct if a group changes its column count.
  // `probe` builds the same markup with no src/srcset, for tileBox() to measure
  // without fetching anything; the real pass then asks the CDN for that box.
  const build=(tw,th,probe)=>hscrollWrap(cols,`<div class="opt-grid ${cols||''}">`+options.map(o=>{
    const checked=o.key===selectedKey;
    // Absolute (a full base price, e.g. body size) vs. delta (an upgrade over an
    // included default, e.g. gel feel) read very differently — "+$1,990" implies
    // "on top of some base" when there isn't one, so only deltas get a "+".
    const amount=o.absolute?configuredBodyPrice():optionPrice(name,o);
    const priceLine=amount>0&&(('price' in o)||o.billable)?`<div class="opt-price">${o.absolute?'':'+'}${money(amount)}</div>`:'';
    const tagLine=o.tag?`<span class="opt-tag">${o.tag}</span>`:'';
    // o.swatchStyle renders the tile as a pure colour chip at full photo size —
    // used by the skin-tone group, where a portrait made every card a face
    // comparison rather than a colour one. Checked before o.img so a group can
    // carry both and choose per render.
    // Otherwise: a real photo (o.img) replaces the CSS swatch entirely, and
    // o.noSwatch renders no media placeholder at all (the Yes/No tan-line group).
    const media=o.swatchStyle
      ?`<div class="opt-photo opt-tone ${photoAspect||''}" style="${o.swatchStyle}" role="img" aria-label="${o.alt||o.label}"></div>`
      :(o.img
        ?`<img class="opt-photo${o.contain?' opt-contain':''} ${photoAspect||''}" ${probe?`width="${tw}" height="${th}"`:imgAttrs(o.img,tw,th,{fit:o.contain?'contain':'cover'})} alt="${o.alt||o.label}" loading="lazy" decoding="async">`
        :(hasPhoto
            ?`<div class="opt-photo opt-photo-ph ${o.sw||'sw-none'} ${photoAspect||''}" aria-hidden="true"></div>`
            :(o.noSwatch?'':`<div class="opt-swatch ${o.sw||''}" aria-hidden="true"></div>`)));
    // imageOnly (fingernails): the photo IS the whole card — no visible name/desc/price.
    // A visually-hidden label keeps each radio named for assistive tech.
    const body=imageOnly
      ? `<span class="visually-hidden">${o.label}</span>`
      : `${tagLine}<div class="opt-name">${o.label}</div>${o.desc?`<div class="opt-desc">${o.desc}</div>`:''}${priceLine}${o.extra||''}`;
    return `<div class="opt${imageOnly?' opt-imageonly':''}">
      <input type="radio" name="${name}" id="${name}-${o.key}" value="${o.key}" ${checked?'checked':''}>
      <label for="${name}-${o.key}">
        <span class="opt-check" aria-hidden="true">✓</span>
        ${media}
        ${body}
      </label>
    </div>`;
  }).join('')+`</div>`);
  // Desktop column math first, so the probe lays out at a plausible size and
  // so this still returns something sane before the panel exists (and in
  // concept.html, which has no #questionPanel to probe).
  let tw=tileWidth(cols), th=tileHeight(tw,photoAspect);
  const box=tileBox(`${name}|${cols||''}|${photoAspect||''}`,()=>build(tw,th,true));
  // Width only, in CSS pixels — imgAttrs turns it into 1x and 2x candidates on
  // its own. The SHAPE still comes from tileHeight(): an srcless probe img has
  // no intrinsic ratio to fall back on, and one grid (fingernails) measured
  // 87x112 in the probe against a real 87x87, which would have asked the CDN to
  // crop to the wrong box.
  if(box){ tw=ceilRung(box[0]); th=tileHeight(tw,photoAspect); }
  return build(tw,th,false);
}
function checkGroup(name,options,selectedSet){
  return `<div class="opt-grid rows">`+options.map(o=>{
    const checked=selectedSet.has(o.key);
    const amount=optionPrice(name,o);
    // An option can supply a looping video (o.video) that sits in the swatch square
    // instead of the flat orange colour block — used by Hip movement.
    const media=o.video
      ?`<video class="opt-swatch opt-video" src="${o.video}"${optPosterAttr(o.video)} loop muted playsinline preload="none" aria-hidden="true"></video>`
      :`<div class="opt-swatch sw-warm" aria-hidden="true"></div>`;
    return `<div class="opt row">
      <input type="checkbox" name="${name}" id="${name}-${o.key}" value="${o.key}" ${checked?'checked':''}>
      <label for="${name}-${o.key}">
        <span class="opt-check" aria-hidden="true">✓</span>
        ${media}
        <div class="row-body"><div class="opt-name">${o.label}</div><div class="opt-desc">${o.desc}</div>${o.note?`<div class="opt-note"><div class="opt-note-title">${o.note.title}</div><div class="opt-note-body">${o.note.body}</div></div>`:''}</div>
        ${amount>0?`<div class="row-right">+${money(amount)}</div>`:''}
      </label>
    </div>`;
  }).join('')+`</div>`;
}
function foot(){
  return `<div class="q-foot">
    <button class="foot-btn" id="footBack" type="button">← Back</button>
    <div class="foot-btns"><button class="foot-btn primary" id="footNext" type="button">${stepIndex===STEPS.length-1?'Reserve her':'Continue →'}</button></div>
  </div>`;
}
function wireFoot(panel){
  const b=panel.querySelector('#footBack'); if(b){ b.disabled=stepIndex===0; b.addEventListener('click',()=>stepBy(-1)); }
  const n=panel.querySelector('#footNext'); if(n){ n.addEventListener('click',()=>stepBy(1)); }
}
function wireChange(panel,handler){
  // #questionPanel is STATIC markup - innerHTML swaps its children, never the node
  // itself - so a listener added here survives every render. Adding a fresh arrow
  // function per render therefore STACKED them: measured 8/10/12/14/16 handler runs
  // for one Skin change, and - because the Enhancements handler calls renderAll()
  // from inside itself - 1/2/4/8/16/32/64/128 full re-renders per Intimate-system
  // click, freezing the tab for 7s then 13s on clicks 10 and 11.
  // Keep exactly one: drop the previous handler before adding the new one.
  if(panel._sbdChange) panel.removeEventListener('change',panel._sbdChange);
  panel._sbdChange=handler;
  panel.addEventListener('change',handler);
}

/* ============================================================
   STEP RENDERERS
   ============================================================ */
function bodyFilterGroup(axis,label,values){
  const selected=bodyGalleryFilters[axis];
  return `<fieldset class="body-filter-group"><legend>${label}</legend><div class="body-filter-options">${values.map(value=>{
    const text=axis==='heightBand'?BODY_HEIGHT_LABEL[value]:BODY_SIZE_LABEL[value];
    return `<button type="button" class="body-filter-pill" data-body-filter-axis="${axis}" data-body-filter-value="${value}" aria-pressed="${selected.has(value)}">${text}</button>`;
  }).join('')}</div></fieldset>`;
}
function bodySkinPicker(){
  const activeSkin=BODY_SKIN_MAP[state.skin.key]||'natural';
  // Only tones the chosen face has renders for — same rule as the Skin step,
  // so the body studio can't route a customer into Luna + Dark Tanned either.
  const availableTones=skinTonesFor(state.face.key);
  return `<fieldset class="body-filter-group body-skin-filter"><legend>Skin tone</legend><div class="body-filter-options">${BODY_SKINS.filter(skin=>availableTones.some(tone=>tone.key===BODY_SKIN_REVERSE[skin.id])).map(skin=>{
    const tone=SKIN_TONES.find(option=>option.key===BODY_SKIN_REVERSE[skin.id]);
    const label=tone?.label||skin.label;
    return `<button type="button" class="body-skin-pill" data-body-skin="${skin.id}" aria-label="Skin tone: ${label}" aria-pressed="${skin.id===activeSkin}">
      <span style="--body-skin:${skin.hex}"></span><span class="body-skin-name">${label}</span>
    </button>`;
  }).join('')}</div></fieldset>`;
}
function bodyViewPicker(){
  return `<fieldset class="body-filter-group body-view-filter"><legend>Preview</legend><div class="body-filter-options">
    <button type="button" class="body-filter-pill" data-body-view="lace" aria-pressed="${bodyView==='lace'}">Laced</button>
    <button type="button" class="body-filter-pill" data-body-view="nude" aria-pressed="${bodyView==='nude'}">Nude</button>
  </div></fieldset>`;
}
function bodyGuideGroup(axis,label,values){
  const selected=bodyGuidePrefs[axis]||'';
  return `<fieldset class="body-filter-group body-guide-group"><legend>${label}</legend><div class="body-filter-options">
    <button type="button" class="body-filter-pill" data-body-guide-axis="${axis}" data-body-guide-value="" aria-pressed="${!selected}">Any</button>
    ${values.map(value=>{
      const text=axis==='heightBand'?BODY_HEIGHT_LABEL[value]:BODY_PREFERENCE_LABEL[value];
      return `<button type="button" class="body-filter-pill" data-body-guide-axis="${axis}" data-body-guide-value="${value}" aria-pressed="${selected===value}">${text}</button>`;
    }).join('')}
  </div></fieldset>`;
}
function bodyMatchesGallery(model){
  // Tone first: a body with no render for the chosen skin is not a filter miss,
  // it is not on offer at all, so it never counts toward "x of y bodies".
  if(!bodyHasTone(model,state.skin.key)) return false;
  return ['breast','butt','heightBand'].every(axis=>!bodyGalleryFilters[axis].size||bodyGalleryFilters[axis].has(model[axis]));
}
function bodyMatch(model){
  const weights={breast:3,butt:3,heightBand:2};
  let score=0,max=0,matches=0,chosen=0;
  Object.keys(weights).forEach(axis=>{
    if(!bodyGuidePrefs[axis]) return;
    chosen++;
    max+=weights[axis];
    if(model[axis]===bodyGuidePrefs[axis]){ score+=weights[axis]; matches++; }
  });
  return {score,max,matches,chosen,percent:max?Math.round(score/max*100):0};
}
function bodyCard(model,opts){
  const options=opts||{};
  const selected=state.bodyModel&&state.bodyModel.id===model.id;
  const match=options.match;
  const badge=selected?'Selected':(options.best?'Best match':'');
  return `<button type="button" class="body-card${selected?' is-selected':''}${options.guided?' is-guided':''}" data-body-id="${encodeURIComponent(model.id)}" aria-pressed="${selected}">
    <span class="body-card-media"><img src="${bodyAssetUrl(model,'thumb',bodyView,fitWidth(140,bodyAssetNative(model,'thumb')))}" alt="${model.label}, front view" loading="lazy" decoding="async">${badge?`<span class="body-card-badge">${badge}</span>`:''}</span>
    <span class="body-card-copy"><strong>${model.label.replace(/^Silicone /,'')}</strong><span>${model.height} cm · ${BODY_SIZE_LABEL[model.breast]} breast · ${BODY_SIZE_LABEL[model.butt]} butt</span>${match&&match.chosen?`<em>${match.percent}% preference match</em>`:''}</span>
    <span class="body-card-check" aria-hidden="true">✓</span>
  </button>`;
}
function bodyViewerMeta(model){
  if(!model) return '';
  return `<div class="body-viewer-heading"><div><span>Selected body</span><h4>${model.label}</h4></div><span class="body-viewer-selected">Selected</span></div>
    <dl class="body-viewer-specs">
      <div><dt>Height</dt><dd>${model.height} cm</dd></div>
      <div><dt>Bust</dt><dd>${model.bust} cm</dd></div>
      <div><dt>Hip</dt><dd>${model.hip} cm</dd></div>
      <div><dt>Hip − waist</dt><dd>${model.hipWaist} cm</dd></div>
    </dl>`;
}
function bodyViewer(model){
  if(!model) return `<aside class="body-viewer"><div class="body-viewer-empty">Choose a body to preview it from every angle.</div></aside>`;
  const asset=bodyAssetData(model);
  const angle=BODY_ANGLES[bodyAngleIndex];
  return `<aside class="body-viewer" aria-label="Selected body preview">
    <div class="body-viewer-stage" tabindex="0" aria-label="Body turntable. Drag or use the arrow keys to rotate.">
      <span class="body-viewer-kicker">8-angle preview</span>
      <div class="body-turntable-viewport" style="--body-frame-ratio:${asset?.width||520}/${asset?.height||1600}">
        <img class="body-viewer-img" src="${bodyAssetUrl(model,'sprite',bodyView,bodySpriteW(model))}" style="--body-frame-offset:${-bodyAngleIndex*12.5}%" alt="${model.label}, ${angle.label.toLowerCase()} view">
      </div>
      <span class="body-viewer-motion" data-body-angle-label>${angle.label} · ${String(angle.deg).padStart(3,'0')}°</span>
    </div>
    <div class="body-turntable-controls" role="group" aria-label="Rotate body preview">
      <button type="button" data-body-rotate="-1" aria-label="Previous angle">←</button>
      <button type="button" class="body-auto-toggle" data-body-auto aria-pressed="${bodyAutoRotate}"><span aria-hidden="true">${bodyAutoRotate?'Ⅱ':'▶'}</span><span data-body-auto-label>${bodyAutoRotate?'Pause rotation':'Play rotation'}</span></button>
      <button type="button" data-body-rotate="1" aria-label="Next angle">→</button>
    </div>
    <div class="body-viewer-meta">${bodyViewerMeta(model)}</div>
  </aside>`;
}
function paintBodyFrame(panel,model=state.bodyModel){
  const image=panel.querySelector('.body-viewer-img');
  const label=panel.querySelector('[data-body-angle-label]');
  const angle=BODY_ANGLES[bodyAngleIndex];
  if(image){
    image.style.setProperty('--body-frame-offset',`${-bodyAngleIndex*12.5}%`);
    image.alt=`${model?.label||'Selected body'}, ${angle.label.toLowerCase()} view`;
  }
  if(label) label.textContent=`${angle.label} · ${String(angle.deg).padStart(3,'0')}°`;
}
function stopBodyAutoRotation(){
  if(bodyAutoTimer) window.clearInterval(bodyAutoTimer);
  bodyAutoTimer=null;
}
function startBodyAutoRotation(panel){
  stopBodyAutoRotation();
  if(!bodyAutoRotate||reduceMotion) return;
  bodyAutoTimer=window.setInterval(()=>{
    if(!panel.isConnected||STEPS[stepIndex]?.id!=='body'){ stopBodyAutoRotation(); return; }
    bodyAngleIndex=(bodyAngleIndex+1)%BODY_ANGLES.length;
    paintBodyFrame(panel);
  },BODY_AUTO_MS);
}
function setBodyAutoRotation(panel,enabled){
  bodyAutoRotate=Boolean(enabled)&&!reduceMotion;
  const button=panel.querySelector('[data-body-auto]');
  if(button){
    button.setAttribute('aria-pressed',String(bodyAutoRotate));
    const icon=button.querySelector('[aria-hidden="true"]');
    const label=button.querySelector('[data-body-auto-label]');
    if(icon) icon.textContent=bodyAutoRotate?'Ⅱ':'▶';
    if(label) label.textContent=bodyAutoRotate?'Pause rotation':'Play rotation';
  }
  bodyAutoRotate?startBodyAutoRotation(panel):stopBodyAutoRotation();
}
function paintBodyViewer(panel,model,temporary){
  const image=panel.querySelector('.body-viewer-img');
  const viewport=panel.querySelector('.body-turntable-viewport');
  const meta=panel.querySelector('.body-viewer-meta');
  const selected=panel.querySelector('.body-viewer-selected');
  if(!image||!meta||!model) return;
  const asset=bodyAssetData(model);
  if(!custDeferImages) image.src=bodyAssetUrl(model,'sprite',bodyView,bodySpriteW(model));
  if(viewport) viewport.style.setProperty('--body-frame-ratio',`${asset?.width||520}/${asset?.height||1600}`);
  paintBodyFrame(panel,model);
  meta.innerHTML=bodyViewerMeta(model);
  const nextSelected=meta.querySelector('.body-viewer-selected');
  if(nextSelected){ nextSelected.textContent=temporary?'Previewing':'Selected'; nextSelected.classList.toggle('is-previewing',temporary); }
  if(selected&&selected!==nextSelected) selected.remove();
}
function renderBodyAngleComparison(){
  const region=document.getElementById('bodyAngleComparison');
  const content=document.getElementById('bodyAngleComparisonContent');
  const model=state.bodyModel;
  if(!region||!content||!model||STEPS[stepIndex]?.id!=='body'){
    if(region) region.hidden=true;
    return;
  }
  const asset=bodyAssetData(model);
  const sprite=bodyAssetUrl(model,'sprite',bodyView,bodySpriteW(model));
  const tone=state.skin.label;
  const viewLabel=bodyView==='lace'?'Laced':'Nude';
  region.hidden=false;
  content.innerHTML=`
    <div class="body-angle-heading"><div><span>Selected body · all angles</span><h3 id="bodyAngleTitle">Every side of ${model.label}</h3></div><p>${tone} skin · ${viewLabel} view</p></div>
    <div class="body-angle-scroll"><div class="body-angle-grid">${BODY_ANGLES.map((angle,index)=>`
      <figure><div class="body-angle-frame" style="--body-frame-ratio:${asset?.width||520}/${asset?.height||1600}"><img src="${sprite}" style="--body-frame-offset:${-index*12.5}%" alt="" loading="lazy" decoding="async"></div><figcaption>${angle.label}<span>${String(angle.deg).padStart(3,'0')}°</span></figcaption></figure>`).join('')}
    </div></div>`;
}
function setSkinFromBody(skinId){
  const key=BODY_SKIN_REVERSE[skinId];
  const tone=skinTonesFor(state.face.key).find(option=>option.key===key);
  if(!tone) return;
  state.lookOverrides.skin=true;
  state.skin=tone;
  const hair=hairListFor(state.face.key,state.skin.key);
  state.hairStyle=retainHairSelection(hair,state.hairStyle);
}
function wireBodyExperience(panel){
  panel.onclick=(event)=>{
    const filter=event.target.closest('[data-body-filter-axis]');
    if(filter){
      const set=bodyGalleryFilters[filter.dataset.bodyFilterAxis];
      set.has(filter.dataset.bodyFilterValue)?set.delete(filter.dataset.bodyFilterValue):set.add(filter.dataset.bodyFilterValue);
      renderBody(panel); return;
    }
    const clear=event.target.closest('[data-body-clear]');
    if(clear){ Object.values(bodyGalleryFilters).forEach(set=>set.clear()); renderBody(panel); return; }
    const skin=event.target.closest('[data-body-skin]');
    // Same rule as the Skin step: this pill changes the tone, which can force a
    // body swap, which can change the base price. updatePrice() was missing here too.
    if(skin){ setSkinFromBody(skin.dataset.bodySkin); renderBody(panel); updatePrice(); updatePreview(); return; }
    const view=event.target.closest('[data-body-view]');
    if(view){ bodyView=view.dataset.bodyView; bodyAngleIndex=0; renderBody(panel); return; }
    const rotate=event.target.closest('[data-body-rotate]');
    if(rotate){
      setBodyAutoRotation(panel,false);
      bodyAngleIndex=(bodyAngleIndex+Number(rotate.dataset.bodyRotate)+BODY_ANGLES.length)%BODY_ANGLES.length;
      paintBodyFrame(panel); return;
    }
    const auto=event.target.closest('[data-body-auto]');
    if(auto){ setBodyAutoRotation(panel,!bodyAutoRotate); return; }
    const guide=event.target.closest('[data-body-guide-axis]');
    if(guide){ bodyGuidePrefs[guide.dataset.bodyGuideAxis]=guide.dataset.bodyGuideValue; renderBody(panel); return; }
    const showAll=event.target.closest('[data-body-show-all]');
    if(showAll){ bodyGuideShowAll=!bodyGuideShowAll; renderBody(panel); return; }
    const card=event.target.closest('[data-body-id]');
    if(card){
      const model=BODY_CATALOG.find(option=>option.id===decodeURIComponent(card.dataset.bodyId));
      if(model){ state.lookOverrides.skin=true; bodyAngleIndex=0; applyBodyModel(model); renderBody(panel); updatePrice(); updatePreview(); }
    }
  };
  const previewFromEvent=(event)=>{
    const card=event.target.closest&&event.target.closest('[data-body-id]');
    if(!card||card.contains(event.relatedTarget)) return;
    const model=BODY_CATALOG.find(option=>option.id===decodeURIComponent(card.dataset.bodyId));
    if(model) paintBodyViewer(panel,model,state.bodyModel?.id!==model.id);
  };
  const restorePreview=(event)=>{
    const card=event.target.closest&&event.target.closest('[data-body-id]');
    if(!card||card.contains(event.relatedTarget)) return;
    paintBodyViewer(panel,state.bodyModel,false);
  };
  panel.onmouseover=previewFromEvent;
  panel.onfocusin=previewFromEvent;
  panel.onmouseout=restorePreview;
  panel.onfocusout=restorePreview;
  const stage=panel.querySelector('.body-viewer-stage');
  if(stage){
    let dragFrom=null;
    stage.onpointerdown=(event)=>{
      if(event.button) return;
      dragFrom=event.clientX;
      stage.setPointerCapture(event.pointerId);
      stage.classList.add('is-dragging');
      setBodyAutoRotation(panel,false);
    };
    stage.onpointermove=(event)=>{
      if(dragFrom===null) return;
      const steps=Math.trunc((event.clientX-dragFrom)/42);
      if(!steps) return;
      bodyAngleIndex=(bodyAngleIndex-steps+BODY_ANGLES.length*4)%BODY_ANGLES.length;
      dragFrom+=steps*42;
      paintBodyFrame(panel);
    };
    stage.onpointerup=stage.onpointercancel=()=>{ dragFrom=null; stage.classList.remove('is-dragging'); };
    stage.onkeydown=(event)=>{
      if(event.key!=='ArrowLeft'&&event.key!=='ArrowRight') return;
      event.preventDefault();
      setBodyAutoRotation(panel,false);
      bodyAngleIndex=(bodyAngleIndex+(event.key==='ArrowRight'?1:-1)+BODY_ANGLES.length)%BODY_ANGLES.length;
      paintBodyFrame(panel);
    };
  }
  wireFoot(panel);
  renderBodyAngleComparison();
  startBodyAutoRotation(panel);
}
function renderBodyGallery(panel){
  const shown=BODY_CATALOG.filter(bodyMatchesGallery);
  const offered=bodiesForTone(state.skin.key);
  panel.innerHTML=`
    <div class="body-finder-heading"><div><span class="body-finder-eyebrow">Body studio</span><h3 class="q-title">Choose her exact body</h3><p class="q-help">Filter the approved bodies, tap or hover to see every angle, then choose the one you want. Measurements and pricing update with your selection.</p></div><span class="body-ux-label">Visual gallery</span></div>
    <div class="body-filter-toolbar">
      ${bodyFilterGroup('breast','Breast size',['small','medium','large'])}
      ${bodyFilterGroup('butt','Butt size',['small','medium','large'])}
      ${bodyFilterGroup('heightBand','Height',['short','tall'])}
      ${bodySkinPicker()}
      ${bodyViewPicker()}
    </div>
    <div class="body-result-bar"><span><strong>${shown.length}</strong> of ${offered.length} bodies</span><button type="button" data-body-clear>Clear filters</button></div>
    <div class="body-studio-layout">
      <div class="body-gallery-pane">${shown.length?`<div class="body-gallery-grid">${shown.map(model=>bodyCard(model)).join('')}</div>`:'<div class="body-empty"><strong>No exact match</strong><span>Clear one filter to see more bodies.</span></div>'}</div>
      ${bodyViewer(state.bodyModel)}
    </div>
    ${!isGranularMobile?buildWeightGroup():''}
    ${foot()}`;
  wireBodyExperience(panel);
  wireBuildWeight(panel);
}
function renderBodyGuided(panel){
  const ranked=bodiesForTone(state.skin.key).map(model=>({model,match:bodyMatch(model)})).sort((a,b)=>
    b.match.score-a.match.score||a.model.height-b.model.height||a.model.label.localeCompare(b.model.label));
  const shown=bodyGuideShowAll?ranked:ranked.slice(0,8);
  const bestScore=ranked[0]?.match.score;
  panel.innerHTML=`
    <div class="body-finder-heading"><div><span class="body-finder-eyebrow">Recommended experience</span><h3 class="q-title">Find the shape that feels right</h3><p class="q-help">Tell us what you prefer. We rank every real body instead of hiding choices, so you can compare close alternatives without hitting a dead end.</p></div><span class="body-ux-label is-recommended">Guided finder</span></div>
    <div class="body-filter-toolbar body-guide-toolbar">
      ${bodyGuideGroup('breast','Breast size',['small','medium','large'])}
      ${bodyGuideGroup('butt','Butt size',['small','medium','large'])}
      ${bodyGuideGroup('heightBand','Height',['short','tall'])}
      ${bodySkinPicker()}
      ${bodyViewPicker()}
    </div>
    <div class="body-result-bar"><span><strong>${ranked.filter(item=>item.match.score===bestScore).length}</strong> closest matches · ${ranked.length} bodies considered</span><button type="button" data-body-show-all>${bodyGuideShowAll?'Show closest matches':'Show all bodies'}</button></div>
    <div class="body-studio-layout is-guided">
      <div class="body-gallery-pane"><div class="body-gallery-grid is-guided">${shown.map((item,index)=>bodyCard(item.model,{guided:true,match:item.match,best:index===0&&item.match.chosen>0})).join('')}</div></div>
      ${bodyViewer(state.bodyModel)}
    </div>
    ${!isGranularMobile?buildWeightGroup():''}
    ${foot()}`;
  wireBodyExperience(panel);
  wireBuildWeight(panel);
}
function renderBody(panel){
  stopBodyAutoRotation();
  if(!BODY_CATALOG.length){
    panel.innerHTML=`<h3 class="q-title">Choose her body</h3><p class="q-help">Body photography is still loading. Please refresh in a moment.</p>${foot()}`;
    wireFoot(panel); return;
  }
  BODY_EXPERIENCE==='guided'?renderBodyGuided(panel):renderBodyGallery(panel);
}
function buildWeightGroup(){
  return ULTRALIGHT_ELIGIBLE?groupWrap('Build weight',radioGroup('ultraLight',ULTRA_LIGHT_CHOICES,state.ultraLight.key,'cols-2 hscroll pair'),true):'';
}
function wireBuildWeight(panel){
  panel.querySelectorAll('input[name="ultraLight"]').forEach(input=>input.addEventListener('change',event=>{
    event.stopPropagation();
    state.ultraLight=ULTRA_LIGHT_CHOICES.find(option=>option.key===input.value)||ULTRA_LIGHT_CHOICES[0];
    updatePrice(); updatePreview();
  }));
}
function renderBodyConfirmation(panel){
  const model=state.bodyModel;
  if(!model){
    panel.innerHTML=`<h3 class="q-title">Confirm her body</h3><p class="q-help">Choose an exact body first.</p>${foot()}`;
    wireFoot(panel); return;
  }
  const asset=bodyAssetData(model);
  panel.innerHTML=`
    <h3 class="q-title">Confirm her exact body</h3>
    <p class="q-help">One last look at the full, uncropped body before you personalise how she feels.</p>
    <div class="body-confirmation">
      <div class="body-confirmation-media"><img src="${bodyAssetUrl(model,'thumb',bodyView,fitWidth(320,bodyAssetNative(model,'thumb')))}" width="${asset?.width||520}" height="${asset?.height||1600}" alt="${model.label}, full body front view" loading="lazy" decoding="async"></div>
      <div class="body-confirmation-copy">${bodyViewerMeta(model)}</div>
    </div>
    ${buildWeightGroup()}
    ${foot()}`;
  wireBuildWeight(panel);
  wireFoot(panel);
}
function renderFeel(panel){
  panel.innerHTML=`
    <h3 class="q-title">Choose her feel</h3>
    <p class="q-help">Choose the existing breast and butt materials, then finish the breast details. Silicone is included; gel-infused adds a softer touch.</p>
    ${groupWrap('Breast feel',radioGroup('breastFeel',BREAST_FEEL,state.breastFeel.key,'cols-2 hscroll pair'),true)}
    ${groupWrap('Butt feel',radioGroup('buttFeel',BUTT_FEEL,state.buttFeel.key,'cols-2 hscroll pair'),true)}
    ${groupWrap('Nipple size',radioGroup('areola',AREOLA_SIZES,state.areola.key,'cols-3 compact-photo hscroll'),true)}
    ${groupWrap('Breast piercing',radioGroup('breastPiercing',BREAST_PIERCING,state.breastPiercing.key,'cols-2 compact-photo hscroll pair'),true)}
    ${foot()}`;
  wireChange(panel,e=>{
    if(e.target.name==='breastFeel') state.breastFeel=BREAST_FEEL.find(o=>o.key===e.target.value);
    if(e.target.name==='buttFeel') state.buttFeel=BUTT_FEEL.find(o=>o.key===e.target.value);
    if(e.target.name==='areola') state.areola=AREOLA_SIZES.find(o=>o.key===e.target.value);
    if(e.target.name==='breastPiercing') state.breastPiercing=BREAST_PIERCING.find(o=>o.key===e.target.value);
    updatePrice(); updatePreview();
  });
  wireFoot(panel);
}
function applyHeadLook(faceKey,refresh=true){
  const look=HEAD_LOOKS[faceKey];
  state.face=FACES.find(option=>option.key===faceKey);
  if(!state.lookOverrides.skin) state.skin=SKIN_TONES.find(option=>option.key===look.skin);
  const list=hairListFor(faceKey,state.skin.key);
  state.hairStyle=state.lookOverrides.hairStyle?retainHairSelection(list,state.hairStyle)
    :(list.find(option=>option.code===look.hair)||list[0]);
  if(!state.lookOverrides.eyes) state.eyes=EYE_COLORS.find(option=>option.eyeNum===look.eyes);
  if(!state.lookOverrides.makeup) state.makeup=MAKEUP_STYLES.find(option=>option.key===look.makeup);
  if(!bodyHasTone(state.bodyModel,state.skin.key)){
    const swap=nearestBodyForTone(state.bodyModel,state.skin.key);
    if(swap) applyBodyModel(swap);
  }
  if(refresh){ updatePrice(); updatePreview(); }
}
function renderFace(panel){
  const faceOpts=FACES.map(f=>({...f,img:headLookImage(f.key),alt:`${f.label} — signature starting look`}));
  panel.innerHTML=`
    <h3 class="q-title">Choose her face</h3>
    <p class="q-help">Each face starts with a signature look. Make it yours — you can change her skin, hair, eyes and makeup in the next steps. Your own choices stay when you switch faces.</p>
    ${groupWrap('Choose her face',radioGroup('face',faceOpts,state.face.key,'cols-4 hscroll'),false)}
    ${foot()}`;
  wireChange(panel,e=>{ if(e.target.name==='face') applyHeadLook(e.target.value); });
  wireFoot(panel);
}
function renderName(panel){
  panel.innerHTML=`
    <h3 class="q-title">What will you call her?</h3>
    <p class="q-help">Give your finished companion a name before reviewing your design. You can change it anytime.</p>
    <fieldset class="group"><legend class="group-label">Her name</legend>
      <input type="text" id="nameInput" class="name-input" value="${state.name.replace(/"/g,'&quot;')}" maxlength="24" placeholder="Type her name…" autocomplete="off" aria-label="Her name">
    </fieldset>
    ${foot()}`;
  const input=panel.querySelector('#nameInput');
  input.addEventListener('input',()=>{ state.name=input.value; updatePreview(); });
  wireFoot(panel);
}
/* The skin cards show the COLOUR, not a portrait. Five head shots of the same
   girl in five tones asked the customer to spot a shade difference across five
   separate faces — the thing being chosen was the least visible thing on the
   card. Each tone already carries its own `sil` ramp (highlight / mid / shadow,
   the same values the body silhouette is painted with), so the chip is the tone
   itself and no new asset is needed. The live portrait on the left still shows
   her in the selected tone, which is where a face belongs. */
function toneSwatchStyle(o){
  const ramp=o.sil&&o.sil.length===3?o.sil:[o.tint,o.tint,o.tint];
  return `background:linear-gradient(155deg,${ramp[0]} 0%,${ramp[1]} 52%,${ramp[2]} 100%)`;
}
function skinToneCards(faceKey){
  return skinTonesFor(faceKey).map(o=>({...o,img:null,swatchStyle:toneSwatchStyle(o),alt:`${o.label} — ${o.desc}`}));
}
function renderSkin(panel){
  panel.innerHTML=`
    <h3 class="q-title">Choose her skin tone</h3>
    <p class="q-help">Her skin tone applies to her whole body and is permanent, so start with the shade you love.</p>
    ${groupWrap('Skin tone',radioGroup('skin',skinToneCards(state.face.key),state.skin.key,'cols-5 hscroll','opt-swatchimg'),false)}
    ${foot()}`;
  wireChange(panel,e=>{ if(e.target.name==='skin'){
    state.lookOverrides.skin=true;
    state.skin=SKIN_TONES.find(o=>o.key===e.target.value);
    // Each face+tone has its own hair set — keep the same slot if it still exists,
    // otherwise snap to that tone's first hair (or null if the tone has no renders).
    const list=hairListFor(state.face.key,state.skin.key);
    state.hairStyle=retainHairSelection(list,state.hairStyle);
    // Same rule for the BODY: some bodies have no render in some tones (see
    // BODY_TONE_EXCLUSIONS). Skin is chosen before body, but it can be changed
    // afterwards, and without this the build would keep a body the new tone
    // cannot draw — which bodyAssetData now correctly returns null for.
    if(!bodyHasTone(state.bodyModel,state.skin.key)){
      const swap=nearestBodyForTone(state.bodyModel,state.skin.key);
      if(swap) applyBodyModel(swap);
    }
    // The swap can change the BASE PRICE (measured live: PR160 -> 166cm 2.0 is
    // +$490, PR178 -> 171cm is +$360, 162cm -> 165cm is -$160). Without this the
    // header total and the breakdown keep naming the OLD body at the OLD price
    // until the customer happens to leave the step - so the rise appears later,
    // detached from the choice that caused it. Body step at :3016 already does both.
    updatePrice();
    updatePreview();
  } });
  wireFoot(panel);
}
// ── Her Look: push-in preview when scrolling to the eyes ─────────────────────
// The preview figure persists across steps, so the observer + .zoomed class are
// torn down on every re-render (renderAll) and re-armed only while the Her Look
// step is showing.
let lookZoomObserver=null;
function teardownLookZoom(){
  if(lookZoomObserver){ lookZoomObserver.disconnect(); lookZoomObserver=null; }
  const fig=document.querySelector('.preview-figure');
  if(fig) fig.classList.remove('zoomed');
}
function setupLookZoom(panel){
  const fig=document.querySelector('.preview-figure');
  const eyesInput=panel.querySelector('input[name="eyes"]');
  if(!fig||!eyesInput||!('IntersectionObserver' in window)) return;
  const eyesGroup=eyesInput.closest('.group');
  if(!eyesGroup) return;
  // Zoom in once the eyes section crosses the vertical middle of the viewport —
  // i.e. as the customer scrolls down from the hair options to the eyes.
  lookZoomObserver=new IntersectionObserver((entries)=>{
    entries.forEach(e=>{ fig.classList.toggle('zoomed', e.isIntersecting); });
  },{ root:null, rootMargin:'-45% 0px -45% 0px', threshold:0 });
  lookZoomObserver.observe(eyesGroup);
}
function renderLook(panel){
  // Hair options are specific to the chosen face + skin tone (each ships its own set).
  const hairList=hairListFor(state.face.key,state.skin.key);
  const hairGroup=hairList.length
    ? radioGroup('hairStyle',hairList,state.hairStyle&&state.hairStyle.key,'cols-6','opt-portrait')
    : `<p class="q-help" style="margin:0">Hair styles for <strong>${state.face.label}</strong> in <strong>${state.skin.label}</strong> are coming soon — choose another skin tone to style her hair.</p>`;
  panel.innerHTML=`
    <h3 class="q-title">Design her look</h3>
    <p class="q-help">Hair and eyes — the details that make her instantly, unmistakably her.</p>
    ${groupWrap('Hair style',hairGroup,true)}
    ${groupWrap('Eyes',radioGroup('eyes',EYE_COLORS,state.eyes.key,'cols-6','',true),true)}
    ${foot()}`;
  wireChange(panel,e=>{
    if(['hairStyle','eyes'].includes(e.target.name)) state.lookOverrides[e.target.name]=true;
    if(e.target.name==='hairStyle') state.hairStyle=hairListFor(state.face.key,state.skin.key).find(o=>o.key===e.target.value);
    if(e.target.name==='eyes') state.eyes=EYE_COLORS.find(o=>o.key===e.target.value);
    updatePreview();
  });
  setupLookZoom(panel);
  wireFoot(panel);
}
function makeupSignature(){
  return [state.face.key,state.skin.key,state.hairStyle&&state.hairStyle.uid,state.eyes&&state.eyes.uid,state.makeup.key,
    state.makeup.key==='custom'?(state.makeupCustomFilename||'no-file'):''].join('|');
}
// A stored preview answers in ~1s (measured 0.9–1.4s) and its image downloads in
// ~0.5–2.5s; a real generation takes 15–40s. The "Generating" overlay waits this
// long, so stored looks never flash it for work that was already done; anything
// slower than PREVIEW_RING_DELAY_MS gets the quiet loading ring in the meantime.
const MAKEUP_OVERLAY_DELAY_MS=2000;
const MAKEUP_PRELOAD_MAX_MS=15000;
let makeupRequestSerial=0;
// Every preview produced this visit, keyed by makeupSignature(). One slot was
// not enough: generating any other look overwrote it, so returning to an
// earlier hair/eye combination showed her without makeup that already existed.
function syncMakeupPreview(){
  const url=(state.makeup.key!=='natural'&&state.makeupPreviews[makeupSignature()])||null;
  // The cart handler in sbd-2d-customizer.liquid reads makeupPreviewUrl, so it
  // must always describe the CURRENT look, never the last one generated.
  state.makeupPreviewUrl=url;
  scheduleMakeupFollow();
  return url;
}
function preloadMakeupImage(url){
  return new Promise(resolve=>{
    const img=new Image();
    const done=()=>{ clearTimeout(timer); resolve(); };
    const timer=setTimeout(done,MAKEUP_PRELOAD_MAX_MS);
    img.onload=done; img.onerror=done;
    img.decoding='async';
    img.src=url;
  });
}
function makeupPreviewable(){
  if(state.makeup.key==='natural') return false;
  if(!(state.hairStyle&&state.hairStyle.hair&&state.eyes&&state.eyes.eyeNum)) return false;
  return state.makeup.key!=='custom'||Boolean(state.makeupCustomDataUri&&state.makeupConsent);
}
function activeStepId(){
  // updatePreview can run before STEPS/stepIndex are initialised on first load.
  try{ return STEPS[stepIndex].id; }catch(error){ return ''; }
}
// One request per look and rev, however many callers ask for it: the Makeup step
// and the background follower share it, so the same image is never paid for twice.
// Reads the look from `state`, so call it while `signature` still describes state.
// Two promises: `answered` when the service replies — enough to know no generation
// is running, which is what the "Generating" overlay waits on — and `ready` once the
// image has downloaded too. A preview is stored only when ready, so no updatePreview,
// from any caller, can swap the portrait to an image that is still arriving.
function fetchMakeupPreview(signature,rev){
  const key=signature+'#'+rev;
  if(makeupInflight[key]) return makeupInflight[key];
  const metadata={face:state.face.uid,skin:state.skin.uid,hair:state.hairStyle.uid,eyes:state.eyes.uid,makeup:state.makeup.uid};
  if(rev>0) metadata.rev=rev;
  const body=JSON.stringify({
    baseImageUrl:faceImg(state.face.key,state.skin.key,state.eyes.eyeNum,state.hairStyle.hair),
    makeupImageUrl:state.makeup.key==='custom'?state.makeupCustomDataUri:state.makeup.ref,
    isCustomPhoto:state.makeup.key==='custom',
    consent:state.makeup.key==='custom'?state.makeupConsent:undefined,
    metadata,
  });
  const answered=(async()=>{
    // A generation legitimately runs 15–40s, but nothing bounded it: a stalled
    // request (or one the browser silently never issued) left the spinner up
    // forever. Abort past the slowest legitimate window and fail visibly instead.
    const timeoutController=typeof AbortController==='function'?new AbortController():null;
    const timeoutTimer=timeoutController?setTimeout(()=>timeoutController.abort(),MAKEUP_TIMEOUT_MS):null;
    try{
      const response=await fetch(MAKEUP_FN_URL,{
        method:'POST',
        headers:{'Content-Type':'application/json'},
        signal:timeoutController?timeoutController.signal:undefined,
        body,
      });
      const data=await response.json().catch(()=>({}));
      return {ok:response.ok&&Boolean(data.url),data};
    }catch(error){
      return {ok:false,data:{},networkError:true,timedOut:Boolean(timeoutController&&timeoutController.signal.aborted)};
    }finally{
      if(timeoutTimer) clearTimeout(timeoutTimer);
    }
  })();
  const ready=answered.then(async result=>{
    if(result.ok){
      await preloadMakeupImage(cdnImg(result.data.url,300,375,{q:IMG_Q_PREVIEW}));
      // Kept even if the shopper has moved on (it was paid for, and they may come
      // back to that look) — but a newer regeneration of the look always wins.
      if(rev>=(state.makeupRevs[signature]||0)){
        state.makeupPreviews[signature]=result.data.url;
        if(rev>0) state.makeupRevs[signature]=rev;
      }
    }
    delete makeupInflight[key];
    return result;
  });
  return (makeupInflight[key]={answered,ready});
}
// mode: omitted = show the stored preview, generating only if there is none;
// 'refresh' = ask again even if stored (a new custom photo);
// 'regen' = ask for a genuinely new image (rev is part of the server's cache key).
async function runMakeupGeneration(panel,mode){
  const requestSerial=++makeupRequestSerial;
  // A superseded request no longer owns the ring; whatever this run decides replaces it.
  hidePreviewRing('makeup');
  const status=panel&&panel.querySelector('#makeupStatus');
  const overlay=document.getElementById('previewGenerating');
  const overlayText=document.getElementById('previewGeneratingText');
  const setStatus=(message,className)=>{
    if(!status) return;
    status.textContent=message||'';
    status.className='makeup-status'+(className?' '+className:'');
  };
  const setGenerating=active=>overlay&&overlay.classList.toggle('is-visible',Boolean(active));
  if(state.makeup.key==='natural'){
    setStatus(''); setGenerating(false); updatePreview(); return;
  }
  if(!(state.hairStyle&&state.hairStyle.hair&&state.eyes&&state.eyes.eyeNum)){
    setStatus('Choose a hair style and eye colour first.','is-error'); setGenerating(false); return;
  }
  if(state.makeup.key==='custom'){
    if(!state.makeupCustomDataUri){ setStatus('Upload a reference photo to see her in this look.'); setGenerating(false); return; }
    if(!state.makeupConsent){ setStatus('Confirm the photo consent checkbox to continue.'); setGenerating(false); return; }
  }
  const signature=makeupSignature();
  if(!mode&&state.makeupPreviews[signature]){
    setStatus(''); setGenerating(false); updatePreview(); return;
  }
  const rev=(state.makeupRevs[signature]||0)+(mode==='regen'?1:0);
  const makeupLabel=state.makeup.label;
  setStatus(''); setGenerating(false);
  const ringTimer=setTimeout(()=>{
    if(requestSerial===makeupRequestSerial) showPreviewRing('makeup');
  },PREVIEW_RING_DELAY_MS);
  const showTimer=setTimeout(()=>{
    if(requestSerial!==makeupRequestSerial) return;
    setStatus('Generating her preview… this takes 15–40 seconds.','is-loading');
    if(overlayText) overlayText.textContent=`Generating her ${makeupLabel} preview… this takes 15–40 seconds.`;
    setGenerating(true);
  },MAKEUP_OVERLAY_DELAY_MS);
  try{
    const request=fetchMakeupPreview(signature,rev);
    const result=await request.answered;
    clearTimeout(showTimer);
    const current=()=>requestSerial===makeupRequestSerial&&signature===makeupSignature();
    if(!result.ok){
      if(!current()) return;
      setStatus(result.networkError
        ?(result.timedOut?'The preview is taking longer than it should — try again in a moment.':'Could not reach the preview service — try again.')
        :(result.data.error||'Could not generate that preview — try again.'),'is-error');
      setGenerating(false); return;
    }
    // Swap only once the bytes are here; dropping the overlay first showed her
    // without makeup for the ~2s the full-size image took to arrive. The loading
    // ring armed at the start of the request covers a slow download. A superseded
    // run paints nothing: the run that replaced it owns the swap.
    await request.ready;
    if(!current()) return;
    setGenerating(false); updatePreview();
    setStatus(mode==='regen'&&typeof result.data.regenRemaining==='number'
      ?`New preview ready — ${result.data.regenRemaining} regeneration${result.data.regenRemaining===1?'':'s'} left today.`
      :'');
  }finally{
    clearTimeout(showTimer);
    clearTimeout(ringTimer);
    if(requestSerial===makeupRequestSerial) hidePreviewRing('makeup');
  }
}
// Her makeup follows her look. Changing eyes, hair, face or skin after choosing a
// style used to drop the preview back to no makeup until the customer returned to
// the Makeup step. Now the new look gets it too — at once when stored, otherwise
// generated once the look has settled for MAKEUP_FOLLOW_DELAY_MS, so swiping through
// eye colours pays for where the customer stops, not for every tile passed. Never on
// the Makeup step (runMakeupGeneration owns that), never behind a closed customizer,
// and one request at a time: a look chosen meanwhile is picked up when it finishes.
function makeupFollowWanted(){
  const signature=makeupPreviewable()?makeupSignature():'';
  // custDeferImages too: a homepage visitor with a saved makeup look was paying
  // for a real generation (~18 s, billed, counted against the per-visitor quota)
  // while the studio was still far below the screen. The release re-renders,
  // which schedules the follow again.
  if(!signature||state.makeupPreviews[signature]||customizerCollapsed()||custDeferImages||activeStepId()==='makeup') return '';
  return signature;
}
function scheduleMakeupFollow(){
  const signature=makeupFollowWanted();
  if(!signature){
    clearTimeout(makeupFollowTimer); makeupFollowTimer=null; makeupFollowQueued='';
    hidePreviewRing('follow');
    return;
  }
  if(signature===makeupFollowRunning){
    // Back on the look already being fetched: nothing else should stay queued.
    clearTimeout(makeupFollowTimer); makeupFollowTimer=null; makeupFollowQueued='';
    return;
  }
  // Already waiting for this look — keep its settle clock, don't restart it. Only
  // while something will act on it, so a stale mark can never strand a look.
  if(signature===makeupFollowQueued&&(makeupFollowTimer||makeupFollowRunning)) return;
  // A new look starts its own settle clock. While another look's request is running
  // it only queues; followMakeup picks it up when that request ends.
  clearTimeout(makeupFollowTimer); makeupFollowTimer=null;
  makeupFollowQueued=signature;
  makeupFollowQueuedAt=Date.now();
  if(!makeupFollowRunning) makeupFollowTimer=setTimeout(followMakeup,MAKEUP_FOLLOW_DELAY_MS);
}
async function followMakeup(){
  makeupFollowTimer=null;
  if(makeupFollowRunning) return;
  const signature=makeupFollowWanted();
  makeupFollowQueued='';
  if(!signature) return;
  makeupFollowRunning=signature;
  // The look has already been waiting MAKEUP_FOLLOW_DELAY_MS, past the ring's threshold.
  showPreviewRing('follow');
  let ok=false;
  try{
    ok=(await fetchMakeupPreview(signature,state.makeupRevs[signature]||0).ready).ok;
  }finally{
    makeupFollowRunning='';
    hidePreviewRing('follow');
  }
  if(signature===makeupSignature()){
    // A failure is not retried for the same look — the Makeup step shows the error
    // and offers a retry.
    if(ok) updatePreview();
    return;
  }
  // The customer moved on meanwhile. Their current look keeps the time it has already
  // been settled, so a look that waited out a long generation starts at once.
  const next=makeupFollowWanted();
  if(next&&next===makeupFollowQueued){
    makeupFollowTimer=setTimeout(followMakeup,Math.max(0,MAKEUP_FOLLOW_DELAY_MS-(Date.now()-makeupFollowQueuedAt)));
  }else{
    makeupFollowQueued='';
    scheduleMakeupFollow();
  }
}
function renderMakeup(panel){
  panel.innerHTML=`
    <h3 class="q-title">Choose her makeup</h3>
    <p class="q-help">Presets are included and generate on selection. Upload your own reference photo for a fully custom, AI-matched look.</p>
    ${groupWrap('Makeup style',radioGroup('makeup',MAKEUP_STYLES,state.makeup.key,'cols-3 hscroll rows2'),true)}
    <div class="makeup-upload" id="makeupUpload" ${state.makeup.key==='custom'?'':'hidden'}>
      <label class="opt-name" for="makeupPhotoInput">Reference photo</label>
      <input type="file" id="makeupPhotoInput" accept="image/*">
      ${state.makeupCustomFilename?`<span class="makeup-filename">Selected: ${state.makeupCustomFilename.replace(/[<>]/g,'')}</span>`:''}
      <label class="makeup-consent"><input type="checkbox" id="makeupConsentInput" ${state.makeupConsent?'checked':''}> This photo is of an adult, and I have the right to use it as a makeup reference.</label>
    </div>
    <p id="makeupStatus" class="makeup-status" role="status" aria-live="polite"></p>
    <button type="button" class="makeup-regen" id="makeupRegenerate" ${state.makeup.key==='natural'?'hidden':''}>Regenerate preview</button>
    ${foot()}`;
  const upload=panel.querySelector('#makeupUpload');
  wireChange(panel,event=>{
    if(event.target.name==='makeup'){
      state.lookOverrides.makeup=true;
      state.makeup=MAKEUP_STYLES.find(option=>option.key===event.target.value);
      upload.hidden=state.makeup.key!=='custom';
      panel.querySelector('#makeupRegenerate').hidden=state.makeup.key==='natural';
      updatePrice();
      runMakeupGeneration(panel);
    }else if(event.target.id==='makeupConsentInput'){
      state.makeupConsent=event.target.checked;
      runMakeupGeneration(panel);
    }else if(event.target.id==='makeupPhotoInput'){
      const file=event.target.files&&event.target.files[0];
      if(!file) return;
      state.makeupCustomFilename=file.name||'customer-reference-image';
      const reader=new FileReader();
      reader.onload=()=>{ state.makeupCustomDataUri=reader.result; runMakeupGeneration(panel,'refresh'); };
      reader.readAsDataURL(file);
    }
  });
  panel.querySelector('#makeupRegenerate')?.addEventListener('click',()=>runMakeupGeneration(panel,'regen'));
  wireFoot(panel);
  runMakeupGeneration(panel);
}
function renderFinish(panel){
  panel.innerHTML=`
    <h3 class="q-title">Finishing touches</h3>
    <p class="q-help">Small details, entirely optional — skip any of these and she comes as pictured, natural throughout.</p>
    ${groupWrap('Fingernails',radioGroup('fingernails',FINGERNAIL_STYLES,state.fingernails.key,'cols-6','',true),true)}
    ${groupWrap('Pubic hair',radioGroup('pubicHair',PUBIC_HAIR_STYLES,state.pubicHair.key,'cols-4 hscroll','wide'),true)}
    ${groupWrap('Tan lines',radioGroup('tanLine',TAN_LINE_CHOICES,state.tanLine.key,'cols-2 hscroll pair'),true)}
    ${groupWrap('Navel piercing',radioGroup('navel',NAVEL_PIERCING,state.navel.key,'cols-3 compact-photo hscroll'),true)}
    ${foot()}`;
  wireChange(panel,e=>{
    if(e.target.name==='fingernails') state.fingernails=FINGERNAIL_STYLES.find(o=>o.key===e.target.value);
    if(e.target.name==='pubicHair') state.pubicHair=PUBIC_HAIR_STYLES.find(o=>o.key===e.target.value);
    if(e.target.name==='tanLine') state.tanLine=TAN_LINE_CHOICES.find(o=>o.key===e.target.value);
    if(e.target.name==='navel') state.navel=NAVEL_PIERCING.find(o=>o.key===e.target.value);
    // Three of these four groups are priced. Without this call the running total
    // froze until some other step changed it, so the $40/$60/$7 deltas appeared
    // to land on whatever was clicked NEXT — including "None"/"Included" options.
    updatePrice();
  });
  wireFoot(panel);
}
/* ============================================================
   AUDIO SAMPLE — the "Hear it" button
   ============================================================ */
/* The only sound anywhere in the customizer, and it is strictly click-to-play.
   The Audio object is not constructed until the first click, so a customer who
   never asks to hear it downloads nothing — and there is no code path that can
   start playback without a click. Deliberately not <audio autoplay>, and
   deliberately not preloaded: this is a storefront, and a doll store that makes
   noise at someone unprompted is a doll store they close. */
let sampleAudio=null, samplePlayingBtn=null;
function paintSampleBtn(btn,playing){
  btn.classList.toggle('is-playing',playing);
  btn.setAttribute('aria-pressed',playing?'true':'false');
  // The icon changes shape as well as colour (triangle -> square), so the state
  // is not signalled by colour alone — same rule as .opt-check (WCAG 1.4.1).
  const t=btn.querySelector('.opt-audio-txt'); if(t) t.textContent=playing?'Stop':'Hear it';
}
function stopSample(){
  if(sampleAudio){ sampleAudio.pause(); sampleAudio.currentTime=0; }
  if(samplePlayingBtn){ paintSampleBtn(samplePlayingBtn,false); samplePlayingBtn=null; }
}
function toggleSample(btn){
  if(samplePlayingBtn===btn){ stopSample(); return; }   // second press = stop
  stopSample();                                          // never two at once
  if(!sampleAudio){ sampleAudio=new Audio(); sampleAudio.addEventListener('ended',stopSample); }
  sampleAudio.src=btn.dataset.audio;                     // always restart from the top
  samplePlayingBtn=btn; paintSampleBtn(btn,true);
  const p=sampleAudio.play();
  // Playback can still be refused — no gesture credited, offline, codec missing.
  // Don't leave a button claiming it is playing when nothing is.
  if(p&&p.catch) p.catch(function(){ stopSample(); });
}
// Independent upgrades share one card layout; native checkbox inputs remain
// the source of truth for selection, Shopify pricing and cart selections.
function upgradeGrid(name,options,set){
  return `<div class="sbd-upgrade-grid">`+options.map(o=>{
    const checked=set.has(o.key), amount=optionPrice(name,o);
    const posterKey=o.video?Object.keys(OPT_POSTERS).find(key=>o.video.includes(key)):null;
    const poster=posterKey?new URL(OPT_POSTERS[posterKey],CUSTOMIZER_ASSET_BASE).href:null;
    const image=o.img||poster;
    const media=image?`<img class="opt-photo${o.contain?' opt-contain':''}" ${imgAttrs(image,320,240,{fit:o.contain?'contain':'cover'})} alt="" loading="lazy" decoding="async">`:'';
    return `<div class="opt sbd-upgrade-card">
      <input type="checkbox" name="${name}" id="${name}-${o.key}" value="${o.key}" ${checked?'checked':''}>
      <label for="${name}-${o.key}">
        <span class="opt-check" aria-hidden="true">✓</span>
        ${media}<span class="sbd-upgrade-copy">
          <span class="sbd-upgrade-heading"><span class="opt-name">${o.label}</span>${amount>0?`<span class="opt-price">+${money(amount)}</span>`:''}</span>
          <span class="opt-desc">${o.desc}</span>
        </span>
      </label>
      <div class="sbd-upgrade-actions">
        ${o.video?`<button type="button" class="sbd-upgrade-watch" data-upgrade-watch aria-expanded="false" aria-controls="demo-${name}-${o.key}" aria-label="Watch ${o.label} video">Watch video</button>`:''}
        <button type="button" class="sbd-upgrade-add" data-upgrade-add aria-pressed="${checked}" aria-label="${checked?'Remove':'Add'} ${o.label}">${checked?'✓ Added':'Add'}</button>
      </div>
      ${o.video?`<div class="sbd-upgrade-demo" id="demo-${name}-${o.key}" hidden><video controls loop muted playsinline preload="none" data-upgrade-video-src="${o.video}" aria-label="${o.label} demonstration"></video></div>`:''}
    </div>`;
  }).join('')+`</div>`;
}
document.addEventListener('click',event=>{
  const button=event.target.closest?.('[data-upgrade-watch]');
  if(!button||!button.closest('#questionPanel')) return;
  event.preventDefault();
  const demo=document.getElementById(button.getAttribute('aria-controls'));
  if(!demo) return;
  const open=demo.hidden;
  document.querySelectorAll('#questionPanel .sbd-upgrade-demo').forEach(other=>{
    if(other===demo) return;
    other.hidden=true; other.querySelector('video')?.pause();
    const toggle=other.closest('.sbd-upgrade-card')?.querySelector('[data-upgrade-watch]');
    if(toggle){ toggle.setAttribute('aria-expanded','false'); toggle.textContent='Watch video'; }
  });
  demo.hidden=!open;
  button.setAttribute('aria-expanded',String(open));
  button.textContent=open?'Hide video':'Watch video';
  const video=demo.querySelector('video');
  if(open){
    if(!video.getAttribute('src')) video.src=video.dataset.upgradeVideoSrc;
    video.play()?.catch(()=>{});
  }else video.pause();
});
function renderEnhancements(panel){
  panel.innerHTML=`
    <h3 class="q-title">Bring her to life</h3>
    <p class="q-help">Choose any combination.</p>
    ${INTIMATE_SYSTEM.length>1?groupWrap('Intimate system',radioGroup('intimateSystem',INTIMATE_SYSTEM,state.intimateSystem.key,'cols-2 hscroll pair'),true):''}
    ${groupWrap('Heating',upgradeGrid('warmth',warmthOptions(),state.warmth),true)}
    ${groupWrap('Automatic movement',upgradeGrid('movement',MOVEMENT_ADDONS,state.movement),true)}
    ${groupWrap('Suction & stroking',upgradeGrid('sensation',sensationOptions(),state.sensation),true)}
    ${audioOptions().length?groupWrap('Audio',upgradeGrid('audio',audioOptions(),state.audio),true):''}
    ${SOFTCORE_ADDONS.length?groupWrap('Softcore',upgradeGrid('softcore',SOFTCORE_ADDONS,state.softcore),true):''}
    ${foot()}`;
  wireChange(panel,e=>{
    const grp=e.target.name;
    // Not a Set like the others — one radio, and it rewrites the Sensation grid
    // underneath it, so this re-renders the whole step rather than patching a Set.
    if(grp==='intimateSystem'){
      state.intimateSystem=INTIMATE_SYSTEM.find(o=>o.key===e.target.value);
      enhLastPick.intimateSystem=e.target.value;
      delete enhPreviewKey.intimateSystem;
      if(state.intimateSystem.key==='vaginax') applyVaginaxExclusions();
      const keepValue=e.target.value;
      renderAll();
      // renderAll() replaces panel.innerHTML, which destroys the focused radio and
      // drops focus to <body>. Arrow-keying a radio group fires `change` on EVERY
      // press, so without this a keyboard user cannot reach the second option of a
      // mandatory $260 decision without tabbing in again from the top of the page.
      const again=panel.querySelector('input[name="intimateSystem"][value="'+keepValue+'"]');
      if(again) again.focus({preventScroll:true});
      return;
    }
    if(grp==='ultraLight'){
      state.ultraLight=ULTRA_LIGHT_CHOICES.find(option=>option.key===e.target.value)||ULTRA_LIGHT_CHOICES[0];
      updatePrice(); updatePreview();
      return;
    }
    if(!['warmth','sensation','audio','softcore','movement'].includes(grp)) return;
    const set=state[grp];
    if(e.target.value==='__none__'){
      // None can't be unchecked into a void — it clears the others and stays on.
      set.clear();
      panel.querySelectorAll('input[name="'+grp+'"]').forEach(i=>{ i.checked=(i.value==='__none__'); });
    } else {
      e.target.checked?set.add(e.target.value):set.delete(e.target.value);
      if(grp==='sensation'&&e.target.checked&&['clampinternal','clampexternal'].includes(e.target.value)){
        const other=e.target.value==='clampinternal'?'clampexternal':'clampinternal';
        set.delete(other);
        const otherInput=panel.querySelector('input[name="sensation"][value="'+other+'"]');
        if(otherInput) otherInput.checked=false;
      }
      if(e.target.checked) enhLastPick[grp]=e.target.value;
      delete enhPreviewKey[grp];
      const n=panel.querySelector('#'+grp+'-__none__'); if(n) n.checked=(set.size===0);
    }
    enhRefreshPills(panel,grp);
    updatePrice(); updatePreview();
  });
  // The "Hear it" click handler is NOT wired here. renderAll() re-renders into
  // the SAME #questionPanel element on every step change and on several option
  // changes, so an addEventListener in this function stacked a new listener each
  // time without ever removing the old ones. Each extra listener called
  // toggleSample again for the same click: two listeners = play then immediately
  // stop, i.e. an even number of visits to this step left the button silent.
  // That is why it "did not work" — it worked exactly once per page load.
  // It now lives in a single document-level listener registered at startup
  // (see the .opt-audio delegation below the tracking listeners).
  wireFoot(panel);
  enhAugmentDesktop(panel);
}
function renderReview(panel){
  const t=total();
  const who=(state.name||'').trim()||'Her';
  const heating=[...state.warmth].map(key=>WARMTH_ADDONS.find(option=>option.key===key)?.label).filter(Boolean);
  if(state.sensation.has('clampinternal')) heating.push('Vaginal heating (included)');
  const rows=[
    ['Name',who,'name'],
    ['Body',state.bodyModel?state.bodyModel.label:state.body.label,'body'],
    ['Height',state.bodyModel?`${state.bodyModel.height} cm`:state.body.label,'body'],
    ['Face',state.face.label,'face'],
    ['Skin tone',state.skin.label,'skin'],
    ['Hair',state.hairStyle?state.hairStyle.label:'—','hair'],
    ['Eyes',state.eyes.label,'eyes'],
    ['Makeup',state.makeup.label,'makeup'],
    ['Breast',`${state.breastSize.label} · ${state.breastFeel.label}`,'breastFeel'],
    ['Nipple',state.areola.label,'areola'],
    ['Breast piercing',state.breastPiercing.label,'breastPiercing'],
    ['Butt',`${state.buttShape.label} · ${state.buttFeel.label}`,'buttFeel'],
    ['Fingernails',state.fingernails.label,'fingernails'],
    ['Pubic hair',state.pubicHair.label,'pubicHair'],
    ['Tan lines',state.tanLine.label,'tanLine'],
    ['Navel piercing',state.navel.label,'navel'],
    ...(INTIMATE_SYSTEM.length>1?[['Intimate system',state.intimateSystem.label,'intimateSystem']]:[]),
    ['Heating',heating.join(', ')||'None','warmth'],
    ['Suction & stroking',[...state.sensation].length?[...state.sensation].map(k=>SENSATION_ADDONS.find(o=>o.key===k).label).join(', '):'None','sensation'],
    ...(AUDIO_ADDONS.length?[['Audio',[...state.audio].map(k=>AUDIO_ADDONS.find(o=>o.key===k).label).join(', ')||'None','audio']]:[]),
    ...(SOFTCORE_ADDONS.length?[['Softcore',[...state.softcore].map(k=>SOFTCORE_ADDONS.find(o=>o.key===k).label).join(', ')||'None','softcore']]:[]),
    ['Automatic movement',[...state.movement].length?[...state.movement].map(k=>MOVEMENT_ADDONS.find(o=>o.key===k).label).join(', '):'None','movement'],
  ];
  if(ULTRALIGHT_ELIGIBLE) rows.push(['Build weight',state.ultraLight.label,'ultraLight']);
  let depositAvailable=false;
  try{
    const plans=JSON.parse(document.querySelector('[data-sbd-deposit-plans]')?.textContent||'null');
    depositAvailable=plans?.currency==='USD'&&Object.values(plans.base||{}).some(Boolean)&&Number.isFinite(t)&&t>500;
  }catch(_){/* Keep the full-payment route available if deposit data is unavailable. */}
  panel.innerHTML=`
    <div class="review-hero">
      <div class="kicker">${who} is ready</div>
      <h3>Meet ${who}</h3>
      <p>Take a moment with her before you decide. Everything below is exactly how she'll be built and brought to you.</p>
      ${addonCount()>0?`<div class="build-badge">${addonCount()} enhancement${addonCount()>1?'s':''} included</div>`:''}
    </div>
    <div class="review-flourish" aria-hidden="true"><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.2 7.8L22 12l-7.8 2.2L12 22l-2.2-7.8L2 12l7.8-2.2z"/></svg></div>
    <dl class="review-grid">${rows.map(([k,v,step])=>`<div class="review-item" data-step="${step}"><dt class="k">${k}</dt><dd class="v"><span>${v}</span><button type="button" class="ri-edit" aria-label="Edit ${k}">Edit</button></dd></div>`).join('')}</dl>
    <div class="review-breakdown">
      <div class="rb-title">How we got to ${money(t)}</div>
      <ul class="price-breakdown">${priceRowsHtml()}</ul>
      <div class="rb-total"><span>Total, built to order</span><span>${money(t)}</span></div>
    </div>
    <div class="review-cta">
      <div class="price-label" style="color:var(--muted-on-dark);">Your build total</div>
      <div class="price-total">${money(t)}</div>
      <div class="price-mo" data-payment-guidance></div>
      <div class="price-mo-note" data-payment-note></div>
      <button class="btn btn-primary btn-block" id="reserveBtn" type="button">Reserve ${who} — ${money(t)} →</button>
      ${depositAvailable?`<button class="btn btn-block" id="depositBtn" type="button">Pay $500 deposit →</button>
      <p class="note">Full build ${money(t)}. Pay $500 today; ${money(t-500)} remains. The balance is due only after you approve your factory photos. If changes are needed, payment waits for your approval.</p>`:''}
      <p class="note">You approve real photos of ${who} before she's shipped.</p>
      <ul class="secure-badges">
        <li><span data-icon="lock"></span>256-bit encrypted checkout</li>
        <li><span data-icon="card"></span>Visa · Mastercard · Amex · Affirm</li>
        <li><span data-icon="shield"></span>PCI-compliant payment processing</li>
      </ul>
    </div>
    <div class="save-design">
      <div class="sd-title">Not ready to decide today?</div>
      <p class="sd-copy">Save this exact design and come back to her anytime — we'll email you a private link.</p>
      <form class="sd-form" id="saveDesignForm" novalidate>
        <input class="sd-email" id="saveDesignEmail" type="email" name="email" autocomplete="email" placeholder="Your email address" aria-label="Your email address">
        <button class="btn btn-ghost sd-submit" id="saveDesignSubmit" type="submit">Email me my design</button>
      </form>
      <!-- Ticked by default outside the EU/EEA/UK only — see marketingOptInDefault().
           Never hard-code the checked attribute here: it would pre-tick in Hamburg. -->
      <label class="sd-consent"><input type="checkbox" id="saveDesignConsent"${marketingOptInDefault()?' checked':''}> Also send me occasional news and offers</label>
      <p class="sd-fine">${marketingOptInDefault()
        ?"One email with your design link. Untick the box above if you'd rather not hear anything else — and you can unsubscribe from any email."
        :"One email with your design link — nothing else unless you tick the box above."}</p>
      <div class="sd-result" id="saveDesignResult" hidden>
        <div class="sd-sent" id="saveDesignSent"></div>
        <div class="sd-linkrow" hidden><input class="sd-link" id="saveDesignLink" type="text" readonly aria-label="Your design link"><button type="button" class="foot-btn sd-copy-btn" id="saveDesignCopy">Copy</button></div>
      </div>
    </div>
    <div style="text-align:center; margin-top:22px;">
      <button class="foot-btn" id="reviewBack" type="button">← Back to make changes</button>
    </div>`;
  // Reserve is handled by the theme section's delegated cart handler.
  panel.querySelector('[data-payment-guidance]').textContent=document.getElementById('priceMo').textContent;
  panel.querySelector('[data-payment-note]').textContent=document.querySelector('#atelier-landing .price-mo-note').textContent;
  panel.querySelector('#reviewBack').addEventListener('click',()=>stepBy(-1));
  wireSaveDesign(panel);
  panel.querySelectorAll('.review-item[data-step]').forEach(row=>row.addEventListener('click',event=>{
    if(event.target.closest('a')) return;
    const target=stepIndexForId(row.dataset.step);
    if(target>=0) goToStep(target);
  }));
  applyIcons(panel);
}

/* ============================================================
   MARKETING CONSENT DEFAULT — pre-ticked, except in the EU/UK
   ============================================================ */
/* The "also send me news and offers" box is ticked by default (owner decision,
   2026-09-06). That is lawful under CAN-SPAM, the operator's home rule — FAPTIC
   CORP is a Delaware corporation — but a pre-ticked consent box is NOT valid
   consent under GDPR/ePrivacy (CJEU C-673/17, Planet49), and the store's single
   shipping zone covers all 27 EU states plus the UK. So the default is decided
   per visitor rather than globally.

   FAIL-SAFE BY DESIGN: this returns false — unticked — whenever it cannot prove
   the visitor is outside the EU/EEA/UK. An unticked box is merely a slightly
   worse opt-in rate; a ticked one in Hamburg is an unlawful one. Two independent
   signals, either of which vetoes:

     1. Shopify.country — the market Shopify resolved for this request. Reliable
        when Markets is doing its job, but it can read "US" for an unlocalised
        visitor, which is exactly why it is not trusted alone.
     2. The browser's own IANA time zone, which no cache layer can get wrong.
        Europe/* covers the mainland and the UK; the EU's non-European zones
        (Canaries, Azores, Madeira, the French DOM) are listed explicitly
        because "Atlantic/Canary" is as much the EU as "Europe/Madrid" is. */
const EEA_UK_COUNTRIES=new Set(['AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','HU','IE','IT','LV','LT','LU','MT','NL','PL','PT','RO','SK','SI','ES','SE','GB','IS','LI','NO','CH']);
const EEA_UK_EXTRA_ZONES=new Set(['Atlantic/Canary','Atlantic/Azores','Atlantic/Madeira','Atlantic/Reykjavik','Atlantic/Faroe','Indian/Reunion','Indian/Mayotte','America/Cayenne','America/Guadeloupe','America/Martinique']);
function marketingOptInDefault(){
  try{
    const country=(window.Shopify&&window.Shopify.country||'').toUpperCase();
    if(!country) return false;                        // no signal at all → unticked
    if(EEA_UK_COUNTRIES.has(country)) return false;
    const zone=(Intl.DateTimeFormat().resolvedOptions()||{}).timeZone||'';
    if(zone.indexOf('Europe/')===0||EEA_UK_EXTRA_ZONES.has(zone)) return false;
    return true;
  }catch(e){ return false; }                          // anything unexpected → unticked
}
/* "Email me my design" — POSTs the current build to the saved-builds edge function,
   which stores it and emails back a ?design=<token> link. The link is also shown
   inline with a copy button, so the design is savable even when the email can't be
   sent (function reports emailSent:false, e.g. no provider key configured yet). */
function wireSaveDesign(panel){
  const form=panel.querySelector('#saveDesignForm');
  if(!form) return;
  const emailInput=panel.querySelector('#saveDesignEmail');
  const submitBtn=panel.querySelector('#saveDesignSubmit');
  const resultBox=panel.querySelector('#saveDesignResult');
  const sentNote=panel.querySelector('#saveDesignSent');
  const linkRow=resultBox.querySelector('.sd-linkrow');
  const linkField=panel.querySelector('#saveDesignLink');
  const copyBtn=panel.querySelector('#saveDesignCopy');
  copyBtn.addEventListener('click',()=>{
    linkField.select();
    const done=()=>{ copyBtn.textContent='Copied'; setTimeout(()=>{ copyBtn.textContent='Copy'; },2000); };
    if(navigator.clipboard&&navigator.clipboard.writeText) navigator.clipboard.writeText(linkField.value).then(done,()=>{ try{ document.execCommand('copy'); done(); }catch(error){} });
    else{ try{ document.execCommand('copy'); done(); }catch(error){} }
  });
  form.addEventListener('submit',async event=>{
    event.preventDefault();
    const email=(emailInput.value||'').trim();
    if(!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)){
      resultBox.hidden=false; linkRow.hidden=true;
      sentNote.textContent='Please enter a valid email address.';
      emailInput.focus();
      sbdTrackSaveDesign('save_design_failed',{reason:'invalid_email'});
      return;
    }
    submitBtn.disabled=true;
    const restingLabel=submitBtn.textContent;
    submitBtn.textContent='Saving…';
    try{
      sbdCaptureEntry();
      const response=await fetch(SAVED_BUILD_FN_URL,{
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({email,build:serializeBuild(),consentMarketing:panel.querySelector('#saveDesignConsent').checked}),
      });
      const data=await response.json().catch(()=>null);
      if(!response.ok||!data||!data.url) throw new Error((data&&data.error)||'save failed');
      resultBox.hidden=false; linkRow.hidden=false;
      sentNote.textContent=data.emailSent
        ?'Sent — your design link is on its way to your inbox.'
        :'Saved — keep this link, it opens your design on any device.';
      linkField.value=data.url;
      sbdTrackSaveDesign('save_design',{
        email_sent:!!data.emailSent,
        consent_marketing:!!panel.querySelector('#saveDesignConsent').checked,
        // Whether the box arrived ticked. A consent record needs to distinguish
        // "they ticked it" from "we ticked it for them" — same true value, very
        // different legal weight, and the opt-in RATE is only meaningful split
        // by this. See marketingOptInDefault().
        consent_prechecked:marketingOptInDefault()
      });
      form.hidden=true;
      panel.querySelector('.sd-consent').hidden=true;
      panel.querySelector('.sd-fine').hidden=true;
    }catch(error){
      resultBox.hidden=false; linkRow.hidden=true;
      sentNote.textContent='That didn’t work — please try again in a moment.';
      sbdTrackSaveDesign('save_design_failed',{reason:'request_failed'});
    }finally{
      submitBtn.disabled=false;
      submitBtn.textContent=restingLabel;
    }
  });
}

/* ============================================================
   ICONS (inline, decorative, aria-hidden via parent context)
   ============================================================ */
const ICONS={
  lock:'<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="4" y="11" width="16" height="9" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>',
  shield:'<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2l8 4v6c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6l8-4z"/></svg>',
  card:'<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/></svg>',
  truck:'<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="1" y="7" width="14" height="10" rx="1"/><path d="M15 10h4l3 3v4h-7z"/><circle cx="6" cy="19" r="1.6"/><circle cx="17.5" cy="19" r="1.6"/></svg>',
  return:'<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 12a9 9 0 1 0 3-6.7"/><path d="M3 4v5h5"/></svg>',
  heart:'<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 21s-7-4.6-9.6-9C.7 8.7 2 5 5.5 5c2 0 3.4 1 4.5 2.5C11.1 6 12.5 5 14.5 5 18 5 19.3 8.7 17.6 12 15 16.4 12 21 12 21z"/></svg>',
  remote:'<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="7" y="2" width="10" height="20" rx="3"/><circle cx="12" cy="8" r="1.6" fill="currentColor" stroke="none"/><path d="M9.5 13h5M9.5 16.5h5"/></svg>',
};
function applyIcons(root){
  (root||document).querySelectorAll('[data-icon]').forEach(el=>{ el.innerHTML=ICONS[el.dataset.icon]||''; el.setAttribute('aria-hidden','true'); });
}
applyIcons();

/* ============================================================
   INIT
   ============================================================ */
/* CTA buttons → scroll to the customizer (JS-driven so it also works in
   sandboxed previews where plain #hash navigation is blocked). */
document.querySelectorAll('a[href="#configurator"]').forEach(a=>{
  a.addEventListener('click',e=>{
    e.preventDefault();
    document.getElementById('configurator').scrollIntoView({behavior:reduceMotion?'auto':'smooth',block:'start'});
  });
});
const previewPhotoOpen=document.getElementById('previewPhotoOpen');
const previewPhotoModal=document.getElementById('previewPhotoModal');
const previewPhotoModalImage=document.getElementById('previewPhotoModalImage');
const previewPhotoModalCaption=document.getElementById('previewPhotoModalCaption');
function closePreviewPhotoModal(){
  if(!previewPhotoModal) return;
  if(typeof previewPhotoModal.close==='function'&&previewPhotoModal.open) previewPhotoModal.close();
  else previewPhotoModal.removeAttribute('open');
}
if(previewPhotoOpen&&previewPhotoModal&&previewPhotoModalImage){
  previewPhotoOpen.addEventListener('click',()=>{
    const photo=document.getElementById('previewPhoto');
    const fullSrc=photo?.dataset.fullSrc||photo?.currentSrc||photo?.src;
    if(!fullSrc) return;
    previewPhotoModalImage.src=fullSrc;
    previewPhotoModalImage.alt=photo.alt||'Full-size doll face preview';
    if(previewPhotoModalCaption) previewPhotoModalCaption.textContent=photo.alt||'';
    if(typeof previewPhotoModal.showModal==='function') previewPhotoModal.showModal();
    else previewPhotoModal.setAttribute('open','');
  });
  previewPhotoModal.querySelector('[data-preview-modal-close]')?.addEventListener('click',closePreviewPhotoModal);
  previewPhotoModal.addEventListener('click',event=>{ if(event.target===previewPhotoModal) closePreviewPhotoModal(); });
}
document.getElementById('sbToggle').addEventListener('click',()=>{
  const drawer=document.getElementById('sbBreakdown');
  const toggle=document.getElementById('sbToggle');
  const willOpen=drawer.hasAttribute('hidden');
  if(willOpen){ drawer.removeAttribute('hidden'); toggle.setAttribute('aria-expanded','true'); }
  else{ closeSbBreakdown(); }
});
document.getElementById('sbBack').addEventListener('click',()=>stepBy(-1));
document.getElementById('sbNext').addEventListener('click',()=>{
  if(stepIndex===STEPS.length-1){ document.getElementById('reserveBtn')?.click(); return; }
  stepBy(1);
});
/* Re-enter on the step the customer left. A #step-<id> hash (a shared or
   reloaded deep link) wins over the step recorded in the saved build. */
function stepIdFromHash(){
  const match=(window.location.hash||'').match(/^#step-([A-Za-z]+)$/);
  return match?match[1]:null;
}
{
  const entryStepId=stepIdFromHash()||(RESTORED_BUILD&&RESTORED_BUILD.step);
  if(entryStepId){
    const target=stepIndexForId(entryStepId);
    if(target>=0) stepIndex=nearestVisible(target);
  }
  if(window.history&&history.replaceState){
    try{ history.replaceState({sbdStep:STEPS[stepIndex].id},''); }catch(error){}
  }
}
window.addEventListener('popstate',event=>{
  const id=(event.state&&event.state.sbdStep)||stepIdFromHash();
  if(!id) return;
  const target=stepIndexForId(id);
  if(target<0) return;
  replayingHistoryStep=true;
  try{ goToStep(target); }finally{ replayingHistoryStep=false; }
});
renderAll();
function savedDesignNotice(kind){
  const notice=document.getElementById('savedDesignNotice');
  if(!notice) return;
  notice.hidden=false;
  notice.dataset.state=kind;
  const copy=document.querySelector('[data-sbd-recovery-copy]');
  const messages=copy?JSON.parse(copy.textContent):{};
  notice.querySelector('[data-recovery-message]').textContent=messages[kind]||'';
  notice.querySelector('[data-recovery-retry]').hidden=kind!=='unavailable';
  notice.querySelector('[data-recovery-review]').hidden=kind!=='changed'&&kind!=='restored';
  notice.querySelector('[data-recovery-continue]').hidden=kind==='loading';
  notice.querySelector('[data-recovery-continue]').textContent=messages[kind==='changed'?'accept':kind==='restored'?'dismiss':'continue'];
  document.getElementById('configurator').inert=savedDesignPending;
  document.getElementById('stickyBar').inert=savedDesignPending;
  document.getElementById('configurator').setAttribute('aria-busy',kind==='loading'?'true':'false');
}
function clearSavedDesignUrl(){
  try{
    const url=new URL(window.location.href);
    url.searchParams.delete('design');
    history.replaceState(history.state,'',url.pathname+url.search+url.hash);
  }catch(error){}
}
function focusSavedDesignNotice(){
  const notice=document.getElementById('savedDesignNotice');
  if(notice){ notice.focus({preventScroll:true}); notice.scrollIntoView({behavior:reduceMotion?'auto':'smooth',block:'center'}); }
}
window.SBD_SAVED_DESIGN_READY=()=>{
  if(!savedDesignPending&&!savedDesignChanges.length) return true;
  focusSavedDesignNotice();
  return false;
};
async function recoverSavedDesign(){
  savedDesignPending=true;
  document.getElementById('atelier-landing')?.classList.remove('sbd-customizer--collapsed');
  savedDesignNotice('loading');
  if(!/^[A-Za-z0-9_-]{16,64}$/.test(savedDesignToken||'')){
    savedDesignNotice('missing');
    sbdEmitEvent('saved_design_recovery',{outcome:'invalid_link'});
    focusSavedDesignNotice();
    return;
  }
  const controller=new AbortController();
  const timer=setTimeout(()=>controller.abort(),15000);
  try{
    const response=await fetch(`${SAVED_BUILD_FN_URL}?token=${encodeURIComponent(savedDesignToken)}`,{signal:controller.signal,cache:'no-store'});
    if(!response.ok){
      const error=new Error('recovery failed');
      error.missing=response.status===404||response.status===410;
      throw error;
    }
    const data=await response.json();
    if(controller.signal.aborted) throw new Error('recovery timed out');
    const applied=data&&applySavedBuild(data.build,'saved_link');
    if(!applied){ const error=new Error('invalid build'); error.missing=true; throw error; }
    savedDesignPending=false;
    const target=stepIndexForId(savedDesignChanges.length?'review':applied.step||'review');
    if(target>=0) stepIndex=nearestVisible(target);
    clearSavedDesignUrl();
    renderAll();
    saveBuild();
    savedDesignNotice(savedDesignChanges.length?'changed':'restored');
    sbdEmitEvent('saved_design_recovery',{outcome:savedDesignChanges.length?'changed':'restored',changed_groups:savedDesignChanges.length});
  }catch(error){
    savedDesignNotice(error.missing?'missing':'unavailable');
    sbdEmitEvent('saved_design_recovery',{outcome:error.missing?'missing':'unavailable'});
  }finally{
    clearTimeout(timer);
    focusSavedDesignNotice();
  }
}
{
  const notice=document.getElementById('savedDesignNotice');
  notice?.querySelector('[data-recovery-retry]').addEventListener('click',recoverSavedDesign);
  notice?.querySelector('[data-recovery-review]').addEventListener('click',()=>{
    goToStep(stepIndexForId('review'));
    const panel=document.getElementById('questionPanel');
    panel?.focus({preventScroll:true});
    panel?.scrollIntoView({behavior:reduceMotion?'auto':'smooth',block:'start'});
  });
  notice?.querySelector('[data-recovery-continue]').addEventListener('click',()=>{
    savedDesignPending=false;
    clearSavedDesignUrl();
    if(savedDesignChanges.length&&notice.dataset.state!=='changed'){
      savedDesignNotice('changed');
      focusSavedDesignNotice();
      return;
    }
    const changed=savedDesignChanges.length;
    savedDesignChanges=[];
    savedDesignNotice('restored');
    notice.hidden=true;
    saveBuild();
    if(changed) sbdEmitEvent('saved_design_recovery',{outcome:'changes_accepted',changed_groups:changed});
    document.getElementById('questionPanel')?.focus();
  });
  if(savedDesignPending) recoverSavedDesign();
  else if(savedDesignChanges.length) savedDesignNotice('changed');
}
// Start both product requests once, then reuse the cached JSON for every price
// update and for the Reserve handler. Availability is deliberately not used as
// a customizer gate: these companions are made to order and Shopify inventory
// policy controls whether their variants can be purchased.
loadShopifyPricing().then(()=>renderAll()).catch(error=>{
  console.error('Live Shopify pricing could not be loaded. Prices remain unavailable until Shopify responds.',error);
  renderAll();
});
